import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { BROWSER_STORAGE_KEYS } from "../browser-storage";
import { useBrowserStorageConsent } from "./browser-storage-consent-context";

export type Language = "en" | "pl" | "uk";

type TranslationValue = string | ((params?: any) => string);

const STORAGE_KEY = BROWSER_STORAGE_KEYS.language;

export function languageFromQuery(search: string): Language | null {
  const value = new URLSearchParams(search).get("language");
  return value === "en" || value === "pl" || value === "uk" ? value : null;
}

const dictionaries: Record<Language, Record<string, TranslationValue>> = {
  en: {
    "app.site": "Site",
    "app.brandTag": "Daily discipline system",
    "app.today": "Today",
    "app.week": "Long Plans",
    "app.calendar": "Calendar",
    "app.planning": "Planning Room",
    "app.insights": "AI Insights",
    "app.coach": "Dio",
    "app.title.coach": "Disciplio · Dio",
    "app.collapseNavigation": "Collapse navigation",
    "app.expandNavigation": "Expand navigation",
    "app.resizeNavigation": "Resize navigation",
    "app.scroll": "Scroll",
    "app.language": "Language",
    "sessionLoad.label": "Account loading",
    "sessionLoad.title": "We couldn't load your account",
    "sessionLoad.description":
      "Check your connection and try again. Your policy acceptance has not changed.",
    "sessionLoad.rateLimited": ({ seconds }) =>
      `Too many attempts. Try again in ${seconds}s.`,
    "sessionLoad.retry": "Retry",
    "browserStorage.title": "Cookies & storage",
    "browserStorage.description":
      "Save unfinished setup and preferences on this device, and — only with your consent — enable privacy-friendly product analytics and optional session replay on the app to help us understand feature usage and improve Disciplio. We never collect your task or goal text, reflections, or AI conversations.",
    "browserStorage.learnMore": "Learn more",
    "browserStorage.accept": "Accept",
    "browserStorage.reject": "Reject",
    "browserStorage.acceptAria": "Accept optional storage",
    "browserStorage.rejectAria": "Reject optional storage",
    "browserStorage.preferencesTitle": "Browser-storage preference",
    "browserStorage.preferencesAccepted":
      "Optional browser storage and privacy-friendly analytics, including session replay when enabled, are enabled on this device.",
    "browserStorage.preferencesRejected":
      "Optional browser storage and analytics are disabled on this device.",
    "browserStorage.change": "Change preference",
    "ai.recommendationDisclaimer":
      "AI recommendation · may be inaccurate and is non-binding",
    "ai.generatedDisclaimer":
      "AI-generated · may be inaccurate and is non-binding",
    "ai.recommendationLabel": "AI recommendation",
    "aiAllowance.status": ({ feature, remaining, limit, period, reset }) =>
      `Free AI for ${feature}: ${remaining}/${limit} left ${period} · resets ${reset}`,
    "aiAllowance.feature.REFLECTION_ADVICE": "Reflection advice",
    "aiAllowance.feature.INSIGHTS_DAILY": "AI insights",
    "aiAllowance.feature.PLANNING_TASK_SUGGESTION": "Planning suggestions",
    "aiAllowance.feature.COACH_GENERATION": "Dio Coach messages",
    "aiAllowance.feature.COACH_TRANSCRIPTION": "Dio voice transcriptions",
    "aiAllowance.today": "today",
    "aiAllowance.thisWeek": "this week",
    "aiAllowance.later": "later",
    "aiLimits.title": "AI limits",
    "aiLimits.description": "Your Free plan allowance for each AI feature.",
    "aiLimits.row": ({ remaining, limit, period, reset }) =>
      `${remaining}/${limit} remaining ${period} · resets ${reset}`,
    "aiLimits.unlimited": "Unlimited locally",
    "aiLimits.unavailable": "AI limits are unavailable right now.",
    "app.quickActions": "Quick actions",
    "app.settings": "Settings",
    "app.account": "Account",
    "app.profileEmpty": "Add your focus map",
    "app.accountFallbackName": "Set up your profile",
    "completion.taskMessage": ({ title }) => `Task completed: “${title}”`,
    "completion.goalMessage": ({ title }) => `Goal completed: “${title}”`,
    "onboarding.aria": "Disciplio setup checklist",
    "onboarding.label": "Your path to the aha moment",
    "onboarding.title": "Core product flow",
    "onboarding.account": "Register or log in",
    "onboarding.profile": "Complete onboarding",
    "onboarding.progress": ({ completed, total }) =>
      `${completed} of ${total} completed`,
    "onboarding.open": "Open next step",
    "onboarding.verify": "Verify saved work",
    "onboarding.clickHere": "Click here to begin",
    "onboarding.moveGuide": "Move checklist",
    "onboarding.collapse": "Collapse checklist",
    "onboarding.expand": "Expand checklist",
    "onboarding.pause": "Pause spotlight",
    "onboarding.resume": "Resume guide",
    "onboarding.skip": "Skip checklist",
    "onboarding.skipTitle": "Skip the setup checklist?",
    "onboarding.skipDescription":
      "Are you sure? Skip only if you already know the full Disciplio workflow. You can continue using the app normally, but this checklist will not return.",
    "onboarding.skipCancel": "Keep checklist",
    "onboarding.skipConfirm": "Yes, skip onboarding",
    "onboarding.skipFailed": "Couldn’t skip the checklist. Please try again.",
    "onboarding.statusSkipped": "Skipped",
    "onboarding.statusDone": "Done",
    "onboarding.statusNext": "Next",
    "onboarding.step.globalGoal.title": "Refine a global goal",
    "onboarding.step.globalGoal.description":
      "Add a new long-range outcome or rename the goal you registered.",
    "onboarding.step.yearGoal.title": "Create a yearly goal",
    "onboarding.step.yearGoal.description":
      "Create a yearly goal and anchor it to one of your global goals.",
    "onboarding.step.monthlyPriority.title": "Choose a monthly priority",
    "onboarding.step.monthlyPriority.description":
      "Turn the yearly goal into one concrete priority for this month.",
    "onboarding.step.weeklyGoal.title": "Set a measurable weekly goal",
    "onboarding.step.weeklyGoal.description":
      "Choose a target and unit, then link it to the monthly priority.",
    "onboarding.step.todayPlan.title": "Plan today",
    "onboarding.step.todayPlan.description":
      "Add one task for today, select its measurable weekly goal, and enter a positive contribution.",
    "onboarding.step.dailyTask.title": "Complete a daily task",
    "onboarding.step.dailyTask.description":
      "Return to Today and check off any task in the list.",
    "onboarding.step.streak.title": "Discover your Disciplio streak",
    "onboarding.step.streak.description":
      "Your streak counts consecutive active days with a completed task or habit. Open the streak card to see the chain and learn how to keep it going.",
    "onboarding.step.reflection.title": "Submit a reflection",
    "onboarding.step.reflection.description":
      "Write one honest note about the day and save it.",
    "onboarding.step.aiRecommendation.title":
      "Receive one useful recommendation",
    "onboarding.step.aiRecommendation.description":
      "Save the reflection and review the recommendation, or intentionally skip AI.",
    "onboarding.step.return.title": "Return and verify",
    "onboarding.step.return.description":
      "Reload Disciplio so it can confirm that your work is still saved.",
    "onboarding.step.aiConsent.title": "Choose your AI preference",
    "onboarding.step.aiConsent.description":
      "Decide whether Disciplio may generate optional recommendations before you start the day.",
    "aiConsent.label": "Before AI interaction",
    "aiConsent.title": "Disciplio can generate optional recommendations",
    "aiConsent.description":
      "This optional feature uses AI. Selected goals, tasks, plans, notes, reflections, and other relevant Disciplio context may be sent to external AI providers. Disciplio currently uses OpenRouter to route these requests to selected AI model providers. Your email address is not included. Output may be inaccurate or unsuitable and is not professional advice. You can edit, reject, ignore, or delete it, remain responsible for decisions, and can use Disciplio without AI.",
    "aiConsent.enable": "Enable AI",
    "aiConsent.decline": "Continue without AI",
    "app.title.today": "Disciplio · Today",
    "app.title.week": "Disciplio · Long Plans",
    "app.title.calendar": "Disciplio · Calendar",
    "app.title.planning": "Disciplio · Planning Room",
    "app.title.insights": "Disciplio · AI Insights",
    "app.title.account": "Disciplio · Account",
    "app.title.welcome": "Disciplio · Access & Onboarding",
    "app.title.login": "Disciplio · Log in",
    "app.title.ragAdmin": "Disciplio · RAG admin",
    "app.title.aiUsageAdmin": "Disciplio · AI usage",
    "coach.brand": "Dio",
    "coach.brandSub": "AI COACH",
    "coach.newChat": "New chat",
    "coach.dashboard": "Today",
    "coach.recent": "Recent",
    "coach.history": "Conversation history",
    "coach.collapseHistory": "Collapse conversation history",
    "coach.expandHistory": "Expand conversation history",
    "coach.noConversations": "Your conversations will appear here.",
    "coach.greeting": "Hi, I’m Dio. What are we working through?",
    "coach.emptyHint": "I can see your goals, plans and reflections. Ask about this week, a decision you’re stuck on, or something that keeps slipping.",
    "coach.placeholder": "Ask Dio anything…",
    "coach.send": "Send message",
    "coach.stopResponse": "Stop response",
    "coach.thinking": "Dio is thinking",
    "coach.retry": "Retry",
    "coach.delete": "Delete conversation",
    "coach.deleteFailed": "Dio couldn’t delete this conversation. It has been restored.",
    "coach.historyRefreshFailed": "The conversation was deleted, but Dio couldn’t refresh the history.",
    "coach.confirmDeleteGoalPrompt": "“{title}” is a long-term goal. Delete it?",
    "coach.confirmDeleteHabitPrompt": "Delete all occurrences of “{title}”?",
    "coach.confirmDeletePlanItemPrompt": "Delete plan item “{title}”?",
    "coach.confirmBatchDeletePrompt": "Dio found {count} changes that need your confirmation:",
    "coach.confirmDeleteButton": "Delete",
    "coach.cancelDeleteButton": "Keep it",
    "coach.taskProposalPrompt": "Dio prepared this task from your request. Add it to your plan?",
    "coach.taskProposalDetails": "You can change the title, add a time, or link a weekly goal before adding it.",
    "coach.taskProposalTitle": "Title",
    "coach.taskProposalStart": "Starts",
    "coach.taskProposalEnd": "Ends",
    "coach.taskProposalWeeklyGoal": "Weekly goal (optional)",
    "coach.noWeeklyGoal": "No weekly goal",
    "coach.taskProposalDate": "Date: {date}",
    "coach.addProposedTask": "Add task",
    "coach.skipProposedTask": "Not now",
    "coach.capabilities.open": "Show what Dio can do",
    "coach.capabilities.tooltip": "Click to see all features Dio can use.",
    "coach.capabilities.title": "What Dio can do",
    "coach.capabilities.description": "Dio can help with these Disciplio actions today.",
    "coach.capabilities.close": "Close Dio capabilities",
    "coach.capabilities.tasks.title": "Daily tasks",
    "coach.capabilities.tasks.description": "Create, edit, complete, move, duplicate, and delete tasks.",
    "coach.capabilities.goals.title": "Long-term goals",
    "coach.capabilities.goals.description": "Create, edit, complete, reopen, and delete global or yearly goals.",
    "coach.capabilities.plans.title": "Monthly and weekly plans",
    "coach.capabilities.plans.description": "Create, edit, complete, reopen, and delete plan items.",
    "coach.capabilities.batch.title": "Batch changes",
    "coach.capabilities.batch.description": "Handle up to 10 supported changes from one message, in order.",
    "coach.capabilities.coaching.title": "Coaching and reflection",
    "coach.capabilities.coaching.description": "Answer questions about your Disciplio goals, plans, tasks, and reflections.",
    "coach.capabilities.voice.title": "Voice messages",
    "coach.capabilities.voice.description": "Transcribe a voice message so Dio can work with it.",
    "coach.capabilities.confirmations.title": "Safe confirmations",
    "coach.capabilities.confirmations.description": "Ask before destructive deletions and keep you in control.",
    "coach.capabilities.limitations.title": "Dio stays focused on Disciplio",
    "coach.capabilities.limitations.description": "Dio does not send emails or perform external actions.",
    "coach.batchActionsCompleted": "Completed changes:",
    "coach.batchActionFailed": "Couldn’t complete: {action}",
    "coach.batchActionPending": "Waiting for confirmation: {action}",
    "coach.actionLabel.planningChange": "this planning change",
    "coach.actionLabel.listTasks": "review tasks",
    "coach.actionLabel.createTask": "create a task",
    "coach.actionLabel.updateTask": "update a task",
    "coach.actionLabel.completeTask": "complete a task",
    "coach.actionLabel.deleteTask": "delete a task",
    "coach.actionLabel.moveTask": "move a task",
    "coach.actionLabel.duplicateTask": "duplicate a task",
    "coach.actionLabel.listGoals": "review goals",
    "coach.actionLabel.createGoal": "create a goal",
    "coach.actionLabel.updateGoal": "update a goal",
    "coach.actionLabel.completeGoal": "complete a goal",
    "coach.actionLabel.deleteGoal": "delete a goal",
    "coach.actionLabel.listMonthlyPlan": "review a monthly plan",
    "coach.actionLabel.createMonthlyPlanItem": "create a monthly plan item",
    "coach.actionLabel.updateMonthlyPlanItem": "update a monthly plan item",
    "coach.actionLabel.completeMonthlyPlanItem": "complete a monthly plan item",
    "coach.actionLabel.deleteMonthlyPlanItem": "delete a monthly plan item",
    "coach.actionLabel.listWeeklyPlan": "review a weekly plan",
    "coach.actionLabel.createWeeklyPlanItem": "create a weekly plan item",
    "coach.actionLabel.updateWeeklyPlanItem": "update a weekly plan item",
    "coach.actionLabel.completeWeeklyPlanItem": "complete a weekly plan item",
    "coach.actionLabel.deleteWeeklyPlanItem": "delete a weekly plan item",
    "coach.actionCancelled": "Okay, keeping it.",
    "coach.actionConfirmedTask": "Added “{title}” to your plan.",
    "coach.actionCancelledTask": "Okay — I didn’t add that proposal.",
    "coach.actionFailedTask": "I couldn’t add that task, so your plan was not changed.",
    "coach.actionConfirmedDeleteGoal": "Deleted “{title}”.",
    "coach.actionConfirmedDeleteHabit": "Deleted “{title}” (all occurrences).",
    "coach.actionConfirmedDeletePlanItem": "Deleted “{title}”.",
    "coach.actionFailedTargetGone": "That's no longer there — it may have already changed. Nothing was deleted.",
    "coach.free": "Free",
    "coach.fast": "Fast",
    "coach.moreThinking": "More thinking",
    "coach.freeHint": "Everyday questions",
    "coach.fastHint": "Shorter, quicker answers",
    "coach.moreThinkingHint": "Harder decisions, slower",
    "coach.effortLabel": "Response profile",
    "coach.effortAria": "Choose response profile",
    "coach.startVoice": "Record a voice message",
    "coach.stopVoice": "Stop recording",
    "coach.transcribing": "Transcribing…",
    "coach.voiceUnsupported": "Voice recording is not supported in this browser.",
    "coach.voicePermission": "Allow microphone access to record a message.",
    "coach.voiceTooLong": "That recording was too long. Try a shorter message.",
    "coach.voiceTooLarge": "That recording is too large. Try a shorter message.",
    "coach.voiceUnavailable": "Voice transcription is temporarily unavailable. Try again or type your message.",
    "coach.voiceFailed": "Voice transcription failed. Try again or type your message.",
    "coach.streamFailed": "Dio couldn’t finish that response.",
    "coach.actionCommitted": "Your change was saved.",
    "coach.authRequired": "Your session expired. Log in again to continue chatting with Dio.",
    "coach.logIn": "Log in",
    "coach.responseStopped": "Response stopped. Retry when you’re ready.",
    "coach.retryLimit": "This response has reached its retry limit.",
    "coach.invalidRequest": "That Coach request is not valid. Check the message and try again.",
    "coach.quota": "Your Coach allowance is used up for now.",
    "coach.rateLimited": ({ seconds }) => `Dio is temporarily rate-limited. Try again in ${seconds}s.`,
    "coach.rateLimitedReady": "Dio was temporarily rate-limited. You can retry now.",
    "coach.consent": "Accept AI use in Account before chatting with Dio.",
    "coach.openAccount": "Open Account AI settings",
    "coach.disclaimer": "AI can make mistakes. Check important details against your Disciplio plans.",
    "coach.copyAnswer": "Copy answer",
    "coach.copiedAnswer": "Answer copied.",
    "coach.copyAnswerError": "Couldn’t copy the answer.",
    "coach.feedback.title": "Tell us about this answer",
    "coach.feedback.positive": "Helpful",
    "coach.feedback.negative": "Not helpful",
    "coach.feedback.prompt": "What made this answer feel this way? (optional)",
    "coach.feedback.reasonLabel": "Optional note",
    "coach.feedback.reasonPlaceholder": "Add a short note…",
    "coach.feedback.send": "Send feedback",
    "coach.feedback.sending": "Sending…",
    "coach.feedback.close": "Close",
    "coach.feedback.change": "Change feedback",
    "coach.feedback.thanks": "Thank you for the feedback.",
    "coach.feedback.error": "Couldn’t save feedback. Try again.",
    "coach.timestamp.answer": "Answer written",
    "coach.timestamp.yesterday": "Yesterday",
    "coach.scrollLatest": "Scroll to latest",
    "coach.mobileHistory": "Open conversation history",
    "coach.closeHistory": "Close conversation history",
    "coach.starter.plan": "Plan my week",
    "coach.starter.decision": "Think through a decision",
    "coach.starter.review": "Review progress",
    "app.skipToMain": "Skip to main content",
    "welcome.logInShort": "Log in",
    "welcome.logInAria": "Already have an account? Log in",
    "welcome.kicker": "Access + onboarding",
    "welcome.heroTitle": "Set up your Disciplio profile",
    "welcome.heroLead":
      "Add the account details and focus context Disciplio needs for planning and feedback. You can refine this profile later",
    "welcome.liveMap": "Live profile map",
    "welcome.futureProfile": "Your future Disciplio profile",
    "welcome.identityFallback": "Identity + orientation show up here",
    "welcome.rolesLabel": "Roles",
    "welcome.rolesEmpty": "Add comma-separated roles to map your context",
    "welcome.globalGoals": "Global goals",
    "welcome.noGoalsYet": "No goals yet",
    "welcome.focusInputs": "Focus inputs",
    "welcome.focusInputsEmpty": "Interests and problem fields appear here",
    "welcome.recoveryLifestyle": "Recovery + lifestyle",
    "welcome.recoveryEmpty": "Hobbies and support needs appear here",
    "welcome.profileLabel": "Profile",
    "welcome.startLabel": "Start here",
    "welcome.updateTitle": "Update your profile",
    "welcome.createTitle": "Create your profile",
    "welcome.fullNameLabel": "Full name",
    "welcome.fullNamePlaceholder": "e.g. Alex Morgan",
    "welcome.roleLabel": "Roles / season of life",
    "welcome.rolePlaceholder": "e.g. student, founder, parent",
    "welcome.emailLabel": "Email",
    "welcome.emailPlaceholder": "you@example.com",
    "welcome.emailFormatHint":
      "Enter an email address in the format name@example.com",
    "welcome.passwordLabel": "Password",
    "welcome.passwordForAccessLabel": "Password for access",
    "welcome.passwordPlaceholder": "At least 8 characters",
    "welcome.goalsPlaceholder":
      "e.g. finish my degree, build a business, improve my health",
    "welcome.goalsHelp": "Only you can see your goals",
    "welcome.tagInputHint": "Press Enter or type a comma to add an item.",
    "welcome.suggested": "Suggested",
    "welcome.removeTag": ({ tag }) => `Remove ${tag}`,
    "welcome.interestsLabel": "Interests",
    "welcome.interestsPlaceholder": "e.g. AI, fitness, psychology",
    "welcome.interestsHelp":
      "Comma or line-separated topics that naturally pull your attention",
    "welcome.problemsLabel": "Problem fields",
    "welcome.problemsPlaceholder":
      "e.g. procrastination, distraction, inconsistent routines",
    "welcome.problemsHelp":
      "Where your system usually breaks down. Optional — if you leave this empty, Disciplio will help identify friction points as you use the app",
    "welcome.hobbiesLabel": "Hobbies",
    "welcome.hobbiesPlaceholder": "e.g. running, reading, photography",
    "welcome.hobbiesHelp":
      "Useful for balance, recovery, and identity outside pure output",
    "welcome.needsLabel": "What should the system help with?",
    "welcome.needsPlaceholder":
      "e.g. plan my days, stay focused, recover after setbacks",
    "welcome.needsHelp": "Inputs the operating system should optimize around",
    "welcome.commitmentLabel": "Weekly commitment",
    "welcome.commitmentPlaceholder":
      "e.g. 8 focused hours, 3 workouts, 2 study sessions",
    "welcome.commitmentHelp":
      "Disciplio uses this as your weekly capacity ceiling to avoid overplanning and keep plans realistic",
    "welcome.heardAboutUsLabel": "Where did you hear about us? (optional)",
    "welcome.heardAboutUsPlaceholder":
      "e.g. a friend, social media, a search engine",
    "welcome.errorName":
      "Add your full name so Disciplio can personalize your profile",
    "welcome.errorEmail":
      "Add your email to create or continue your Disciplio access",
    "welcome.errorEmailFormat":
      "Enter a valid email address, for example name@example.com",
    "welcome.errorExistingAccount":
      "An account with this email already exists in Disciplio. Sign in instead.",
    "welcome.errorPasswordTooShort": ({ count }) =>
      `Use at least ${count} characters`,
    "welcome.errorPasswordTooLong": ({ count }) =>
      `Use no more than ${count} characters`,
    "welcome.errorPasswordReused":
      "Choose a password you have not used for this account.",
    "welcome.errorRegistration":
      "Could not create your account. Check your details and try again.",
    "welcome.errorContext":
      "Add at least one interest and one global goal so the system has real context",
    "welcome.errorSave": "Could not save your profile. Please try again.",
    "welcome.errorTerms":
      "Accept the Terms and Privacy notice to create an account",
    "welcome.saveAndReturn": "Save profile and return",
    "welcome.enterDisciplio": "Enter Disciplio",
    "welcome.termsAgreementPrefix": "I agree to the",
    "welcome.termsLabel": "Terms",
    "welcome.privacyAgreementPrefix": "I have read the",
    "welcome.privacyLabel": "Privacy notice",
    "plan.toggleAria": ({ title, done }) =>
      `${title}, ${done ? "completed" : "not completed"}`,

    "lang.en": "English",
    "lang.pl": "Polski",
    "lang.uk": "Українська",
    "lang.enShort": "EN",
    "lang.plShort": "PL",
    "lang.ukShort": "UA",

    "login.label": "Access",
    "login.title": "Log in",
    "login.heroTitle": "Return to your discipline system",
    "login.heroLead":
      "Open the same profile, language, plans, and goals without rebuilding your setup",
    "login.proofLabel": "Login benefits",
    "login.localOnly": "Same account",
    "login.localOnlyText": "Sign in with the same email to reopen your profile",
    "login.profileReady": "Profile synced",
    "login.profileReadyText":
      "Plans and settings are ready as soon as you sign in",
    "login.fastReturn": "Fast return",
    "login.fastReturnText": "Pick up where you left off",
    "login.password": "Password",
    "login.showPassword": "Show password",
    "login.hidePassword": "Hide password",
    "login.submit": "Log in",
    "login.create": "Create profile",
    "login.noAccount":
      "No saved account exists on this device yet. Create a profile first",
    "login.invalid": "Email or password is not correct",
    "auth.rateLimited": ({ seconds }) =>
      `Too many attempts — try again in ${seconds}s`,
    "auth.serverUnavailable":
      "The server did not respond. We are working on it — please try again in a few minutes.",
    "login.attemptPolicy":
      "After 5 incorrect attempts, sign-in pauses for 15 minutes.",
    "login.forgotPassword": "Forgot password?",
    "login.throttled":
      "Too many sign-in attempts. Try again in about 15 minutes.",
    "resetPassword.label": "Secure access recovery",
    "resetPassword.title": "Set a new password",
    "resetPassword.heroTitle": "Restore access securely",
    "resetPassword.heroLead":
      "This link works once and expires soon. Setting a new password signs out all existing sessions.",
    "resetPassword.newPassword": "New password",
    "resetPassword.confirmPassword": "Confirm new password",
    "resetPassword.tooShort": ({ count }) => `Use at least ${count} characters`,
    "resetPassword.tooLong": ({ count }) =>
      `Use no more than ${count} characters`,
    "resetPassword.reused":
      "Choose a password you have not used for this account.",
    "resetPassword.match": "The new passwords do not match",
    "resetPassword.invalid":
      "This reset link is invalid, expired, or has already been used.",
    "resetPassword.invalidTitle": "Link invalid or missing",
    "resetPassword.requestNew": "Request a new link",
    "resetPassword.submit": "Save new password",
    "resetPassword.saving": "Saving…",
    "resetPassword.back": "Back to login",
    "resetPassword.successTitle": "Password updated",
    "resetPassword.successLead":
      "Your other sessions have been signed out. Log in with your new password.",
    "resetPassword.login": "Go to login",

    "verifyEmail.label": "Confirm your email",
    "verifyEmail.heroTitle": "Almost there",
    "verifyEmail.heroLead":
      "Enter the six-digit code we sent to finish creating your account.",
    "verifyEmail.title": "Enter your confirmation code",
    "verifyEmail.sentTo": ({ email }) =>
      `We sent a code to ${email}. Check Spam and Promotions if you don't see it.`,
    "verifyEmail.codeLabel": "Confirmation code",
    "verifyEmail.submit": "Confirm",
    "verifyEmail.verifying": "Confirming…",
    "verifyEmail.resend": "Resend code",
    "verifyEmail.resendCooldown": ({ seconds }) =>
      `Resend available in ${seconds}s`,
    "verifyEmail.hint":
      "Only the newest code works. Codes expire after a short time.",
    "verifyEmail.invalidCode":
      "That code is incorrect or has expired. Request a new one.",
    "verifyEmail.missingEmail":
      "We could not find a pending registration for this session. Sign in or register again.",
    "verifyEmail.successTitle": "Email confirmed",
    "verifyEmail.successLead": "Your account is now active.",

    "app.title.register": "Disciplio · Register",
    "register.label": "Create account",
    "register.heroTitle": "Start your Disciplio account",
    "register.heroLead":
      "Create your account with Google or email. You'll set up your profile right after.",
    "register.title": "Register",
    "register.divider": "or continue with Google",
    "register.submit": "Create account",
    "register.logInLink": "Log in",

    "welcome.accountLabel": "Account",
    "welcome.logOutShort": "Log out",
    "welcome.logOutAria": "Log out and return to login",

    "google.continueWithGoogle": "Continue with Google",
    "google.loading": "Loading Google sign-in…",
    "google.unavailable": "Google sign-in unavailable",
    "google.signInFailed": "Google sign-in failed",
    "google.accountNotFound":
      "No Disciplio account is connected to this Google account. Create an account or sign in with password.",
    "google.linkRequired":
      "An account with this email already exists; sign in with password and connect Google from Account settings",
    "google.connected": "Google account connected",
    "google.disconnected": "Google account disconnected",
    "google.onlyMethod": "Cannot disconnect the only sign-in method",
    "google.emailMismatch":
      "The Google account email must match your Disciplio account email",
    "google.identityOwnedByAnotherUser":
      "This Google account is already connected to a different Disciplio account",

    "account.authMethodsTitle": "Sign-in methods",
    "account.authMethodsHelp": "How you can sign in to this account",
    "account.authMethodEmail": "Email & password",
    "account.authMethodGoogle": "Google",
    "account.authMethodGoogleNotConnected": "Not connected",
    "account.passwordSet": "Password set",
    "account.noPasswordSet": "No password set",
    "account.setPasswordFirst": "Set a password first",
    "account.setPasswordLead":
      "Add a password so you can sign in without Google and manage your account security.",
    "account.setPasswordNew": "New password",
    "account.setPasswordConfirm": "Confirm new password",
    "account.setPasswordSubmit": "Save password",
    "account.setPasswordSaving": "Saving…",
    "account.setPasswordCancel": "Cancel",
    "account.setPasswordSuccess": "Password set",
    "account.setPasswordFailed": "Could not set password. Try again.",
    "account.disconnectGoogle": "Disconnect Google",
    "account.deleteRequiresPassword":
      "Set a password before deleting your account, so we can confirm it's really you.",

    "forgotPassword.label": "Password recovery",
    "forgotPassword.heroTitle": "Reset your password",
    "forgotPassword.heroLead":
      "Enter your account email and, if it exists, we will send a single-use reset link.",
    "forgotPassword.formTitle": "Forgot your password?",
    "forgotPassword.emailLabel": "Email",
    "forgotPassword.emailPlaceholder": "name@example.com",
    "forgotPassword.invalidEmail": "Enter a valid email address",
    "forgotPassword.submit": "Send reset link",
    "forgotPassword.submitting": "Sending…",
    "forgotPassword.back": "Back to login",
    "forgotPassword.successTitle": "Check your email",
    "forgotPassword.successLead":
      "If an account exists for this email, a reset link has been sent.",
    "forgotPassword.login": "Go to login",

    "account.label": "Account",
    "account.navLabel": "Account menu",
    "account.navProfile": "Profile",
    "account.navProfileHint": "Identity and direction",
    "account.navSettings": "Settings",
    "account.navSettingsHint": "Daily operating rules",
    "account.navAi": "AI",
    "account.navAiHint": "Consent and usage",
    "account.navPrivacy": "Privacy & account",
    "account.navPrivacyHint": "Legal and access",
    "account.infoLabel": ({ label }) => `More information about ${label}`,
    "account.backToMenu": "Account menu",
    "account.aiTitle": "AI recommendations",
    "account.aiDescription":
      "Recommendations may be inaccurate and are not professional advice.",
    "account.aiEnabled": "Enabled",
    "account.aiDeclined": "Declined",
    "account.aiUndecided": "Not decided",
    "account.aiEnable": "Enable AI",
    "account.aiDecline": "Decline AI",
    "account.aiEnabledHelp": "Use optional AI recommendations.",
    "account.aiDeclinedHelp": "Keep AI recommendations turned off.",
    "account.aiEnabledStatus": "AI recommendations are enabled.",
    "account.aiDisabledStatus":
      "AI recommendations are disabled. Non-AI features remain available.",
    "account.aiUpdateFailed": "We could not update the AI preference.",
    "account.memoryTitle": "Memory",
    "account.memoryDescription": "Review the details Disciplio AI may use to make replies and recommendations more relevant.",
    "account.memoryPrivacy": "Memory belongs to your account. It can shape AI context, but never changes your goals, tasks, or reflections.",
    "account.memoryFilterLabel": "Memory visibility",
    "account.memoryActive": "Active",
    "account.memorySignals": "Signals",
    "account.memorySignalsDescription": "These are observations from your plans, not personal facts. Dio may use them to help you decide what to do next.",
    "account.memoryAll": "All",
    "account.memoryAdd": "Add memory",
    "account.memoryAddTitle": "Add a memory",
    "account.memoryType": "Type",
    "account.memorySummary": "What should Disciplio remember?",
    "account.memorySummaryPlaceholder": "e.g. I do my best focused work before lunch",
    "account.memorySave": "Save memory",
    "account.memorySaving": "Saving…",
    "account.memoryCancel": "Cancel",
    "account.memoryEdit": "Edit",
    "account.memorySaveEdit": "Save",
    "account.memoryDelete": "Remove",
    "account.memoryRemoving": "Removing…",
    "account.memoryRemoveConfirm": "Remove this memory?",
    "account.memoryReset": "Clear all memory",
    "account.memoryResetConfirm": "Clear all saved memory? Goals, tasks, and reflections will stay.",
    "account.memoryResetConfirmAction": "Clear memory",
    "account.memoryResetCancel": "Keep memory",
    "account.memoryResetting": "Clearing…",
    "account.memoryEmpty": "No active memory yet.",
    "account.memoryEmptySignals": "No active progress signals.",
    "account.memoryEmptyAll": "No saved memory yet.",
    "account.memoryLoading": "Loading memory…",
    "account.memoryError": "Memory could not be loaded. Try again.",
    "account.memoryActionError": "Memory could not be changed. Try again.",
    "account.memoryYouAdded": "You added",
    "account.memoryFromCoach": "You told Dio",
    "account.memorySignal": "Progress signal",
    "account.memoryLearned": "Learned",
    "account.memoryRemoved": "Removed",
    "account.memoryStatusResolved": "Resolved",
    "account.memoryStatusStale": "Expired",
    "account.memoryStatusDismissed": "Dismissed",
    "account.memoryStatusSuperseded": "Replaced",
    "account.memoryNeedsReview": "Needs review",
    "account.memoryTemporary": "Temporary",
    "account.memoryUntil": ({ date }) => `Until ${date}`,
    "account.memoryReviewAfter": ({ date }) => `Check again ${date}`,
    "account.memoryImport": "Import from your AI",
    "account.memoryFromChatGPT": "Imported from ChatGPT",
    "account.memoryFromClaude": "Imported from Claude",
    "account.memoryFromGemini": "Imported from Gemini",
    "account.memoryFromOtherAI": "Imported from another AI",
    "account.memoryEvidence": ({ count }) => count === 1 ? "1 signal" : `${count} signals`,
    "account.memoryLastObserved": ({ date }) => `Updated ${date}`,
    "account.memoryType.FACT": "Fact",
    "account.memoryType.PREFERENCE": "Preference",
    "account.memoryType.CONSTRAINT": "Constraint",
    "account.memoryType.DECISION": "Decision",
    "account.memoryType.BEHAVIOR_PATTERN": "Behavior pattern",
    "account.memoryType.PROGRESS_PATTERN": "Progress pattern",
    "account.memoryType.RECOMMENDATION_OUTCOME": "Recommendation outcome",
    "account.memoryType.RULE": "Rule",
    "account.memoryType.EXPERIMENT": "Experiment",
    "account.memoryType.OPEN_LOOP": "Open loop",
    "memoryImport.eyebrow": "MEMORY IMPORT",
    "memoryImport.title": "Bring your context to Dio",
    "memoryImport.subtitle": "Choose an AI assistant, ask it for the context you want to keep, then review everything before it enters Memory.",
    "memoryImport.stepsLabel": "Memory import steps",
    "memoryImport.step.choose": "Choose assistant",
    "memoryImport.step.paste": "Paste answer",
    "memoryImport.step.review": "Review and apply",
    "memoryImport.chooseLead": "Which assistant has the context you want to bring over?",
    "memoryImport.assistant.chatgpt": "ChatGPT",
    "memoryImport.assistant.chatgptHelp": "Open a prepared request, or copy it yourself.",
    "memoryImport.assistant.claude": "Claude",
    "memoryImport.assistant.claudeHelp": "Open Claude with the request copied for you.",
    "memoryImport.assistant.gemini": "Gemini",
    "memoryImport.assistant.geminiHelp": "Open Gemini with the request copied for you.",
    "memoryImport.assistant.other": "Other AI",
    "memoryImport.assistant.otherHelp": "Copy the request into any assistant.",
    "memoryImport.securityNote": "Only the context you explicitly ask the assistant to return is considered. Never include passwords, keys, tokens, or payment details.",
    "memoryImport.selectedAssistant": ({ assistant }) => `Selected: ${assistant}`,
    "memoryImport.loadingPrompt": "Preparing prompt…",
    "memoryImport.open.chatgpt": "Open in ChatGPT",
    "memoryImport.open.claude": "Copy & open Claude",
    "memoryImport.open.gemini": "Copy & open Gemini",
    "memoryImport.copyPrompt": "Copy prompt",
    "memoryImport.pasteAnswer": "I have the answer",
    "memoryImport.promptError": "The prompt could not be prepared. Try again.",
    "memoryImport.copyError": "We could not copy the prompt. You can try again or copy it manually.",
    "memoryImport.externalOpenError": "The assistant could not be opened. The prompt is still ready to copy.",
    "memoryImport.copied": "Prompt copied to your clipboard.",
    "memoryImport.copiedGemini": "Prompt copied — paste it into Gemini.",
    "memoryImport.bringAnswerBack": "Bring the answer back",
    "memoryImport.pasteLead": ({ assistant }) => `Paste the JSON answer from ${assistant} below. Nothing is saved until you review and apply it.`,
    "memoryImport.pastePlaceholder.chatgpt": "Paste ChatGPT's answer here…",
    "memoryImport.pastePlaceholder.claude": "Paste Claude's answer here…",
    "memoryImport.pastePlaceholder.gemini": "Paste Gemini's answer here…",
    "memoryImport.pastePlaceholder.other": "Paste your AI assistant's answer here…",
    "memoryImport.pasteLabel": "Assistant JSON answer",
    "memoryImport.pastePrivacy": "This answer is kept only in this open session until you choose Apply. It is never put in the URL, analytics, or logs.",
    "memoryImport.reviewAnswer": "Review memories",
    "memoryImport.back": "Back",
    "memoryImport.reviewTitle": "Review before saving",
    "memoryImport.reviewCounts": ({ newCount, duplicateCount, conflictCount }) => `${newCount} new · ${duplicateCount} duplicate · ${conflictCount} conflict`,
    "memoryImport.reviewPrivacy": "Choose what Dio should keep. New Memory is created only after you press Apply.",
    "memoryImport.groupNew": "New Memory",
    "memoryImport.groupConflicts": "Updates & conflicts",
    "memoryImport.groupReview": "Review required",
    "memoryImport.groupDuplicates": "Already in Memory",
    "memoryImport.groupSkipped": "Skipped safely",
    "memoryImport.decisionLabel": "Decision",
    "memoryImport.memoryTypeLabel": "Memory type",
    "memoryImport.summaryLabel": "Text",
    "memoryImport.editValidation": "Each Memory needs a type and up to 500 characters of text before you can apply it.",
    "memoryImport.decision.import": "Import",
    "memoryImport.decision.skip": "Skip",
    "memoryImport.decision.replace": "Use imported",
    "memoryImport.decision.keep_both": "Keep both",
    "memoryImport.decision.keep_existing": "Keep existing",
    "memoryImport.disposition.new": "New",
    "memoryImport.disposition.duplicate": "Duplicate",
    "memoryImport.disposition.conflict": "Conflict",
    "memoryImport.disposition.review_required": "Review required",
    "memoryImport.disposition.rejected": "Not imported",
    "memoryImport.existingMemory": "Current Memory",
    "memoryImport.reason.previously_imported": "This statement was already imported. Manage its current state in Memory settings.",
    "memoryImport.reason.exact_duplicate": "This matches an active Memory exactly.",
    "memoryImport.reason.explicit_replacement": "The assistant marked this as replacing the current Memory.",
    "memoryImport.reason.replacement_target_not_found": "The requested replacement could not be matched safely.",
    "memoryImport.reason.no_match": "No current Memory matched this item.",
    "memoryImport.reason.comparison_pending": "A safe comparison is not available yet.",
    "memoryImport.reason.semantic_duplicate": "This appears to mean the same thing as the current Memory.",
    "memoryImport.reason.semantic_conflict": "This appears to conflict with the current Memory.",
    "memoryImport.reason.semantic_independent": "This appears to be independent context.",
    "memoryImport.reason.comparison_uncertain": "The comparison was inconclusive, so choose explicitly.",
    "memoryImport.reason.invalid_item": "This item was not valid JSON.",
    "memoryImport.reason.unsupported_type": "This Memory type is not supported for import.",
    "memoryImport.reason.blank_summary": "This item had no usable summary.",
    "memoryImport.reason.summary_too_long": "This summary is too long to import.",
    "memoryImport.reason.unsafe_content": "This item was skipped because it may contain a secret or credential.",
    "memoryImport.reason.invalid_replaces": "The replacement field was not valid.",
    "memoryImport.reason.replaces_too_long": "The replacement field is too long.",
    "memoryImport.reason.duplicate_candidate": "This item duplicated another imported item and was skipped.",
    "memoryImport.applying": "Applying…",
    "memoryImport.apply": ({ count }) => `Import ${count} memor${count === 1 ? "y" : "ies"}`,
    "memoryImport.backToPaste": "Back to answer",
    "memoryImport.applyError": "The import could not be applied. Nothing was changed; review the answer and try again.",
    "memoryImport.previewError": "We could not review this answer. Check that it is the JSON returned by the assistant and try again.",
    "memoryImport.successTitle": "Memory imported",
    "memoryImport.successSummary": ({ assistant }) => `The reviewed context from ${assistant} is now part of your editable Disciplio Memory.`,
    "memoryImport.successImported": "new",
    "memoryImport.successReplaced": "updated",
    "memoryImport.successKeptBoth": "kept both",
    "memoryImport.successSkipped": "skipped",
    "memoryImport.done": "Done",
    "memoryImport.close": "Close Memory import",
    "memoryImport.goToEnd": "Go to end",
    "account.legalTitle": "Legal and privacy",
    "account.legalDates": ({ terms, privacy }) =>
      `Accepted Terms: ${terms} · Privacy: ${privacy}`,
    "account.legalNotRecorded": "Not recorded",
    "account.terms": "Terms",
    "account.privacy": "Privacy",
    "account.cookies": "Cookies",
    "account.contact": "Contact",
    "account.support": "Support",
    "account.deleteTitle": "Delete account",
    "account.deleteDescription":
      "This is immediate and permanent. Account-owned plans, reflections, recommendations, settings, tokens, and linked support reports will be deleted.",
    "account.deleteCurrentPassword": "Current password",
    "account.deleteConfirmation": "Type DELETE",
    "account.deleteButton": "Delete account permanently",
    "account.deleteConfirmError": "Type DELETE to confirm account removal.",
    "account.deleteFailed":
      "Account deletion failed. Check your password and confirmation word.",
    "account.fallbackTitle": "Your Disciplio account",
    "account.lead": "Personal context and Disciplio settings",
    "account.roles": "Roles",
    "account.globalGoals": "Global goals",
    "account.yearGoals": "Year goals",
    "account.focusInputs": "Focus inputs",
    "account.workspaceReady": "Workspace ready",
    "account.personalInfo": "Personal information",
    "account.profileContext": "Profile context",
    "account.identitySection": "Identity",
    "account.directionSection": "Long-term direction",
    "account.directionSectionHelp": "Separate goals with commas or new lines",
    "account.focusSection": "Focus map",
    "account.focusSectionHelp":
      "Inputs the system uses to shape recommendations",
    "account.saveProfile": "Save profile",
    "account.profileSaved": "Profile saved",
    "account.autoSaved": "Saved automatically",
    "account.autoSaveReady": "Autosave on blur",
    "account.unsavedChanges": "Changed",
    "account.passwordSaved": "Password changed",
    "account.passwordCurrentError": "Current password is not correct",
    "account.passwordLengthError":
      "Use at least 8 characters for the new password",
    "account.passwordMatchError": "New passwords do not match",
    "account.fullName": "Full name",
    "account.fullNameHelp":
      "The name shown throughout your Disciplio workspace",
    "account.fullNamePlaceholder": "Name shown in your workspace",
    "account.email": "Email",
    "account.emailHelp": "The email address associated with this account",
    "account.emailPlaceholder": "you@example.com",
    "account.rolesSeason": "Roles / season of life",
    "account.rolesPlaceholder": "Founder, student, operator, athlete",
    "account.rolesHelp": "Comma-separated roles",
    "account.globalGoalsHelp":
      "Long-term outcomes that give your planning direction. Separate goals with commas or new lines",
    "account.globalGoalsPlaceholder":
      "Build a disciplined studio, finish thesis, improve health",
    "account.yearGoalsPlaceholder":
      "Ship product v1, publish 24 essays, run a half marathon",
    "account.interests": "Interests",
    "account.interestsHelp":
      "Topics and activities Disciplio can use to understand your focus",
    "account.interestsPlaceholder":
      "AI, writing, endurance, psychology, design",
    "account.problemFields": "Problem fields",
    "account.problemFieldsHelp":
      "Challenges that can interfere with consistent execution",
    "account.problemFieldsPlaceholder":
      "Consistency, overplanning, distraction, sleep debt",
    "account.hobbies": "Hobbies",
    "account.hobbiesHelp":
      "Activities and interests that support your recovery and balance",
    "account.hobbiesPlaceholder": "Climbing, reading, walking, chess, cooking",
    "account.systemNeeds": "What should the system help with?",
    "account.systemNeedsHelp":
      "The kinds of structure, protection, or support you want Disciplio to provide",
    "account.systemNeedsPlaceholder":
      "Morning structure, focus protection, recovery planning",
    "account.weeklyCommitment": "Weekly commitment",
    "account.weeklyCommitmentHelp":
      "Your realistic weekly capacity for focused work, training, or other commitments",
    "account.weeklyCommitmentPlaceholder":
      "10 focused hours, 4 workouts, 2 writing blocks",
    "account.settingsLabel": "Disciplio settings",
    "account.systemPreferences": "System preferences",
    "account.saveSettings": "Save settings",
    "account.settingsSaved": "Settings saved",
    "account.securityLabel": "Security",
    "account.passwordTitle": "Change password",
    "account.passwordLead":
      "Update the local password used for Disciplio access on this device",
    "account.currentPassword": "Current password",
    "account.currentPasswordHelp":
      "Required only when a password already exists",
    "account.newPassword": "New password",
    "account.confirmPassword": "Confirm new password",
    "account.passwordPlaceholder": "Minimum 8 characters",
    "account.savePassword": "Save password",
    "account.interfaceLanguage": "Interface language",
    "account.interfaceLanguageHelp":
      "Controls the language used throughout Disciplio",
    "account.dayStartTime": "Day start time",
    "account.dayStartTimeHelp":
      "Before this time, Today still points to the previous Disciplio day",
    "account.dailyNoteUnlock": "Daily note unlock",
    "account.dailyNoteUnlockHelp":
      "Hour when reflection editing becomes available",
    "account.autoSuggestFocus": "Automatically suggest planning actions",
    "account.autoSuggestFocusHelp":
      "Show AI planning suggestions automatically when your goals have gaps",
    "account.showCompletedDefault": "Show completed actions by default",
    "account.showCompletedDefaultHelp":
      "Keep completed actions visible when you open a plan",
    "account.dailyReminders": "Daily reminders",
    "account.dailyRemindersHelp":
      "A quiet time signal for scheduled daily actions and habits",
    "account.dailyRemindersEnabled": "Remind me about timed daily actions",
    "account.dailyRemindersEnabledHelp":
      "Shows a reminder when an action starts and, when configured, before it ends",
    "account.dailyReminderLead": "Before an action ends",
    "account.dailyReminderLeadHelp":
      "The lead time is used only when an action has both a start and end time",
    "account.reminderOff": "Off",
    "account.reminderMinutes": ({ minutes }) => `${minutes} minutes`,
    "account.reminderCustom": "Custom",
    "account.notificationSound": "Play reminder sound",
    "account.notificationSoundHelp":
      "Uses your supplied sound while Disciplio is open; browser audio policies can mute it",
    "account.browserNotifications": "Browser notifications",
    "account.browserNotificationStatus.unsupported":
      "This browser does not support system notifications.",
    "account.browserNotificationStatus.default":
      "Enable system notifications for reminders while Disciplio is closed or in the background.",
    "account.browserNotificationStatus.granted":
      "Permission granted. Finish setup to create a push subscription for this browser.",
    "account.browserNotificationStatus.denied":
      "Blocked in browser settings. Enable it there to receive system notifications.",
    "account.enableBrowserNotifications": "Enable browser notifications",
    "account.finishBrowserNotifications": "Finish notification setup",
    "account.sendTestBrowserNotification": "Send test notification",
    "account.browserNotificationsEnabled": "Browser notifications enabled",
    "account.browserNotificationsNotEnabled":
      "Browser notifications were not enabled",
    "account.browserNotificationsSubscribed":
      "Enabled on this browser and device, even when Disciplio is closed.",
    "account.browserNotificationsUnavailable":
      "Browser push is not configured for this environment yet.",
    "account.browserNotificationsPermissionOnly":
      "Allowed in this browser. Reminders can appear while a Disciplio tab is open; closed-tab push is not configured here yet.",
    "account.browserNotificationsSubscriptionMissing":
      "Permission is granted, but this browser still needs a push subscription. Finish setup to enable closed-tab reminders.",
    "account.browserNotificationsTestBody":
      "Notifications are ready. You will see reminders like this for timed daily actions.",
    "account.browserNotificationsTestSent": ({ count }) =>
      count === 1
        ? "Test notification sent."
        : `${count} test notifications sent.`,
    "account.browserNotificationsTestNone":
      "No active push subscriptions were found for this account.",
    "account.browserNotificationsTestFailed":
      "The test notification could not be delivered.",
    "account.browserNotificationsError.unsupported":
      "Browser notifications are not supported.",
    "account.browserNotificationsError.serviceWorker":
      "Service worker unavailable",
    "account.browserNotificationsError.invalidConfig":
      "Invalid notification configuration",
    "account.browserNotificationsError.subscribeFailed":
      "Browser subscription creation failed",
    "account.browserNotificationsError.saveFailed":
      "Subscription could not be saved",
    "account.browserNotificationsInstallRequired":
      "On iPhone or iPad, add Disciplio to your Home Screen first, then enable notifications from the installed app.",
    "account.browserNotificationsDisabled":
      "Browser notifications disabled on this device",
    "account.disableBrowserNotifications": "Disable on this device",
    "account.metricUnits": "Metric units",
    "account.metricUnitsHelp": ({ count, max }) =>
      `${count}/${max} custom units. Defaults follow the interface language`,
    "account.defaultMetricUnits": "Default units",
    "account.customMetricUnits": "Custom units",
    "account.noCustomMetricUnits": "No custom units yet",
    "account.metricUnitPlaceholder": "Custom unit",
    "account.addMetricUnit": "Add unit",
    "account.weeklyGoalCategories": "Weekly goal categories",
    "account.weeklyGoalCategoriesHelp": ({ count, max }) =>
      `${count}/${max} custom categories. Defaults follow the interface language`,
    "account.defaultWeeklyGoalCategories": "Default categories",
    "account.customWeeklyGoalCategories": "Custom categories",
    "account.noCustomWeeklyGoalCategories": "No custom categories yet",
    "account.weeklyGoalCategoryPlaceholder": "Custom category",
    "account.addWeeklyGoalCategory": "Add category",
    "account.streakFreeDays": "Streak free days",
    "account.streakFreeDaysHelp":
      "Days that should not break or increase the streak count",
    "account.streakFreeWeekdays": "Weekly free days",
    "account.streakFreeWeekdaysHelp":
      "Selected weekdays do not break or increase your streak",
    "account.streakFreeRanges": "Free date ranges",
    "account.streakFreeRangesHelp":
      "Temporary date spans that should be ignored by your streak",
    "account.streakFreeRangeStart": "Start",
    "account.streakFreeRangeStartHelp": "The first date in the free range",
    "account.streakFreeRangeEnd": "End",
    "account.streakFreeRangeEndHelp":
      "The last date in the free range. Leave it empty for a single-day range",
    "account.addFreeRange": "Add range",
    "account.removeFreeRange": "Remove range",
    "account.noFreeRanges": "No free ranges saved",
    "account.exitTitle": "Exit account",
    "account.exitHelp":
      "Leave this workspace and return to the login screen. Your local data stays saved",
    "account.exitButton": "Exit",
    "account.deleteCurrentPasswordHelp":
      "Confirm your identity with the current account password",
    "account.deleteConfirmationHelp":
      "Type DELETE exactly to confirm this permanent action",

    "today.label": "TODAY",
    "today.streak": "Disciplio streak",
    "today.day": "day",
    "today.days": "days",
    "today.streakAria": ({ streak }) =>
      `${streak} consecutive ${Number(streak) === 1 ? "day" : "days"}; seven slots light up with your streak`,
    "today.streakHint":
      "Active when at least one goal or habit is completed today",
    "streak.openCalendar": "Open streak calendar",
    "streak.calendarTitle": "Streak calendar",
    "streak.current": "Current streak",
    "streak.restores": "Restores this month",
    "streak.resets": ({ date }) => `Resets ${date}`,
    "streak.legend": "Streak calendar legend",
    "streak.completed": "Completed",
    "streak.restored": "Restored",
    "streak.free": "Free day",
    "streak.missed": "Missed",
    "streak.calendarNote":
      "Complete at least one task or habit on each active day to extend your streak. Free days keep the chain without adding to it; eligible short gaps may be restored when you start a new day, up to two missed days each month.",
    "streak.close": "Close streak calendar",
    "streak.loading": "Loading streak",
    "streak.unavailable": "Not available yet",
    "streak.restoreTitle": "Streak restored",
    "streak.restoreDescription": ({ dates }) => `Protected: ${dates}`,
    "streak.remaining": ({ count }) => `${count} restores left this month`,
    "streak.continue": "Continue",
    "today.completion": "Today Completion",
    "today.dayCompletion": "Day completion",
    "today.actionsDone": ({ done, total }) => `${done} / ${total} actions done`,
    "today.pageTitle": "Today",
    "today.nextAction": "Next action",
    "today.noNextAction": "No action queued",
    "today.exitPlanning": "Exit planning mode",
    "today.planTitle": "Today's plan",
    "today.selectedPlanTitle": "Day plan",
    "today.planSubtitle":
      "Work the next item first, then move through the rest of the day",
    "today.planningMode": "Planning mode",
    "today.quickAdd": "Quick add",
    "today.quickAddLead":
      "Add a task fast, then open details only if you need time or goal linkage",
    "today.dailyNote": "Daily note",
    "today.dailyNoteLead":
      "Optional. Opens for writing at 17:00. Feeds the summary below",
    "today.dailyNoteLeadLocked": ({ hour }) =>
      `Optional. Opens for writing at ${hour}. Feeds the summary below`,
    "today.dailyNoteLeadEditable": ({ hour }) =>
      `Optional. Opens for writing at ${hour}. Feeds the summary below`,
    "today.dailyNoteInfo": ({ hour }) =>
      `Opens for writing at ${hour} and feeds the summary below. Describe what was very good, good, and bad today, along with any thoughts you want to capture.`,
    "today.dailyReflection": "Daily reflection",
    "today.aiSummaryLabel": "Summary",
    "today.aiRecommendationLabel": "Recommendation for tomorrow",
    "today.generatingAdvice": "Generating recommendation",
    "today.reflectionAdvicePending":
      "Save your note to generate a concrete recommendation for tomorrow",
    "today.reflectionPlaceholder":
      "e.g. I skipped my evening tasks because I didn’t feel like doing them; I completed a two-hour focused session for my main goal",
    "today.saveNote": "Save note",
    "today.noteSaving": "Saving note…",
    "today.noteSaved": "Note saved. You can continue editing.",
    "today.noteUnsaved": "Changes have not been saved.",
    "today.noteSaveFailed": "Could not save the note. Try again.",
    "today.planTomorrow": "Plan tomorrow",
    "today.recommendationActionsHelp":
      "Not useful? Hide it, or delete it permanently.",
    "today.rejectRecommendation": "Not useful",
    "today.rejectRecommendationHelp":
      "Hide this recommendation for this reflection.",
    "today.deleteRecommendation": "Delete permanently",
    "today.deleteRecommendationHelp": "Permanently remove this recommendation.",

    "todayReflectionWorkflow.eyebrow": "Reflect",
    "todayReflectionWorkflow.note.helper":
      "Reflect on today and optionally describe tomorrow. Disciplio will turn it into an editable plan.",
    "todayReflectionWorkflow.note.emptyPast": "No note was saved for this day.",
    "todayReflectionWorkflow.tomorrowContext.label":
      "Anything important about tomorrow? (optional)",
    "todayReflectionWorkflow.tomorrowContext.placeholder":
      "Fixed hours, meetings, energy constraints, or things you already plan to do",
    "todayReflectionWorkflow.action.generate": "Generate reflection",
    "todayReflectionWorkflow.action.regenerate": "Regenerate reflection",
    "todayReflectionWorkflow.action.retry": "Retry",
    "todayReflectionWorkflow.regenerate.menuLabel": "Regenerate…",
    "todayReflectionWorkflow.regenerate.FULL": "Regenerate reflection",
    "todayReflectionWorkflow.regenerate.RECOMMENDATION_ONLY":
      "Regenerate recommendation",
    "todayReflectionWorkflow.regenerate.PLAN_ONLY":
      "Regenerate tomorrow's plan",
    "todayReflectionWorkflow.state.resuming": "Loading your reflection…",
    "todayReflectionWorkflow.state.outdated":
      "The note changed since this reflection was generated. Regenerate to update it.",
    "todayReflectionWorkflow.live.resumed": "Reflection resumed.",
    "todayReflectionWorkflow.error.consentRequired":
      "Accept the AI disclosure to generate a reflection.",
    "todayReflectionWorkflow.error.generic":
      "Could not generate the reflection. Try again.",
    "todayReflectionWorkflow.error.unavailable":
      "Disciplio AI is temporarily unavailable. Your Daily Note is saved; try again shortly.",
    "todayReflectionWorkflow.error.contract":
      "The reflection data could not be processed. Your Daily Note is saved; try again after updating the app.",
    "todayReflectionWorkflow.error.generator":
      "The model did not return a valid reflection. Your Daily Note is saved; try generating it again.",
    "todayReflectionWorkflow.error.reviewer":
      "The reflection review could not finish. Your safe note and plan were preserved.",
    "todayReflectionWorkflow.error.responseInvalid":
      "Disciplio received an invalid AI response. Your Daily Note is saved; try again.",
    "todayReflectionWorkflow.error.quota": ({ remaining, limit }) =>
      `Daily AI limit reached (${remaining}/${limit} left). Try again after it resets.`,
    "todayReflectionWorkflow.error.notFound":
      "This reflection run is no longer available. Start a new one.",
    "todayReflectionWorkflow.question.ariaLabel":
      "Quick question from Disciplio",
    "todayReflectionWorkflow.question.badge": "Disciplio AI",
    "todayReflectionWorkflow.question.chipsLabel": "Answer options",
    "todayReflectionWorkflow.question.somethingElse": "Something else",
    "todayReflectionWorkflow.question.freeTextPlaceholder":
      "Say more in your own words",
    "todayReflectionWorkflow.question.submitAnswer": "Send answer",
    "todayReflectionWorkflow.question.helper":
      "Answering helps Disciplio give a more accurate recommendation and plan.",
    "todayReflectionWorkflow.question.skip": "Skip question",
    "todayReflectionWorkflow.reflection.forTomorrow": "For tomorrow:",
    "todayReflectionWorkflow.recommendation.label": "Recommendation",
    "todayReflectionWorkflow.recommendation.firstStep": ({ step }) =>
      `First step: ${step}`,
    "todayReflectionWorkflow.recommendation.generatedPlanItem":
      "Added a suggested action to tomorrow's plan below.",
    "todayReflectionWorkflow.feedback.helpful": "Helpful",
    "todayReflectionWorkflow.feedback.notUseful": "Not useful",
    "todayReflectionWorkflow.feedback.reason.TOO_REPETITIVE": "Too repetitive",
    "todayReflectionWorkflow.feedback.reason.TOO_GENERIC": "Too generic",
    "todayReflectionWorkflow.feedback.reason.NOT_RELEVANT_TODAY":
      "Not relevant today",
    "todayReflectionWorkflow.feedback.reason.ALREADY_DOING_THIS":
      "Already doing this",
    "todayReflectionWorkflow.feedback.reason.OTHER": "Other",
    "todayReflectionWorkflow.feedback.more": "More",
    "todayReflectionWorkflow.feedback.wrongAssumption": "Wrong assumption",
    "todayReflectionWorkflow.feedback.wrongAssumptionPlaceholder":
      "What did it get wrong? (optional)",
    "todayReflectionWorkflow.feedback.submit": "Send",
    "todayReflectionWorkflow.feedback.dontRecommend":
      "Don't recommend this again",
    "todayReflectionWorkflow.feedback.suppressed": "Won't recommend this again",
    "todayReflectionWorkflow.feedback.duration.ONE_WEEK": "For 1 week",
    "todayReflectionWorkflow.feedback.duration.ONE_MONTH": "For 1 month",
    "todayReflectionWorkflow.feedback.duration.PERMANENT": "Permanently",
    "todayReflectionWorkflow.feedback.error":
      "Could not save your feedback. Try again.",
    "todayReflectionWorkflow.plan.eyebrow": "Plan",
    "todayReflectionWorkflow.plan.title": "Tomorrow's plan",
    "todayReflectionWorkflow.plan.helper":
      "Review the proposed actions, edit anything that needs adjusting, and accept what you want on tomorrow's plan.",
    "todayReflectionWorkflow.plan.addAction": "Plan tomorrow",
    "todayReflectionWorkflow.plan.acceptAll": "Accept all",
    "todayReflectionWorkflow.plan.accept": "Accept",
    "todayReflectionWorkflow.plan.accepted": "Added to plan",
    "todayReflectionWorkflow.plan.acceptError":
      "Could not update tomorrow's plan. Try again.",
    "todayReflectionWorkflow.plan.skippedConflicted": ({ count }) =>
      `${count} item(s) skipped because they overlap a fixed commitment.`,
    "todayReflectionWorkflow.plan.empty": "No plan proposals yet.",
    "todayReflectionWorkflow.plan.origin.FIXED_COMMITMENT": "Fixed",
    "todayReflectionWorkflow.plan.origin.EXISTING_ACTION": "Existing",
    "todayReflectionWorkflow.plan.origin.MOVED_FROM_TODAY": "Moved from today",
    "todayReflectionWorkflow.plan.origin.PARSED_FROM_NOTE": "From your note",
    "todayReflectionWorkflow.plan.origin.AI_SUGGESTED": "Suggested",
    "todayReflectionWorkflow.plan.sourceIcon.goal": "Goal",
    "todayReflectionWorkflow.plan.sourceIcon.habit": "Habit",
    "todayReflectionWorkflow.plan.sourceIcon.FIXED_COMMITMENT":
      "Fixed commitment",
    "todayReflectionWorkflow.plan.sourceIcon.MOVED_FROM_TODAY":
      "Moved from today",
    "todayReflectionWorkflow.plan.sourceIcon.PARSED_FROM_NOTE":
      "From your note",
    "todayReflectionWorkflow.plan.sourceIcon.AI_SUGGESTED": "AI suggestion",
    "todayReflectionWorkflow.plan.sourceIcon.EXISTING_ACTION":
      "Already in your plan",
    "todayReflectionWorkflow.plan.moveUp": "Move up",
    "todayReflectionWorkflow.plan.moveDown": "Move down",
    "todayReflectionWorkflow.plan.titleLabel": "Title",
    "todayReflectionWorkflow.plan.startLabel": "Start",
    "todayReflectionWorkflow.plan.endLabel": "End",
    "todayReflectionWorkflow.plan.doneEditing": "Done",
    "todayReflectionWorkflow.plan.firstStep": ({ step }) =>
      `First step: ${step}`,
    "todayReflectionWorkflow.plan.conflict": ({ title }) =>
      `Overlaps a fixed commitment: ${title}`,
    "todayReflectionWorkflow.plan.edit": "Edit",
    "todayReflectionWorkflow.plan.remove": "Remove",
    "todayReflectionWorkflow.plan.regenerateWarning":
      "You have unsaved edits to tomorrow's plan. Regenerating may change or remove them. Continue?",

    "today.quoteTitle": "Daily note",
    "today.quoteLead": "Keep one small brand moment, not a full hero block",
    "notifications.liveLabel": "Daily reminders",
    "notifications.startNow": ({ time }) => `Start now · ${time}`,
    "notifications.endsSoon": ({ minutes, time }) =>
      `Ends in ${minutes} min · ${time}`,
    "notifications.openToday": "Open Today",
    "notifications.dismiss": "Dismiss reminder",
    "notifications.promptTitle": "Enable browser notifications",
    "notifications.promptBody":
      "Allow Disciplio to send reminders for timed daily actions, even when this tab is closed.",
    "notifications.promptEnable": "Allow notifications",
    "notifications.promptEnabling": "Enabling…",
    "notifications.promptLater": "Not now",
    "notifications.promptFailed":
      "Could not enable notifications. Try again from Account settings.",
    "notifications.promptUnavailable":
      "Browser push is not configured here yet. You can try again later from Account settings.",

    "add.title": "Action title",
    "add.placeholder": "What will you do?",
    "add.button": "Add",
    "add.more": "Time, type & weekly link",
    "add.type": "Type",
    "add.goal": "Goal",
    "add.habit": "Habit",
    "add.time": "Time",
    "add.date": "Date",
    "add.week": "Week",
    "add.thisWeek": ({ range }) => `This week · ${range}`,
    "add.nextWeek": ({ range }) => `Next week · ${range}`,
    "add.from": "From",
    "add.to": "To",
    "add.clear": "Clear",
    "add.timeConstraintWorkWindow": "Work window (other actions may be scheduled inside)",
    "add.timeConstraintWorkWindowHint":
      "Off: this fully blocks the time range. On: other actions may be scheduled inside it.",
    "add.weeklyGoal": "Weekly goal",
    "add.weeklyGoalLink": "Weekly goal link",
    "add.monthlyGoal": "Monthly goal",
    "add.none": "None",
    "add.amount": "Amount",
    "add.unit": "Unit",
    "add.unitPlaceholder": "hours, pages",
    "add.useProposedAmount": ({ amount }) =>
      `Use ${amount} from the time range`,
    "add.unitMismatchWarning": ({ goalUnit }) =>
      `This unit differs from the linked goal's unit (${goalUnit}), so the amount will not count toward its progress.`,
    "weeklyGoal.progressSummary": ({ progress, target, unit }) =>
      `${progress} / ${target} ${unit}`,
    "weeklyGoal.noTarget": "No target",
    "metricUnitInput.showOptions": "Show unit options",
    "add.linkAlert":
      "Linking a measurable weekly goal requires a positive amount (e.g. hours, pages)",
    "add.saveFailed":
      "Your task could not be saved. Check your connection and try again.",
    "repeat.label": "Repeat",
    "repeat.frequencyLabel": "Repeat pattern",
    "repeat.frequencyHelp": "Choose how often this habit should return",
    "repeat.intervalLabel": "Interval",
    "repeat.daily": "Every day",
    "repeat.withExceptions": ({ repeat, exceptions }) =>
      `${repeat} except ${exceptions}`,
    "repeat.customDays": ({ count }) => `Every ${count} days`,
    "repeat.customDaysShort": "Every X days",
    "repeat.weekly": "Every week",
    "repeat.monthly": "Every month",
    "repeat.everyXDays": "Days interval",
    "repeat.rulesTitle": "Repeat rules",
    "repeat.exceptions": "Exceptions",
    "repeat.exceptionsHelp":
      "Optional skips. Use weekdays for recurring breaks and dates for one-off blocks",
    "repeat.skipWeekdays": "Skip weekdays",
    "repeat.skipWeekdaysHelp": "Tap days when this habit should not appear",
    "repeat.skipDates": "Skip exact dates",
    "repeat.skipDatesHelp":
      "Add holidays, travel days, or any single date to skip",
    "repeat.skipDate": "Skip date",
    "repeat.selectedSkipDates": "Selected skipped dates",
    "weekday.short.0": "Sun",
    "weekday.short.1": "Mon",
    "weekday.short.2": "Tue",
    "weekday.short.3": "Wed",
    "weekday.short.4": "Thu",
    "weekday.short.5": "Fri",
    "weekday.short.6": "Sat",
    "weeklyCategories.filterLabel": "Weekly goal categories",
    "weeklyCategories.all": "All",
    "weeklyCategories.placeholder": "Category, e.g. Learning",
    "weeklyCategories.useCustom": ({ value }) => `Use “${value}”`,
    "weeklyCategories.limitReached":
      "Your custom category limit is reached. Existing categories remain available.",

    "plan.unscheduled": "Unscheduled",
    "plan.flexible": "Flexible · no fixed time",
    "plan.noUnscheduled": "Nothing without a time yet",
    "plan.scheduled": "Scheduled",
    "plan.chronological": "Chronological",
    "plan.noScheduled": "No timed actions yet",
    "plan.newScheduledAction": "New scheduled action",
    "plan.newUnscheduledAction": "New unscheduled action",
    "plan.completed": "Completed",
    "plan.completedHint": "Shown by default so you can review your progress",
    "plan.noCompleted": "No completed items yet",
    "plan.linked": ({ title, amount, unit }) =>
      `Weekly goal: ${title} (+${amount} ${unit})`,
    "plan.linkedUnmeasured": ({ title }) => `Weekly goal: ${title}`,
    "plan.habitMetric": ({ amount, unit }) => `${amount} ${unit}`,
    "plan.noLink": "No weekly goal link",
    "plan.anyTime": "Any time",
    "plan.wrapsNextDay": "+1 day",
    "plan.wrapsNextDayAria": "Ends after midnight, the next day",
    "plan.sectionEmpty": "Nothing here yet",
    "plan.edit": "Edit",
    "plan.moreActions": "More goal actions",
    "plan.saving": "Saving…",
    "plan.duplicate": "Duplicate goal",
    "plan.duplicateTitle": "Duplicate goal",
    "plan.duplicateDescription": "Create copies of this goal on selected days.",
    "plan.closeDuplicate": "Close duplicate goal",
    "plan.duplicateModes": "Duplication mode",
    "plan.duplicateSimple": "Simple",
    "plan.duplicateWeekdays": "Weekdays",
    "plan.duplicateRange": "Date range",
    "plan.duplicatePickDate": "Pick a date",
    "plan.duplicateRepeatOn": "Repeat on",
    "plan.duplicateStartDate": "Start date",
    "plan.duplicateEndDate": "End date",
    "plan.duplicateRangeInvalid":
      "Choose an end date on or after the start date.",
    "plan.duplicateRangeTooLong": "Choose a range with no more than 31 copies.",
    "plan.duplicateDays": "Select days",
    "plan.duplicateAllWeek": "All week",
    "plan.duplicateWorkdays": "Workdays",
    "plan.duplicateCustomDays": "Custom days",
    "plan.duplicateSourceDay": ({ day }) =>
      `Source day: ${day}. It will not be copied.`,
    "plan.duplicateKeepDetails": "Keep same time and details",
    "plan.duplicateKeepDetailsHelp":
      "Time, amount and other details will be copied.",
    "plan.duplicateKeepTime": "Keep time",
    "plan.duplicateKeepTimeHelp": "Copy the scheduled time to each new goal.",
    "plan.duplicateKeepWeekly": "Keep weekly goal connected",
    "plan.duplicateKeepWeeklyHelp":
      "Copy the contribution and keep the planning hierarchy connected.",
    "plan.duplicateKeepWeeklyUnavailable":
      "This goal is not connected to a weekly goal.",
    "plan.duplicateHierarchyWarning":
      "For another week, Disciplio may create or reuse the weekly goal and its monthly parent. New parents start with zero progress and keep the year-goal link.",
    "plan.duplicateCopyOptions": "Copy options",
    "plan.duplicateToDays": ({ count }) =>
      `Duplicate to ${count} ${count === 1 ? "day" : "days"}`,
    "plan.duplicating": "Duplicating…",
    "plan.duplicateFailed":
      "Your goals could not be duplicated. Check your connection and try again.",
    "plan.duplicateSuccessSingle": ({ date }) => `Goal added to ${date}.`,
    "plan.duplicateSuccessList": ({ dates }) => `Goal added to ${dates}.`,
    "plan.duplicateSuccessMany": ({ count, from, to }) =>
      `Goal added to ${count} dates (${from} – ${to}).`,
    "plan.closeDuplicateSuccess": "Close duplication confirmation",
    "plan.delete": "Delete",
    "plan.deleteTaskTitle": ({ title }) => `Delete “${title}”?`,
    "plan.deleteTaskDescription": "This action cannot be undone.",
    "plan.deleteGoalTitle": ({ title }) => `Delete “${title}”?`,
    "plan.deleteGoalDescription":
      "This will also remove any links to this goal. This action cannot be undone.",
    "plan.deleteHabitTitle": ({ title }) =>
      `Delete recurring habit “${title}”?`,
    "plan.deleteHabitDescription":
      "Choose whether to remove this occurrence only or every occurrence.",
    "plan.changeHabitTimeTitle": ({ title }) =>
      `Change the time for “${title}”?`,
    "plan.changeHabitTimeDescription":
      "Choose whether this time applies only on this date or from this date forward.",
    "plan.changeHabitTimeOne": "This date only",
    "plan.changeHabitTimeFuture": "This date and future",
    "plan.deleteThisDate": "Delete this date",
    "plan.deleteAllOccurrences": "Delete all occurrences",
    "plan.deletedUndoMessage": ({ title }) => `"${title}" deleted`,
    "plan.undo": "Undo",
    "plan.closeUndo": "Close",
    "plan.save": "Save",
    "plan.cancel": "Cancel",
    "plan.show": "Show",
    "plan.hide": "Hide",
    "plan.suggestionOnly": "Suggestion only",
    "plan.scheduledSuggestionHint":
      "Not in your daily plan yet. Use it to create a scheduled task if it fits today",
    "plan.unscheduledSuggestionHint":
      "Not in your daily plan yet. Use it as a flexible task only if it belongs today",
    "plan.useSuggestion": "Use suggestion",

    "planning.workspace": "Planning workspace",
    "planning.subtitle": "Today's tasks alongside your weekly context",
    "planning.weekHeaderTitle": "Week plan",
    "planning.weekSubtitle":
      "Review monthly goals and shape this week's priorities",
    "planning.monthHeaderTitle": ({ month }) => `${month} plan`,
    "planning.monthSubtitle":
      "Review monthly goals against the year-level outcomes they should support",
    "planning.yearHeaderTitle": ({ year }) => `${year} plan`,
    "planning.yearSubtitle":
      "Review year goals against the global direction they should support",
    "planning.dayMode": "Day",
    "planning.weekMode": "Week",
    "planning.monthMode": "Month",
    "planning.yearMode": "Year",
    "planning.today": "Today",
    "planning.addToday": "Add to today",
    "planning.list": "Your list",
    "planning.weeklyPlan": "Weekly plan",
    "planning.weekLead": "Use this to decide what belongs on today's list",
    "planning.avgCompletion": "Average completion",
    "planning.goalsTracked": "Goals tracked",
    "planning.weekReview": "Week review",
    "planning.weekReviewText": ({
      aligned,
      total,
      percent,
      monthlyAvg,
      focus,
    }) =>
      `Aligned with ${aligned}/${total} monthly goals (${percent}%). Monthly completion averages ${monthlyAvg}%; keep this week pointed at ${focus}`,
    "planning.monthReview": "Month review",
    "planning.monthReviewText": ({
      aligned,
      total,
      percent,
      yearGoals,
      monthlyAvg,
      focus,
    }) =>
      yearGoals
        ? `Aligned with ${aligned}/${total} month goals (${percent}%) against ${yearGoals} year goals. Monthly completion averages ${monthlyAvg}%; protect attention on ${focus}`
        : `No year goals yet, so this month cannot be checked against annual outcomes. Monthly completion averages ${monthlyAvg}%; start by clarifying year goals or push ${focus}`,
    "planning.noMonthlyFocus": "the weakest monthly goal",
    "planning.suggestedFocus": "Suggested focus",
    "planning.suggestionsLoading": "Generating suggestion tasks for you",
    "planning.generateDaySuggestions": "Generate day suggestions",
    "planning.generateWeekSuggestions": "Generate week suggestions",
    "planning.generateMonthSuggestions": "Generate month suggestions",
    "planning.generateYearSuggestions": "Generate year suggestions",
    "planning.suggestionsEmpty":
      "All goals planned for today — nothing more to suggest",
    "planning.suggestionsEmptyDay": "No day suggestions right now",
    "planning.suggestionsEmptyWeek": "No week suggestions right now",
    "planning.suggestionsEmptyMonth": "No month suggestions right now",
    "planning.suggestionsEmptyYear": "No year suggestions right now",
    "planning.suggestionPersistenceError":
      "We could not save that dismissal. Please try again.",
    "planning.remainingGap": "From remaining weekly gap",
    "planning.focusFallback":
      "Use the lowest-coverage goal as the first planning move",
    "planning.monthGoals": "Month goals",
    "planning.monthGoalsFor": ({ month }) => `Your ${month} goals`,
    "planning.monthTitle": "Plan your week from monthly priorities",
    "planning.monthLead":
      "Pick low-coverage monthly goals and convert them into this week's wins",
    "planning.monthPlanningLead":
      "Decide which monthly outcomes deserve the strongest push now",
    "planning.yearGoalsFor": ({ year }) => `${year} goals`,
    "planning.yearGoalsLead":
      "Use these as the right-side context for choosing monthly priorities",
    "planning.yearPlanningLead":
      "Set the annual outcomes you want your monthly work to move forward",
    "planning.yearGoal": "Year goal",
    "planning.yearGoalAnchor": "Keep monthly goals aligned with this outcome",
    "planning.noWeeklyGoals":
      "No weekly goals saved yet. Add weekly goals to see them here",
    "planning.noMonthGoals":
      "No month goals saved yet. Add month goals to see them here",
    "planning.noYearGoals":
      "No year goals saved yet. Add year goals in Account to see them here",
    "planning.addYearGoal": "Add",
    "planning.yearGoalPlaceholder": "Add a year goal",
    "planning.yearGoalExamples": "Year goal examples",
    "planning.yearGoalExampleProduct": "Ship product v1",
    "planning.yearGoalExampleHealth": "Run a half marathon",
    "planning.yearGoalExampleMoney": "Save 20% of income",
    "planning.addMonthGoal": "Add monthly goal",
    "planning.goalAddedToast": ({ title }) => `Goal added: “${title}”`,
    "planning.monthGoalPlaceholder": "Name the action or outcome",
    "planning.monthGoalTitleLabel": "Monthly action",
    "planning.monthGoalTargetLabel": "Target value",
    "planning.monthGoalProgressLabel": "Current progress",
    "planning.monthGoalTargetPlaceholder": "e.g. 20",
    "planning.monthGoalUnitLabel": "Unit",
    "planning.monthGoalUnitPlaceholder": "e.g. hours",
    "planning.weekGoalTitleLabel": "Weekly goal",
    "planning.weekGoalPlaceholder": "Name the weekly priority",
    "planning.weekGoalTargetLabel": "Target value",
    "planning.weekGoalProgressLabel": "Current progress",
    "planning.weekGoalUnitLabel": "Unit",
    "planning.weekGoalCategoryLabel": "Category",
    "planning.yearGoalTitleLabel": "Yearly goal",
    "planning.globalGoalTitleLabel": "Global goal",
    "planning.goalPlanLabel.week": "Plan for the week",
    "planning.goalPlanLabel.month": "Plan for the month",
    "planning.goalPlanLabel.year": "Plan for the year",
    "planning.goalPlanLabel.global": "Long-term plan",
    "planning.goalProgressLabel": "Progress",
    "planning.goalProgressSummary": ({ progress, target, unit }) =>
      `${progress} of ${target} ${unit}`,
    "planning.goalProgressAutoHint":
      "Updates automatically from completed actions",
    "planning.goalManualProgressCta": "+ I already started this goal",
    "planning.monthGoalComposerHint":
      "Add a target value and unit only when you want to measure progress",
    "planning.monthGoalRequiredError": "Enter a monthly action",
    "planning.monthGoalTargetError": "Enter a target greater than zero",
    "planning.monthGoalMetricPairError":
      "If you add a target value, add a unit too",
    "planning.monthGoalLinkError":
      "This year goal is no longer available. Choose another goal or no link.",
    "planning.monthGoalSaveError":
      "We couldn’t save this monthly goal. Please try again.",
    "planning.weekGoalSaveError":
      "We couldn’t save this weekly goal. Please try again.",
    "planning.monthGoalDefaultUnit": "outcome",
    "planning.noProgressMetric": "No progress metric",
    "planning.monthGoalSuggestionTitle": ({ title }) =>
      `Monthly step for: ${title}`,
    "planning.monthGoalSuggestionHint": () => "From the year goal",
    "planning.monthGoalSuggestionReason": ({ yearGoal }) =>
      `One monthly result from ${yearGoal}.`,
    "planning.linkYearGoal": "Linked year goal",
    "planning.linkedYearGoal": ({ goal }) => `Supports year goal: ${goal}`,
    "planning.noLinkedYearGoal": "No linked year goal",
    "planning.linkMonthlyGoal": "Linked monthly goal",
    "planning.linkedMonthlyGoal": ({ goal }) =>
      `Supports monthly goal: ${goal}`,
    "planning.noLinkedMonthlyGoal": "No linked monthly goal",
    "planning.globalGoals": "Global goals",
    "planning.globalGoalsLead":
      "Broader direction that should shape year goals",
    "planning.globalGoal": "Global goal",
    "planning.linkGlobalGoal": "Linked global goal",
    "planning.linkedGlobalGoal": ({ goal }) => `Supports global goal: ${goal}`,
    "planning.noLinkedGlobalGoal": "No linked global goal",
    "planning.addGlobalGoal": "Add global goal",
    "planning.globalGoalPlaceholder": "Add a global goal",
    "planning.globalGoalExamples": "Global goal examples",
    "planning.globalGoalExampleDiscipline":
      "Build a disciplined independent work system",
    "planning.globalGoalExampleHealth": "Protect long-term health",
    "planning.globalGoalExampleFamily": "Be present for family",
    "planning.noGlobalGoals": "No global goals saved yet. Add them in Account",
    "planning.monthFocus": ({ title }) =>
      `Start with the lowest-coverage monthly goal: ${title}`,
    "planning.yearFocus": ({ title }) =>
      `Keep the first year goal visible: ${title}`,
    "planning.addWeek": "Add to week plan",
    "planning.weekQuickAddLead":
      "Choose the week, then define a weekly priority that supports a monthly goal",
    "planning.weekSuggestionHint": "Fits this week only",
    "planning.monthSuggestionHint": "Fits this month only",
    "planning.monthSuggestionHintShort": "Fits this month only",
    "planning.yearSuggestionHintShort": "Fits this year only",
    "planning.useInWeekPlan": "Use in week plan",
    "planning.remainingMonth": ({ percent, remaining, unit }) =>
      `${percent}% completed · ${remaining} ${unit} remaining this month`,
    "planning.completedGoalStatus": "Completed",
    "planning.markGoalCompleted": "Mark goal as completed",
    "planning.markGoalActive": "Move goal back to active",
    "planning.reorderHandle": "Drag to reorder",
    "planning.moveGoalUp": "Move up",
    "planning.moveGoalDown": "Move down",
    "planning.onTrack": "On track",
    "planning.inMotion": "In motion",
    "planning.addGoal": "Add goal",
    "planning.copyGoal": "Copy",
    "planning.copyTitleSuffix": "(copy)",
    "planning.copyTitleSuffixNumbered": ({ count }) => `(copy ${count})`,
    "ragAdmin.label": "ADMIN",
    "ragAdmin.title": "RAG sources",
    "ragAdmin.lead":
      "Manage local source material for future recommendations. This pass stores sources only",
    "ragAdmin.sourceCount": ({ count }) => `${count} sources`,
    "ragAdmin.newSource": "New source",
    "ragAdmin.editSource": "Edit source",
    "ragAdmin.formLead":
      "Use structured fields so future retrieval can cite clear evidence",
    "ragAdmin.topic": "Topic",
    "ragAdmin.principle": "What it can help with",
    "ragAdmin.principlePlaceholder": "focus, sleep, consistency",
    "ragAdmin.principleHelp": "Separate several areas with commas",
    "ragAdmin.content": "Content",
    "ragAdmin.actionableAdvice": "Actionable advice",
    "ragAdmin.sourceType": "Source type",
    "ragAdmin.confidence": "Confidence",
    "ragAdmin.sourceUrl": "Source link",
    "ragAdmin.sourceType.book": "Book",
    "ragAdmin.sourceType.blog": "Blog",
    "ragAdmin.sourceType.research": "Research",
    "ragAdmin.confidence.high": "High",
    "ragAdmin.confidence.medium": "Medium",
    "ragAdmin.confidence.low": "Low",
    "ragAdmin.addSource": "Add source",
    "ragAdmin.saveChanges": "Save changes",
    "ragAdmin.sources": "Sources",
    "ragAdmin.sourcesLead": "Loaded from the backend knowledge base",
    "ragAdmin.errorCount": ({ count }) =>
      `${count} indexing error${Number(count) === 1 ? "" : "s"}`,
    "ragAdmin.indexError": "Indexing error",
    "ragAdmin.reindex": "Reindex",
    "ragAdmin.reindexAll": "Reindex all",
    "ragAdmin.reindexing": "Reindexing",
    "ragAdmin.reindexingOne": "Reindexing source",
    "ragAdmin.reindexedOne": "Source reindexed",
    "ragAdmin.reindexFailed": "Reindex failed. Check the AI service logs",
    "ragAdmin.reindexingAll": ({ count }) =>
      `Reindexing ${count} source${Number(count) === 1 ? "" : "s"}`,
    "ragAdmin.reindexedAll": "All sources reindexed",
    "ragAdmin.ingestion.ready": "Ready",
    "ragAdmin.ingestion.chunked": "Chunked",
    "ragAdmin.ingestion.embedded": "Embedded",
    "ragAdmin.ingestion.error": "Error",
    "ragAdmin.empty": "No RAG sources yet",
    "ragAdmin.required": "Fill all source fields before saving",
    "ragAdmin.added": "Source added",
    "ragAdmin.updated": "Source updated",
    "ragAdmin.deleted": "Source deleted",

    "aiUsage.label": "ADMIN",
    "aiUsage.title": "AI usage",
    "aiUsage.lead": "Review AI calls, tokens, and estimated cost per user.",
    "aiUsage.heroCount": ({ count }) => `${count} users`,
    "aiUsage.backToRag": "RAG admin",
    "aiUsage.currentView": "Usage table",
    "aiUsage.stats": "Usage summary",
    "aiUsage.totalCalls": "AI calls",
    "aiUsage.totalTokens": "Total tokens",
    "aiUsage.totalCost": "Estimated cost",
    "aiUsage.users": "Users",
    "aiUsage.tableTitle": "Per-user usage",
    "aiUsage.tableLead":
      "One row per user with request, token, and cost totals.",
    "aiUsage.loading": "Loading",
    "aiUsage.empty": "No AI usage logged yet",
    "aiUsage.loadError": "Unable to load AI usage summary",
    "aiUsage.user": "User",
    "aiUsage.calls": "Calls",
    "aiUsage.promptTokens": "Prompt tokens",
    "aiUsage.completionTokens": "Completion tokens",
    "aiUsage.cost": "Cost",
    "aiUsage.firstCallAt": "First call",
    "aiUsage.lastCallAt": "Last call",

    "week.label": "WEEK",
    "week.heroTitle": "Weekly Goals and Progress",
    "week.heroLead": "Track how daily actions contribute to weekly outcomes",
    "week.avgCompletion": "Avg completion",
    "week.goalsOnTrack": "Goals on track",
    "week.bestMomentum": "Best momentum",
    "week.goalsProgress": "Goals Progress",
    "week.progressLead":
      "Push low-coverage goals first to protect weekly outcomes",
    "week.completed": "Completed",
    "week.remaining": ({ percent, remaining, unit }) =>
      `${percent}% completed · ${remaining} ${unit} remaining`,
    "longPlans.label": "LONG PLANS",
    "longPlans.heroTitle": "Long Plans",
    "longPlans.switcher": "Goal range",
    "longPlans.week": "Week",
    "longPlans.month": "Month",
    "longPlans.year": "Year",
    "longPlans.global": "Global",
    "longPlans.weekLead": "Track the outcomes that should move this week",
    "longPlans.monthLead":
      "Review the monthly outcomes that give shape to the next few weeks",
    "longPlans.yearLead":
      "Keep the year-level direction visible while choosing near-term goals",
    "longPlans.globalLead":
      "Review the broadest goals that define your overall direction",
    "longPlans.avgCompletion": "Avg completion",
    "longPlans.goalsOnTrack": "Goals on track",
    "longPlans.bestMomentum": "Best momentum",
    "longPlans.activeScope": "Active scope",
    "longPlans.yearItems": "Year goals",
    "longPlans.globalItems": "Global goals",
    "longPlans.yearAnchor": "Primary anchor",
    "longPlans.globalAnchor": "Primary direction",
    "longPlans.weekGoals": "Week goals",
    "longPlans.monthGoals": "Month goals",
    "longPlans.yearGoals": "Year goals",
    "longPlans.globalGoals": "Global goals",
    "longPlans.weekProgressLead":
      "Push low-coverage goals first to protect weekly outcomes",
    "longPlans.monthProgressLead":
      "Use monthly progress to decide what deserves a stronger weekly push",
    "longPlans.emptyTitle": "No goals here yet",
    "longPlans.emptyLead":
      "Give this period one clear outcome, then turn it into a goal you can act on.",
    "longPlans.emptyFilteredTitle": "No goals match this filter",
    "longPlans.emptyFilteredLead":
      "Try another category or show all goals to find the next focus.",
    "longPlans.clearFilter": "Show all goals",
    "longPlans.yearProgressLead":
      "These are long-range anchors from your Account profile",
    "longPlans.completed": "Completed",
    "longPlans.remaining": ({ percent, remaining, unit }) =>
      `${percent}% completed · ${remaining} ${unit} remaining`,
    "longPlans.yearGoalMeta": "Year-level outcome",
    "longPlans.yearGoalLead":
      "Break this into monthly goals, then weekly commitments",
    "longPlans.noYearGoals":
      "No year goals saved yet. Add year goals in Account to see them here",
    "longPlans.globalGoalsLead":
      "These are broader than year goals and define the overall direction",
    "longPlans.globalGoal": "Global goal",
    "longPlans.globalGoalMeta": "Global direction",
    "longPlans.globalGoalLead":
      "Use this to shape year goals, then monthly outcomes",
    "longPlans.noGlobalGoals": "No global goals saved yet. Add them in Account",
    "goalImage.generate": "Generate image",
    "goalImage.title": "Goals image",
    "goalImage.preview": "Preview",
    "goalImage.download": "Download PNG",
    "goalImage.copyImage": "Copy image",
    "goalImage.copySuccess": "Image copied to clipboard.",
    "goalImage.copyError": "Could not copy image. Try downloading it instead.",
    "goalImage.copyUnavailable":
      "Image copying is unavailable in this browser.",
    "goalImage.generating": "Generating…",
    "goalImage.close": "Close",
    "goalImage.generationError": "Could not generate image. Try again.",
    "goalImage.noGoals": "No goals to export",
    "goalImage.totalGoals": "Total goals",
    "goalImage.completed": "Completed",
    "goalImage.averageProgress": "Average progress",
    "goalImage.action": "Action",
    "goalImage.generated": "Generated",
    "goalImage.footerTagline": "Turn long-term goals into daily action",
    "goalImage.longTermDirection": "Long-term direction",
    "goalImage.weekTitle": "My Week Goals",
    "goalImage.monthTitle": "My Month Goals",
    "goalImage.yearTitle": "My Year Goals",
    "goalImage.globalTitle": "My Global Goals",
    "goalImage.weekSubtitle":
      "Overview of all goals and actions for this week.",
    "goalImage.monthSubtitle": "The priorities shaping this month.",
    "goalImage.yearSubtitle": "The outcomes guiding this year.",
    "goalImage.globalSubtitle": "The direction behind your long-term plans.",

    "calendar.label": "CALENDAR",
    "calendar.heroSub": ({ label }) =>
      `${label} view · times shown from 06:00–23:00`,
    "calendar.tablist": "Calendar view",
    "calendar.day": "Day",
    "calendar.week": "Week",
    "calendar.threeDays": "3 days",
    "calendar.month": "Month",
    "calendar.noFixedTime": "No fixed time",
    "calendar.done": "Done",
    "calendar.todo": "To do",
    "calendar.status": "Status",
    "calendar.editTask": "Edit task",
    "calendar.editTaskHint": "Adjust title, type, time, and completion status",
    "calendar.editTaskAria": ({ title }) => `Edit ${title}`,
    "calendar.titleRequired": "Enter a title before duplicating",
    "calendar.invalidTime": "End time must be different from start time",
    "calendar.wrapsContinuesAria": ({ title }) =>
      `${title} continues from the previous day`,
    "calendar.groupMoveFailed":
      "Some goals could not be moved. Their dates were refreshed.",
    "calendar.habitDayMoveBlocked":
      "A habit stays on its own day. Its time was changed for this day instead.",
    "calendar.now": "Now",
    "calendar.setStartTimeToNow": "Set start time to now",
    "calendar.setEndTimeToNow": "Set end time to now",
    "calendar.weekdays.mon": "Mon",
    "calendar.weekdays.tue": "Tue",
    "calendar.weekdays.wed": "Wed",
    "calendar.weekdays.thu": "Thu",
    "calendar.weekdays.fri": "Fri",
    "calendar.weekdays.sat": "Sat",
    "calendar.weekdays.sun": "Sun",
    "calendar.month.todayPlan": "Today's plan",
    "calendar.month.planForDate": "Plan for this date",
    "calendar.month.noPlan": "No saved plan for this date yet",
    "calendar.month.noActions":
      "No actions yet. Add some from Today or Planning",
    "calendar.month.noActionsForDate": "No actions saved for this date yet",
    "calendar.month.pickToday":
      "Only today's actions are shown here. Pick today to see your list",
    "calendar.anyTime": "Any time",
    "dateNav.controls": "Date controls",
    "dateNav.previous": "Prev",
    "dateNav.current": "Current",
    "dateNav.next": "Next",
    "dateNav.pickDate": "Pick a date",
    "dateNav.close": "Close date picker",

    "insights.label": "INSIGHTS",
    "insights.title": "AI Discipline Feedback",
    "insights.emptyTitle": "No AI insight yet",
    "insights.emptyPrompt":
      "Generate an AI insight to see your personalized analysis.",
    "insights.eligibilityProgress": ({ count, required }) =>
      `${count}/${required} active planning days recorded.`,
    "insights.eligibilityRemaining": ({ count }) =>
      `${count} more planning day${count === 1 ? "" : "s"} needed before generation is available.`,
    "insights.generate": "Generate insight",
    "insights.loadError": "We could not load your Insights data. Try again.",
    "insights.lead":
      "Behavior-focused coaching from your history, goals, reflection, and completion signals",
    "insights.executionStrong": "Execution strong",
    "insights.executionSteady": "Execution steady",
    "insights.executionRisk": "Execution at risk",
    "insights.executionNeedsWork": "Needs execution support",
    "insights.lockedIn": "Locked-in rhythm",
    "insights.building": "Building rhythm",
    "insights.fresh": "Fresh streak",
    "insights.reflectionCaptured": "Reflection captured",
    "insights.reflectionMissing": "Reflection missing",
    "insights.reflectionCount": ({ count }) => `${count}/7`,
    "insights.summary": "AI Summary",
    "insights.completionPercent": ({ percent }) => `${percent}% completion`,
    "insights.metrics": "Insight metrics",
    "insights.completed": "Completed this week",
    "insights.actionsToday": "actions today",
    "insights.currentStreak": "Current streak",
    "insights.consistencySignal": "consistency signal",
    "insights.reflection": "Reflection",
    "insights.added": "Added",
    "insights.missing": "Missing",
    "insights.inputQuality": "input quality",
    "insights.sourceSignals": "Source Signals",
    "insights.inputsUsed": "Inputs used for this recommendation",
    "insights.date": "Date",
    "insights.completionSignal": "Completion signal",
    "insights.streakSignal": "Streak signal",
    "insights.dayStreakActive": ({ streak }) => `${streak} day streak active`,
    "insights.reflectionSignal": "Reflection signal",
    "insights.noReflection": "No reflection submitted today",
    "insights.aiHealth": ({ score }) => `AI health ${score}%`,
    "insights.nextActions": "Next Actions",
    "insights.priorityMoves": "Priority moves based on weekly gaps",
    "insights.contribution": ({ amount, unit }) =>
      `Contribution: ${amount} ${unit}`,
    "insights.noPriorityGap":
      "No priority gap found. Keep your current plan and protect focus blocks",
    "insights.addFromSuggestion":
      "Turn a recommendation into a real plan item before leaving this screen",
    "insights.modelReading": "Model reading",
    "insights.weakestSignal": ({ signal }) =>
      `Improve ${signal.toLowerCase()} to raise recommendation quality`,
    "insights.aiGenerated":
      "Uses recent weeks of execution, linkage across goals, reflection, and retrieved principles—not only today's snapshot",
    "insights.todayDone": "Today completion",
    "insights.todayDoneValue": ({ done, total, percent }) =>
      `${done}/${total} actions done (${percent}%)`,
    "insights.whatBlocked": "What may have blocked it",
    "insights.blockerLow":
      "Nothing critical is visible; protect the rhythm and avoid adding unnecessary scope",
    "insights.blockerNoReflection":
      "No reflection was added, so the system can only infer blockers from unfinished actions",
    "insights.blockerFromReflection": ({ reflection }) =>
      `Your reflection points to: ${reflection}`,
    "insights.tomorrowMove": "What to do tomorrow",
    "insights.tomorrowProtect":
      "Keep the same plan shape and reserve one protected focus block early",
    "insights.tomorrowRecover":
      "Move the hardest unfinished goal to the first focus block and reduce the rest of the day",
    "insights.problemZonesTitle": "Problem-zone battle",
    "insights.problemZonesLead":
      "How your challenge areas show up in execution—and concrete experiments to manage each zone",
    "insights.problemZoneFallback": "No problem zone set yet",
    "insights.problemZoneStable":
      "Movement is visible. Keep one small constraint for this zone tomorrow",
    "insights.problemZoneNeedsWork":
      "This zone still needs a clearer barrier: one time block, one limit, one next action",
    "insights.alignmentTitle": "Goal alignment",
    "insights.alignmentLead":
      "How tightly daily through global layers connect—strong linkage reduces thrash",
    "insights.alignDailyWeekly": "Daily goals → weekly goals",
    "insights.alignWeeklyMonth": "Weekly goals → month goals",
    "insights.alignMonthYear": "Month goals → year goals",
    "insights.alignYearGlobal": "Year goals → global goals",
    "insights.alignDailyWeeklyDetail": ({ linked, total }) =>
      `${linked}/${total} daily goal actions are linked to a weekly goal`,
    "insights.alignParentDetail": ({ linked, total }) =>
      `${linked}/${total} goals look connected to the parent level`,
    "insights.reloadWithAI": "Regenerate with AI",
    "insights.reloading": "Generating",
    "insights.generatedBy": ({ provider, model }) =>
      `Generated by ${provider}/${model}`,
    "insights.reloadError": "Could not generate AI insights now",
    "insights.analysisStepsTitle": "Analysis steps",
    "insights.analysisStepsLead":
      "Observable signals the AI weighed—not prescriptions by themselves",
    "insights.patternWarningLabel": "Pattern to watch",
    "insights.zoneHowYouManage": "How you tend to handle this:",
    "insights.zoneAdvice": "Try next:",
    "insights.primaryRecommendation": "Recommendation",
    "insights.sourcesTitle": "Sources (RAG)",
    "insights.sourcesLead":
      "Knowledge-base passages retrieved for this insight. Empty when nothing matched closely enough",
    "insights.sourcesEmpty":
      "No knowledge-base passages were attached to this run (search returned weak matches or the index is empty)",
    "insights.sourceUntitled": "Untitled source",
    "insights.sourceRank": ({ rank }) => `#${rank}`,
    "insights.sourceScore": ({ score }) => `score ${score}`,
    "insights.sourcesCompactTitle": "Sources",
    "insights.sourcesCaption": "Knowledge base sources used for this insight",
    "insights.sourceColTopic": "Topic",
    "insights.sourceColType": "Type",
    "insights.sourceColLink": "Link",
    "insights.sourceColConfidence": "Confidence",
    "insights.sourceOpenLink": "Open",
    "insights.sourcesEmptyShort": "No source rows for this run",

    "aside.aiRecommendations": "AI Recommendations",
    "aside.aiTitle": "Recommendations",
    "aside.systemStatus": "System status",
    "aside.todayOps": "Today operations",
    "aside.unscheduledMeta": "Still needs a time or a deliberate decision",
    "aside.savedLocally": "Saved locally",
    "aside.localState": "Local state updated on every change",
    "aside.modelAssisted": "Model-assisted",
    "aside.beforeNoon": ({ title }) => `${title}. Prioritize this before noon`,
    "aside.consistency":
      "Keep consistency with one strategic action and one habit",
    "aside.weeklyFocus": "Weekly Focus",
    "aside.weeklyFocusLead":
      "Prioritize goals below 60% completion when planning tomorrow",
    "aside.planningRule": "Planning Rule",
    "aside.planningRuleLead":
      "Keep no more than 3 strategic goals and 2 support habits for one day",
    "logic.strongExecution": "Strong execution rhythm",
    "logic.balancedExecution": "Balanced execution with room for one more push",
    "logic.lowExecution":
      "Low completion today. Reduce scope and protect one focus block tomorrow",
    "logic.recapWithFeedback": ({ done, total, percent, cadence, feedback }) =>
      `You completed ${done}/${total} actions (${percent}%). ${cadence} Your note says: "${feedback}". AI recommendation: keep tomorrow's first 90 minutes for the most strategic goal and plan one recovery habit in the evening`,
    "logic.recapWithoutFeedback": ({ done, total, percent, cadence }) =>
      `You completed ${done}/${total} actions (${percent}%). ${cadence} Add a short reflection to improve recommendation quality tomorrow`,
    "logic.pushGoal": ({ title, amount, unit }) =>
      `Push "${title}" with ${amount} ${unit}`,
    "recommendation.none":
      "No recommendations right now. Keep the current plan and protect focus time",
    "recommendation.apply": "Apply to plan",
    "recommendation.dismiss": "Dismiss",
    "goalScope.global": "Long-term",
    "goalScope.year": "Yearly",
    "goalScope.month": "Monthly",
    "goalScope.week": "Weekly",
    "goalDetails.back": "Back",
    "goalDetails.close": "Close",
    "goalDetails.completed": "Completed",
    "goalDetails.loadError": "Couldn't load this goal",
    "goalDetails.loadingNotes": "Loading notes…",
    "goalDetails.notes": "Notes",
    "goalDetails.parent": ({ title }) => `Part of ${title}`,
    "goalDetails.breakdown": "Progress breakdown",
    "goalDetails.loadingChildren": "Loading contributors…",
    "goalDetails.noChildren": "No direct contributors yet",
    "goalDetails.active": "Active",
    "goalDetails.contributed": "Contributed",
    "goalDetails.dailyItem": "Daily item",
    "goalDetails.habit": "Habit",
    "goalNotes.edit": "Edit",
    "goalNotes.preview": "Preview",
    "goalNotes.saving": "Saving…",
    "goalNotes.saved": "Saved",
    "goalNotes.saveError": "Couldn't save notes",
    "goalNotes.placeholder": "Add context for this goal. Type @ to link another goal.",
    "goalNotes.emptyPreview": "Nothing here yet. Switch to Edit to add notes.",
    "goalNotes.mentionSearch": "Search goals",
    "goalNotes.noGoalsFound": "No goals found",
    "goalNotes.mentionUnavailable": "This goal is no longer available",
    "goalNotes.mentionedIn": "Mentioned in",
    "goalNotes.editorHint": "Type naturally · use / for blocks · @ for goals",
    "goalNotes.commandSearch": "Note commands",
    "goalNotes.noCommandsFound": "No commands found",
    "goalNotes.commandText": "Text",
    "goalNotes.commandHeading1": "Heading 1",
    "goalNotes.commandHeading2": "Heading 2",
    "goalNotes.commandHeading3": "Heading 3",
    "goalNotes.commandTodo": "To-do list",
    "goalNotes.commandBulletList": "Bullet list",
    "goalNotes.commandOrderedList": "Numbered list",
    "goalNotes.commandQuote": "Quote",
    "goalNotes.commandCodeBlock": "Code block",
    "goalNotes.commandDivider": "Divider",
    "goalNotes.taskCheckbox": "Checklist item",
    "goalNotes.untitledTask": "Untitled task",
  },
  pl: {
    "app.site": "Nawigacja",
    "app.brandTag": "System codziennej dyscypliny",
    "app.today": "Dzisiaj",
    "app.week": "Cele długoterminowe",
    "app.calendar": "Kalendarz",
    "app.planning": "Pokój planowania",
    "app.insights": "Analizy AI",
    "app.coach": "Dio",
    "app.title.coach": "Disciplio · Dio",
    "app.collapseNavigation": "Zwiń nawigację",
    "app.expandNavigation": "Rozwiń nawigację",
    "app.resizeNavigation": "Zmień rozmiar nawigacji",
    "app.scroll": "Przewiń",
    "app.language": "Język",
    "sessionLoad.label": "Ładowanie konta",
    "sessionLoad.title": "Nie udało się wczytać konta",
    "sessionLoad.description":
      "Sprawdź połączenie i spróbuj ponownie. Twoja akceptacja polityk nie uległa zmianie.",
    "sessionLoad.rateLimited": ({ seconds }) =>
      `Zbyt wiele prób. Spróbuj ponownie za ${seconds}s.`,
    "sessionLoad.retry": "Spróbuj ponownie",
    "browserStorage.title": "Cookies i pamięć",
    "browserStorage.description":
      "Zachowaj niedokończoną konfigurację i preferencje na tym urządzeniu oraz — wyłącznie za Twoją zgodą — włącz przyjazną prywatności analitykę produktu i opcjonalne nagrywanie sesji w aplikacji, które pomagają nam zrozumieć sposób korzystania z funkcji i ulepszać Disciplio. Nigdy nie zbieramy treści Twoich zadań, celów, refleksji ani rozmów z AI.",
    "browserStorage.learnMore": "Dowiedz się więcej",
    "browserStorage.accept": "Zezwól",
    "browserStorage.reject": "Odrzuć",
    "browserStorage.acceptAria": "Zezwól na pamięć opcjonalną",
    "browserStorage.rejectAria": "Odrzuć pamięć opcjonalną",
    "browserStorage.preferencesTitle": "Ustawienie pamięci przeglądarki",
    "browserStorage.preferencesAccepted":
      "Opcjonalna pamięć przeglądarki i przyjazna prywatności analityka, w tym nagrywanie sesji, gdy jest włączone, są aktywne na tym urządzeniu.",
    "browserStorage.preferencesRejected":
      "Opcjonalna pamięć przeglądarki i analityka są wyłączone na tym urządzeniu.",
    "browserStorage.change": "Zmień ustawienie",
    "ai.recommendationDisclaimer":
      "Rekomendacja AI · może być niedokładna i nie jest wiążąca",
    "ai.generatedDisclaimer":
      "Wygenerowane przez AI · może być niedokładne i nie jest wiążące",
    "ai.recommendationLabel": "Rekomendacja AI",
    "aiAllowance.status": ({ feature, remaining, limit, period, reset }) =>
      `Darmowe AI · ${feature}: ${remaining}/${limit} pozostało ${period} · odnowienie ${reset}`,
    "aiAllowance.feature.REFLECTION_ADVICE": "Porady do refleksji",
    "aiAllowance.feature.INSIGHTS_DAILY": "Analiz AI",
    "aiAllowance.feature.PLANNING_TASK_SUGGESTION": "Sugestii planowania",
    "aiAllowance.feature.COACH_GENERATION": "Wiadomości Coach Dio",
    "aiAllowance.feature.COACH_TRANSCRIPTION": "Transkrypcje głosowe Dio",
    "aiAllowance.today": "dzisiaj",
    "aiAllowance.thisWeek": "w tym tygodniu",
    "aiAllowance.later": "później",
    "aiLimits.title": "Limity AI",
    "aiLimits.description": "Limity planu Free dla każdej funkcji AI.",
    "aiLimits.row": ({ remaining, limit, period, reset }) =>
      `${remaining}/${limit} pozostało ${period} · odnowienie ${reset}`,
    "aiLimits.unlimited": "Bez limitu lokalnie",
    "aiLimits.unavailable": "Limity AI są obecnie niedostępne.",
    "app.quickActions": "Szybkie akcje",
    "app.settings": "Ustawienia",
    "app.account": "Konto",
    "app.profileEmpty": "Dodaj mapę swojego fokusu",
    "app.accountFallbackName": "Skonfiguruj swój profil",
    "completion.taskMessage": ({ title }) => `Zadanie ukończone: „${title}”`,
    "completion.goalMessage": ({ title }) => `Cel ukończony: „${title}”`,
    "onboarding.aria": "Lista kontrolna konfiguracji Disciplio",
    "onboarding.label": "Twoja droga do momentu aha",
    "onboarding.title": "Główny przepływ produktu",
    "onboarding.account": "Zarejestruj się lub zaloguj",
    "onboarding.profile": "Uzupełnij onboarding",
    "onboarding.progress": ({ completed, total }) =>
      `${completed} z ${total} ukończono`,
    "onboarding.open": "Otwórz kolejny krok",
    "onboarding.verify": "Sprawdź zapisane dane",
    "onboarding.clickHere": "Kliknij tutaj, aby zacząć",
    "onboarding.moveGuide": "Przenieś listę",
    "onboarding.collapse": "Zwiń listę",
    "onboarding.expand": "Rozwiń listę",
    "onboarding.pause": "Wstrzymaj podpowiedzi",
    "onboarding.resume": "Wznów przewodnik",
    "onboarding.skip": "Pomiń listę",
    "onboarding.skipTitle": "Pominąć listę konfiguracji?",
    "onboarding.skipDescription":
      "Na pewno? Pomiń ją tylko wtedy, gdy znasz cały sposób działania Disciplio. Nadal możesz korzystać z aplikacji, ale ta lista już nie wróci.",
    "onboarding.skipCancel": "Zachowaj listę",
    "onboarding.skipConfirm": "Tak, pomiń onboarding",
    "onboarding.skipFailed": "Nie udało się pominąć listy. Spróbuj ponownie.",
    "onboarding.statusSkipped": "Pominięto",
    "onboarding.statusDone": "Gotowe",
    "onboarding.statusNext": "Dalej",
    "onboarding.step.globalGoal.title": "Dopracuj globalny cel",
    "onboarding.step.globalGoal.description":
      "Dodaj długoterminowy rezultat lub zmień nazwę zarejestrowanego celu.",
    "onboarding.step.yearGoal.title": "Utwórz cel roczny",
    "onboarding.step.yearGoal.description":
      "Utwórz cel roczny i powiąż go z jednym z celów globalnych.",
    "onboarding.step.monthlyPriority.title": "Wybierz priorytet miesięczny",
    "onboarding.step.monthlyPriority.description":
      "Zamień cel roczny w jeden konkretny priorytet na ten miesiąc.",
    "onboarding.step.weeklyGoal.title": "Ustaw mierzalny cel tygodniowy",
    "onboarding.step.weeklyGoal.description":
      "Wybierz wartość i jednostkę, a następnie połącz je z priorytetem miesięcznym.",
    "onboarding.step.todayPlan.title": "Zaplanuj dzisiaj",
    "onboarding.step.todayPlan.description":
      "Dodaj jedno zadanie na dziś, wybierz jego mierzalny cel tygodniowy i wpisz dodatni wkład.",
    "onboarding.step.dailyTask.title": "Wykonaj zadanie dzienne",
    "onboarding.step.dailyTask.description":
      "Wróć do widoku Dzisiaj i odhacz dowolne zadanie z listy.",
    "onboarding.step.streak.title": "Poznaj swoją serię Disciplio",
    "onboarding.step.streak.description":
      "Seria liczy kolejne aktywne dni z ukończonym zadaniem lub nawykiem. Otwórz kartę serii, aby zobaczyć ciąg i dowiedzieć się, jak go utrzymać.",
    "onboarding.step.reflection.title": "Wyślij refleksję",
    "onboarding.step.reflection.description":
      "Zapisz jedną szczerą notatkę o dniu.",
    "onboarding.step.aiRecommendation.title": "Otrzymaj użyteczną rekomendację",
    "onboarding.step.aiRecommendation.description":
      "Zapisz refleksję i przejrzyj rekomendację albo świadomie pomiń AI.",
    "onboarding.step.return.title": "Wróć i sprawdź",
    "onboarding.step.return.description":
      "Odśwież Disciplio, aby potwierdzić, że Twoja praca nadal jest zapisana.",
    "onboarding.step.aiConsent.title": "Wybierz ustawienie AI",
    "onboarding.step.aiConsent.description":
      "Zdecyduj, czy Disciplio może generować opcjonalne rekomendacje, zanim zaczniesz dzień.",
    "aiConsent.label": "Przed użyciem AI",
    "aiConsent.title": "Disciplio może generować opcjonalne rekomendacje",
    "aiConsent.description":
      "Ta opcjonalna funkcja używa AI. Wybrane cele, zadania, plany, notatki, refleksje i inny istotny kontekst Disciplio mogą zostać wysłane do zewnętrznych dostawców AI. Disciplio obecnie używa OpenRouter do kierowania tych żądań do wybranych dostawców modeli AI. Twój adres e-mail nie jest dołączany. Wynik może być niedokładny lub nieodpowiedni i nie jest poradą zawodową. Możesz go edytować, odrzucić, zignorować lub usunąć, pozostajesz odpowiedzialny za decyzje i możesz korzystać z Disciplio bez AI.",
    "aiConsent.enable": "Włącz AI",
    "aiConsent.decline": "Kontynuuj bez AI",
    "app.title.today": "Disciplio · Dzisiaj",
    "app.title.week": "Disciplio · Cele długoterminowe",
    "app.title.calendar": "Disciplio · Kalendarz",
    "app.title.planning": "Disciplio · Pokój planowania",
    "app.title.insights": "Disciplio · Analizy AI",
    "coach.brand": "Dio",
    "coach.brandSub": "AI COACH",
    "coach.newChat": "Nowy czat",
    "coach.dashboard": "Dzisiaj",
    "coach.recent": "Ostatnie",
    "coach.history": "Historia rozmów",
    "coach.collapseHistory": "Zwiń historię rozmów",
    "coach.expandHistory": "Rozwiń historię rozmów",
    "coach.noConversations": "Tutaj pojawią się Twoje rozmowy.",
    "coach.greeting": "Cześć, tu Dio. Nad czym dziś pracujemy?",
    "coach.emptyHint": "Widzę Twoje cele, plany i refleksje. Zapytaj o ten tydzień, o decyzję, która Cię blokuje, albo o coś, co ciągle się przesuwa.",
    "coach.placeholder": "Zapytaj Dio o cokolwiek…",
    "coach.send": "Wyślij wiadomość",
    "coach.stopResponse": "Zatrzymaj odpowiedź",
    "coach.thinking": "Dio myśli",
    "coach.retry": "Spróbuj ponownie",
    "coach.delete": "Usuń rozmowę",
    "coach.deleteFailed": "Dio nie mógł usunąć tej rozmowy. Została przywrócona.",
    "coach.historyRefreshFailed": "Rozmowa została usunięta, ale Dio nie mógł odświeżyć historii.",
    "coach.confirmDeleteGoalPrompt": "„{title}” to cel długoterminowy. Usunąć go?",
    "coach.confirmDeleteHabitPrompt": "Usunąć wszystkie wystąpienia „{title}”?",
    "coach.confirmDeletePlanItemPrompt": "Usunąć element planu „{title}”?",
    "coach.confirmBatchDeletePrompt": "Dio znalazł {count} zmian wymagających potwierdzenia:",
    "coach.confirmDeleteButton": "Usuń",
    "coach.cancelDeleteButton": "Zachowaj",
    "coach.taskProposalPrompt": "Dio przygotował to zadanie na podstawie Twojej prośby. Dodać je do planu?",
    "coach.taskProposalDetails": "Przed dodaniem możesz zmienić tytuł, dodać czas lub połączyć zadanie z celem tygodniowym.",
    "coach.taskProposalTitle": "Tytuł",
    "coach.taskProposalStart": "Początek",
    "coach.taskProposalEnd": "Koniec",
    "coach.taskProposalWeeklyGoal": "Cel tygodniowy (opcjonalnie)",
    "coach.noWeeklyGoal": "Bez celu tygodniowego",
    "coach.taskProposalDate": "Data: {date}",
    "coach.addProposedTask": "Dodaj zadanie",
    "coach.skipProposedTask": "Nie teraz",
    "coach.capabilities.open": "Pokaż, co potrafi Dio",
    "coach.capabilities.tooltip": "Kliknij, aby zobaczyć wszystkie funkcje dostępne w Dio.",
    "coach.capabilities.title": "Co potrafi Dio",
    "coach.capabilities.description": "Dio może dziś wykonać te działania w Disciplio.",
    "coach.capabilities.close": "Zamknij listę możliwości Dio",
    "coach.capabilities.tasks.title": "Codzienne zadania",
    "coach.capabilities.tasks.description": "Twórz, edytuj, kończ, przenoś, duplikuj i usuwaj zadania.",
    "coach.capabilities.goals.title": "Cele długoterminowe",
    "coach.capabilities.goals.description": "Twórz, edytuj, kończ, wznawiaj i usuwaj cele globalne lub roczne.",
    "coach.capabilities.plans.title": "Plany miesięczne i tygodniowe",
    "coach.capabilities.plans.description": "Twórz, edytuj, kończ, wznawiaj i usuwaj elementy planów.",
    "coach.capabilities.batch.title": "Zmiany zbiorcze",
    "coach.capabilities.batch.description": "Wykonuj kolejno do 10 obsługiwanych zmian z jednej wiadomości.",
    "coach.capabilities.coaching.title": "Coaching i refleksja",
    "coach.capabilities.coaching.description": "Odpowiadaj na pytania o cele, plany, zadania i refleksje w Disciplio.",
    "coach.capabilities.voice.title": "Wiadomości głosowe",
    "coach.capabilities.voice.description": "Transkrybuj wiadomość głosową, aby Dio mógł z nią pracować.",
    "coach.capabilities.confirmations.title": "Bezpieczne potwierdzenia",
    "coach.capabilities.confirmations.description": "Proś o potwierdzenie przed usunięciem i zostawiaj decyzję Tobie.",
    "coach.capabilities.limitations.title": "Dio skupia się na Disciplio",
    "coach.capabilities.limitations.description": "Dio nie wysyła e-maili ani nie wykonuje działań zewnętrznych.",
    "coach.batchActionsCompleted": "Wykonane zmiany:",
    "coach.batchActionFailed": "Nie udało się wykonać: {action}",
    "coach.batchActionPending": "Oczekuje na potwierdzenie: {action}",
    "coach.actionLabel.planningChange": "tej zmiany w planie",
    "coach.actionLabel.listTasks": "przejrzeć zadania",
    "coach.actionLabel.createTask": "utworzyć zadanie",
    "coach.actionLabel.updateTask": "zaktualizować zadanie",
    "coach.actionLabel.completeTask": "ukończyć zadanie",
    "coach.actionLabel.deleteTask": "usunąć zadanie",
    "coach.actionLabel.moveTask": "przenieść zadanie",
    "coach.actionLabel.duplicateTask": "zduplikować zadanie",
    "coach.actionLabel.listGoals": "przejrzeć cele",
    "coach.actionLabel.createGoal": "utworzyć cel",
    "coach.actionLabel.updateGoal": "zaktualizować cel",
    "coach.actionLabel.completeGoal": "ukończyć cel",
    "coach.actionLabel.deleteGoal": "usunąć cel",
    "coach.actionLabel.listMonthlyPlan": "przejrzeć plan miesięczny",
    "coach.actionLabel.createMonthlyPlanItem": "utworzyć element planu miesięcznego",
    "coach.actionLabel.updateMonthlyPlanItem": "zaktualizować element planu miesięcznego",
    "coach.actionLabel.completeMonthlyPlanItem": "ukończyć element planu miesięcznego",
    "coach.actionLabel.deleteMonthlyPlanItem": "usunąć element planu miesięcznego",
    "coach.actionLabel.listWeeklyPlan": "przejrzeć plan tygodniowy",
    "coach.actionLabel.createWeeklyPlanItem": "utworzyć element planu tygodniowego",
    "coach.actionLabel.updateWeeklyPlanItem": "zaktualizować element planu tygodniowego",
    "coach.actionLabel.completeWeeklyPlanItem": "ukończyć element planu tygodniowego",
    "coach.actionLabel.deleteWeeklyPlanItem": "usunąć element planu tygodniowego",
    "coach.actionCancelled": "Dobrze, zostaje bez zmian.",
    "coach.actionConfirmedTask": "Dodano „{title}” do planu.",
    "coach.actionCancelledTask": "Dobrze — nie dodałem tej propozycji.",
    "coach.actionFailedTask": "Nie udało się dodać zadania, więc plan nie został zmieniony.",
    "coach.actionConfirmedDeleteGoal": "Usunięto „{title}”.",
    "coach.actionConfirmedDeleteHabit": "Usunięto „{title}” (wszystkie wystąpienia).",
    "coach.actionConfirmedDeletePlanItem": "Usunięto „{title}”.",
    "coach.actionFailedTargetGone": "Tego już tam nie ma — mogło się już zmienić. Nic nie zostało usunięte.",
    "coach.free": "Darmowy",
    "coach.fast": "Szybki",
    "coach.moreThinking": "Więcej namysłu",
    "coach.freeHint": "Codzienne pytania",
    "coach.fastHint": "Krótsze, szybsze odpowiedzi",
    "coach.moreThinkingHint": "Trudniejsze decyzje, wolniej",
    "coach.effortLabel": "Profil odpowiedzi",
    "coach.effortAria": "Wybierz profil odpowiedzi",
    "coach.startVoice": "Nagraj wiadomość głosową",
    "coach.stopVoice": "Zatrzymaj nagrywanie",
    "coach.transcribing": "Transkrypcja…",
    "coach.voiceUnsupported": "Nagrywanie głosu nie jest obsługiwane w tej przeglądarce.",
    "coach.voicePermission": "Zezwól na dostęp do mikrofonu, aby nagrać wiadomość.",
    "coach.voiceTooLong": "Nagranie było za długie. Spróbuj krótszej wiadomości.",
    "coach.voiceTooLarge": "Nagranie jest za duże. Spróbuj krótszej wiadomości.",
    "coach.voiceUnavailable": "Transkrypcja głosu jest chwilowo niedostępna. Spróbuj ponownie lub wpisz wiadomość.",
    "coach.voiceFailed": "Transkrypcja nie powiodła się. Spróbuj ponownie lub wpisz wiadomość.",
    "coach.streamFailed": "Dio nie mógł dokończyć odpowiedzi.",
    "coach.actionCommitted": "Zmiana została zapisana.",
    "coach.authRequired": "Sesja wygasła. Zaloguj się ponownie, aby kontynuować rozmowę z Dio.",
    "coach.logIn": "Zaloguj się",
    "coach.responseStopped": "Odpowiedź zatrzymana. Spróbuj ponownie, gdy będziesz gotowy.",
    "coach.retryLimit": "Ta odpowiedź osiągnęła limit ponownych prób.",
    "coach.invalidRequest": "To żądanie Coach jest nieprawidłowe. Sprawdź wiadomość i spróbuj ponownie.",
    "coach.quota": "Limit Coach został na razie wykorzystany.",
    "coach.rateLimited": ({ seconds }) => `Dio jest chwilowo ograniczony. Spróbuj ponownie za ${seconds}s.`,
    "coach.rateLimitedReady": "Dio był chwilowo ograniczony. Możesz spróbować ponownie.",
    "coach.consent": "Zaakceptuj użycie AI w Koncie, aby rozmawiać z Dio.",
    "coach.openAccount": "Otwórz ustawienia AI w Koncie",
    "coach.disclaimer": "AI może się mylić. Ważne szczegóły sprawdź w swoich planach Disciplio.",
    "coach.copyAnswer": "Kopiuj odpowiedź",
    "coach.copiedAnswer": "Odpowiedź skopiowana.",
    "coach.copyAnswerError": "Nie udało się skopiować odpowiedzi.",
    "coach.feedback.title": "Opowiedz nam o tej odpowiedzi",
    "coach.feedback.positive": "Pomocna",
    "coach.feedback.negative": "Niepomocna",
    "coach.feedback.prompt": "Co wpłynęło na tę ocenę? (opcjonalnie)",
    "coach.feedback.reasonLabel": "Opcjonalna uwaga",
    "coach.feedback.reasonPlaceholder": "Dodaj krótką uwagę…",
    "coach.feedback.send": "Wyślij opinię",
    "coach.feedback.sending": "Wysyłanie…",
    "coach.feedback.close": "Zamknij",
    "coach.feedback.change": "Zmień opinię",
    "coach.feedback.thanks": "Dziękujemy za opinię.",
    "coach.feedback.error": "Nie udało się zapisać opinii. Spróbuj ponownie.",
    "coach.timestamp.answer": "Odpowiedź napisana",
    "coach.timestamp.yesterday": "Wczoraj",
    "coach.scrollLatest": "Przewiń do najnowszych",
    "coach.mobileHistory": "Otwórz historię rozmów",
    "coach.closeHistory": "Zamknij historię rozmów",
    "coach.starter.plan": "Zaplanuj mój tydzień",
    "coach.starter.decision": "Przemyśl decyzję",
    "coach.starter.review": "Przejrzyj postępy",
    "app.title.account": "Disciplio · Konto",
    "app.title.welcome": "Disciplio · Dostęp i onboarding",
    "app.title.login": "Disciplio · Logowanie",
    "app.title.ragAdmin": "Disciplio · Admin RAG",
    "app.title.aiUsageAdmin": "Disciplio · Użycie AI",
    "app.skipToMain": "Przejdź do treści głównej",
    "welcome.logInShort": "Zaloguj się",
    "welcome.logInAria": "Masz już konto? Zaloguj się",
    "welcome.kicker": "Dostęp + onboarding",
    "welcome.heroTitle": "Skonfiguruj swój profil Disciplio",
    "welcome.heroLead":
      "Dodaj dane konta i kontekst fokusu, których Disciplio potrzebuje do planowania i feedbacku. Ten profil możesz później dopracować",
    "welcome.liveMap": "Mapa profilu na żywo",
    "welcome.futureProfile": "Twój przyszły profil Disciplio",
    "welcome.identityFallback": "Tożsamość i kierunek pojawią się tutaj",
    "welcome.rolesLabel": "Role",
    "welcome.rolesEmpty":
      "Dodaj role rozdzielone przecinkami, aby zmapować kontekst",
    "welcome.globalGoals": "Cele globalne",
    "welcome.noGoalsYet": "Brak celów",
    "welcome.focusInputs": "Źródła fokusu",
    "welcome.focusInputsEmpty":
      "Zainteresowania i pola problemowe pojawią się tutaj",
    "welcome.recoveryLifestyle": "Regeneracja + styl życia",
    "welcome.recoveryEmpty": "Hobby i potrzeby wsparcia pojawią się tutaj",
    "welcome.profileLabel": "Profil",
    "welcome.startLabel": "Start tutaj",
    "welcome.updateTitle": "Zaktualizuj profil",
    "welcome.createTitle": "Utwórz profil",
    "welcome.fullNameLabel": "Imię i nazwisko",
    "welcome.fullNamePlaceholder": "np. Alex Morgan",
    "welcome.roleLabel": "Role / etap życia",
    "welcome.rolePlaceholder": "np. student, założyciel, rodzic",
    "welcome.emailLabel": "Email",
    "welcome.emailPlaceholder": "ty@example.com",
    "welcome.emailFormatHint": "Wpisz adres email w formacie nazwa@example.com",
    "welcome.passwordLabel": "Hasło",
    "welcome.passwordForAccessLabel": "Hasło dostępu",
    "welcome.passwordPlaceholder": "Co najmniej 8 znaków",
    "welcome.goalsPlaceholder":
      "np. skończyć studia, rozwinąć firmę, poprawić zdrowie",
    "welcome.goalsHelp": "Tylko Ty widzisz swoje cele",
    "welcome.tagInputHint":
      "Naciśnij Enter lub wpisz przecinek, aby dodać element.",
    "welcome.suggested": "Sugestie",
    "welcome.removeTag": ({ tag }) => `Usuń ${tag}`,
    "welcome.interestsLabel": "Zainteresowania",
    "welcome.interestsPlaceholder": "np. AI, fitness, psychologia",
    "welcome.interestsHelp":
      "Tematy rozdzielone przecinkiem lub nową linią, które naturalnie przyciągają uwagę",
    "welcome.problemsLabel": "Pola problemowe",
    "welcome.problemsPlaceholder":
      "np. prokrastynacja, rozproszenie, brak regularności",
    "welcome.problemsHelp":
      "Miejsca, w których system najczęściej się rozpada. Pole opcjonalne — jeśli zostawisz je puste, Disciplio pomoże wychwycić te miejsca w miarę korzystania z aplikacji",
    "welcome.hobbiesLabel": "Hobby",
    "welcome.hobbiesPlaceholder": "np. bieganie, czytanie, fotografia",
    "welcome.hobbiesHelp":
      "Ważne dla równowagi, regeneracji i tożsamości poza samym wynikiem",
    "welcome.needsLabel": "W czym system ma pomóc?",
    "welcome.needsPlaceholder":
      "np. planować dni, chronić fokus, wracać po potknięciach",
    "welcome.needsHelp":
      "Wejścia, pod które system operacyjny powinien się optymalizować",
    "welcome.commitmentLabel": "Tygodniowe zaangażowanie",
    "welcome.commitmentPlaceholder":
      "np. 8 godzin fokusu, 3 treningi, 2 sesje nauki",
    "welcome.commitmentHelp":
      "Disciplio używa tego jako tygodniowego limitu pojemności, żeby ograniczać przeplanowanie i utrzymać realny plan",
    "welcome.heardAboutUsLabel": "Skąd o nas wiesz? (opcjonalnie)",
    "welcome.heardAboutUsPlaceholder":
      "np. od znajomego, z mediów społecznościowych, z wyszukiwarki",
    "welcome.errorName":
      "Dodaj imię i nazwisko, aby Disciplio mogło personalizować profil",
    "welcome.errorEmail":
      "Dodaj email, aby utworzyć lub kontynuować dostęp do Disciplio",
    "welcome.errorEmailFormat":
      "Wpisz poprawny adres email, na przykład nazwa@example.com",
    "welcome.errorExistingAccount":
      "Konto z tym adresem email już istnieje w Disciplio. Zaloguj się.",
    "welcome.errorPasswordTooShort": ({ count }) =>
      `Użyj hasła zawierającego co najmniej ${count} znaków`,
    "welcome.errorPasswordTooLong": ({ count }) =>
      `Użyj hasła zawierającego najwyżej ${count} znaków`,
    "welcome.errorPasswordReused":
      "Wybierz hasło, którego wcześniej nie użyto na tym koncie.",
    "welcome.errorRegistration":
      "Nie udało się utworzyć konta. Sprawdź dane i spróbuj ponownie.",
    "welcome.errorContext":
      "Dodaj co najmniej jedno zainteresowanie i jeden cel globalny, żeby system miał realny kontekst",
    "welcome.errorSave": "Nie udało się zapisać profilu. Spróbuj ponownie.",
    "welcome.errorTerms":
      "Zaakceptuj Regulamin i Politykę prywatności, aby założyć konto",
    "welcome.saveAndReturn": "Zapisz profil i wróć",
    "welcome.enterDisciplio": "Wejdź do Disciplio",
    "welcome.termsAgreementPrefix": "Akceptuję",
    "welcome.termsLabel": "Warunki",
    "welcome.privacyAgreementPrefix": "Potwierdzam zapoznanie się z",
    "welcome.privacyLabel": "informacją o prywatności",
    "plan.toggleAria": ({ title, done }) =>
      `${title}, ${done ? "ukończone" : "nieukończone"}`,

    "lang.en": "English",
    "lang.pl": "Polski",
    "lang.uk": "Українська",
    "lang.enShort": "EN",
    "lang.plShort": "PL",
    "lang.ukShort": "UA",

    "login.label": "Dostęp",
    "login.title": "Zaloguj się",
    "login.heroTitle": "Wróć do swojego systemu dyscypliny",
    "login.heroLead":
      "Otwórz ten sam profil, język, plany i cele bez budowania konfiguracji od nowa",
    "login.proofLabel": "Korzyści logowania",
    "login.localOnly": "To samo konto",
    "login.localOnlyText":
      "Zaloguj się tym samym adresem email, aby otworzyć profil ponownie",
    "login.profileReady": "Profil gotowy",
    "login.profileReadyText":
      "Plany i ustawienia ładują się od razu po zalogowaniu",
    "login.fastReturn": "Szybki powrót",
    "login.fastReturnText": "Wróć dokładnie tam, gdzie skończyłeś",
    "login.password": "Hasło",
    "login.showPassword": "Pokaż hasło",
    "login.hidePassword": "Ukryj hasło",
    "login.submit": "Zaloguj",
    "login.create": "Utwórz profil",
    "login.noAccount":
      "Na tym urządzeniu nie ma jeszcze zapisanego konta. Najpierw utwórz profil",
    "login.invalid": "Email lub hasło są niepoprawne",
    "auth.rateLimited": ({ seconds }) =>
      `Zbyt wiele prób — spróbuj ponownie za ${seconds}s`,
    "auth.serverUnavailable":
      "Serwer nie odpowiedział. Już nad tym pracujemy — spróbuj ponownie za kilka minut.",
    "login.attemptPolicy":
      "Po 5 nieudanych próbach logowanie zostanie wstrzymane na 15 minut.",
    "login.forgotPassword": "Nie pamiętasz hasła?",
    "login.throttled":
      "Zbyt wiele prób logowania. Spróbuj ponownie za około 15 minut.",
    "resetPassword.label": "Bezpieczne odzyskiwanie dostępu",
    "resetPassword.title": "Ustaw nowe hasło",
    "resetPassword.heroTitle": "Odzyskaj dostęp bezpiecznie",
    "resetPassword.heroLead":
      "Ten link działa tylko raz i wkrótce wygasa. Nowe hasło wyloguje wszystkie aktywne sesje.",
    "resetPassword.newPassword": "Nowe hasło",
    "resetPassword.confirmPassword": "Potwierdź nowe hasło",
    "resetPassword.tooShort": ({ count }) =>
      `Użyj hasła zawierającego co najmniej ${count} znaków`,
    "resetPassword.tooLong": ({ count }) =>
      `Użyj hasła zawierającego najwyżej ${count} znaków`,
    "resetPassword.reused":
      "Wybierz hasło, którego wcześniej nie użyto na tym koncie.",
    "resetPassword.match": "Nowe hasła nie są takie same",
    "resetPassword.invalid":
      "Ten link jest nieprawidłowy, wygasł lub został już użyty.",
    "resetPassword.invalidTitle": "Link jest nieprawidłowy lub go brakuje",
    "resetPassword.requestNew": "Poproś o nowy link",
    "resetPassword.submit": "Zapisz nowe hasło",
    "resetPassword.saving": "Zapisywanie…",
    "resetPassword.back": "Wróć do logowania",
    "resetPassword.successTitle": "Hasło zostało zmienione",
    "resetPassword.successLead":
      "Pozostałe sesje zostały wylogowane. Zaloguj się nowym hasłem.",
    "resetPassword.login": "Przejdź do logowania",

    "verifyEmail.label": "Potwierdź swój adres e-mail",
    "verifyEmail.heroTitle": "Jeszcze tylko chwila",
    "verifyEmail.heroLead":
      "Wpisz sześciocyfrowy kod, który wysłaliśmy, aby dokończyć zakładanie konta.",
    "verifyEmail.title": "Wpisz kod potwierdzający",
    "verifyEmail.sentTo": ({ email }) =>
      `Wysłaliśmy kod na adres ${email}. Sprawdź folder Spam i Oferty, jeśli go nie widzisz.`,
    "verifyEmail.codeLabel": "Kod potwierdzający",
    "verifyEmail.submit": "Potwierdź",
    "verifyEmail.verifying": "Potwierdzanie…",
    "verifyEmail.resend": "Wyślij kod ponownie",
    "verifyEmail.resendCooldown": ({ seconds }) =>
      `Ponowne wysłanie za ${seconds}s`,
    "verifyEmail.hint":
      "Działa tylko najnowszy kod. Kody wygasają po krótkim czasie.",
    "verifyEmail.invalidCode":
      "Ten kod jest nieprawidłowy lub wygasł. Poproś o nowy.",
    "verifyEmail.missingEmail":
      "Nie znaleźliśmy oczekującej rejestracji dla tej sesji. Zaloguj się lub zarejestruj ponownie.",
    "verifyEmail.successTitle": "Adres e-mail potwierdzony",
    "verifyEmail.successLead": "Twoje konto jest teraz aktywne.",

    "app.title.register": "Disciplio · Rejestracja",
    "register.label": "Utwórz konto",
    "register.heroTitle": "Załóż konto Disciplio",
    "register.heroLead":
      "Utwórz konto przez Google lub e-mail. Profil skonfigurujesz zaraz potem.",
    "register.title": "Rejestracja",
    "register.divider": "lub kontynuuj przez Google",
    "register.submit": "Utwórz konto",
    "register.logInLink": "Zaloguj się",

    "welcome.accountLabel": "Konto",
    "welcome.logOutShort": "Wyloguj się",
    "welcome.logOutAria": "Wyloguj się i wróć do logowania",

    "google.continueWithGoogle": "Kontynuuj przez Google",
    "google.loading": "Ładowanie logowania Google…",
    "google.unavailable": "Logowanie przez Google jest niedostępne",
    "google.signInFailed": "Logowanie przez Google nie powiodło się",
    "google.accountNotFound":
      "Żadne konto Disciplio nie jest połączone z tym kontem Google. Utwórz konto lub zaloguj się hasłem.",
    "google.linkRequired":
      "Konto z tym adresem e-mail już istnieje; zaloguj się hasłem i połącz Google w ustawieniach konta",
    "google.connected": "Konto Google połączone",
    "google.disconnected": "Konto Google odłączone",
    "google.onlyMethod": "Nie można odłączyć jedynego sposobu logowania",
    "google.emailMismatch":
      "Adres e-mail konta Google musi być zgodny z adresem e-mail konta Disciplio",
    "google.identityOwnedByAnotherUser":
      "To konto Google jest już połączone z innym kontem Disciplio",

    "account.authMethodsTitle": "Sposoby logowania",
    "account.authMethodsHelp": "Jak możesz zalogować się na to konto",
    "account.authMethodEmail": "E-mail i hasło",
    "account.authMethodGoogle": "Google",
    "account.authMethodGoogleNotConnected": "Niepołączone",
    "account.passwordSet": "Hasło ustawione",
    "account.noPasswordSet": "Brak ustawionego hasła",
    "account.setPasswordFirst": "Najpierw ustaw hasło",
    "account.setPasswordLead":
      "Dodaj hasło, aby móc zalogować się bez Google i zarządzać bezpieczeństwem konta.",
    "account.setPasswordNew": "Nowe hasło",
    "account.setPasswordConfirm": "Potwierdź nowe hasło",
    "account.setPasswordSubmit": "Zapisz hasło",
    "account.setPasswordSaving": "Zapisywanie…",
    "account.setPasswordCancel": "Anuluj",
    "account.setPasswordSuccess": "Hasło ustawione",
    "account.setPasswordFailed":
      "Nie udało się ustawić hasła. Spróbuj ponownie.",
    "account.disconnectGoogle": "Odłącz Google",
    "account.deleteRequiresPassword":
      "Ustaw hasło przed usunięciem konta, abyśmy mogli potwierdzić, że to naprawdę Ty.",

    "forgotPassword.label": "Odzyskiwanie hasła",
    "forgotPassword.heroTitle": "Zresetuj hasło",
    "forgotPassword.heroLead":
      "Podaj adres e-mail konta, a jeśli istnieje, wyślemy jednorazowy link do resetu.",
    "forgotPassword.formTitle": "Nie pamiętasz hasła?",
    "forgotPassword.emailLabel": "E-mail",
    "forgotPassword.emailPlaceholder": "imie@example.com",
    "forgotPassword.invalidEmail": "Podaj prawidłowy adres e-mail",
    "forgotPassword.submit": "Wyślij link resetujący",
    "forgotPassword.submitting": "Wysyłanie…",
    "forgotPassword.back": "Wróć do logowania",
    "forgotPassword.successTitle": "Sprawdź swoją skrzynkę e-mail",
    "forgotPassword.successLead":
      "Jeśli konto o tym adresie istnieje, wysłaliśmy link do resetu hasła.",
    "forgotPassword.login": "Przejdź do logowania",

    "account.label": "Konto",
    "account.navLabel": "Menu konta",
    "account.navProfile": "Profil",
    "account.navProfileHint": "Tożsamość i kierunek",
    "account.navSettings": "Ustawienia",
    "account.navSettingsHint": "Zasady działania dnia",
    "account.navAi": "AI",
    "account.navAiHint": "Zgoda i użycie",
    "account.navPrivacy": "Prywatność i konto",
    "account.navPrivacyHint": "Prawo i dostęp",
    "account.infoLabel": ({ label }) => `Więcej informacji o: ${label}`,
    "account.backToMenu": "Menu konta",
    "account.aiTitle": "Rekomendacje AI",
    "account.aiDescription":
      "Rekomendacje mogą być niedokładne i nie są poradą zawodową.",
    "account.aiEnabled": "Włączone",
    "account.aiDeclined": "Odrzucone",
    "account.aiUndecided": "Nie zdecydowano",
    "account.aiEnable": "Włącz AI",
    "account.aiDecline": "Wyłącz AI",
    "account.aiEnabledHelp": "Korzystaj z opcjonalnych rekomendacji AI.",
    "account.aiDeclinedHelp": "Pozostaw rekomendacje AI wyłączone.",
    "account.aiEnabledStatus": "Rekomendacje AI są włączone.",
    "account.aiDisabledStatus":
      "Rekomendacje AI są wyłączone. Funkcje inne niż AI nadal działają.",
    "account.aiUpdateFailed": "Nie udało się zaktualizować ustawienia AI.",
    "account.memoryTitle": "Pamięć",
    "account.memoryDescription": "Przejrzyj informacje, których AI Disciplio może używać, aby lepiej dopasować odpowiedzi i rekomendacje.",
    "account.memoryPrivacy": "Pamięć należy do Twojego konta. Może kształtować kontekst AI, ale nie zmienia celów, zadań ani refleksji.",
    "account.memoryFilterLabel": "Widoczność pamięci",
    "account.memoryActive": "Aktywne",
    "account.memorySignals": "Sygnały",
    "account.memorySignalsDescription": "To obserwacje z Twoich planów, a nie osobiste fakty. Dio może używać ich, aby pomóc Ci zdecydować, co zrobić dalej.",
    "account.memoryAll": "Wszystkie",
    "account.memoryAdd": "Dodaj pamięć",
    "account.memoryAddTitle": "Dodaj pamięć",
    "account.memoryType": "Typ",
    "account.memorySummary": "Co Disciplio ma zapamiętać?",
    "account.memorySummaryPlaceholder": "np. Najlepiej koncentruję się przed południem",
    "account.memorySave": "Zapisz pamięć",
    "account.memorySaving": "Zapisywanie…",
    "account.memoryCancel": "Anuluj",
    "account.memoryEdit": "Edytuj",
    "account.memorySaveEdit": "Zapisz",
    "account.memoryDelete": "Usuń",
    "account.memoryRemoving": "Usuwanie…",
    "account.memoryRemoveConfirm": "Usunąć tę pamięć?",
    "account.memoryReset": "Wyczyść całą pamięć",
    "account.memoryResetConfirm": "Wyczyścić całą zapisaną pamięć? Cele, zadania i refleksje pozostaną.",
    "account.memoryResetConfirmAction": "Wyczyść pamięć",
    "account.memoryResetCancel": "Zachowaj pamięć",
    "account.memoryResetting": "Czyszczenie…",
    "account.memoryEmpty": "Brak aktywnej pamięci.",
    "account.memoryEmptySignals": "Brak aktywnych sygnałów postępu.",
    "account.memoryEmptyAll": "Brak zapisanej pamięci.",
    "account.memoryLoading": "Ładowanie pamięci…",
    "account.memoryError": "Nie udało się załadować pamięci. Spróbuj ponownie.",
    "account.memoryActionError": "Nie udało się zmienić pamięci. Spróbuj ponownie.",
    "account.memoryYouAdded": "Dodane przez Ciebie",
    "account.memoryFromCoach": "Powiedziałeś to Dio",
    "account.memorySignal": "Sygnał postępu",
    "account.memoryLearned": "Wyuczone",
    "account.memoryRemoved": "Usunięte",
    "account.memoryStatusResolved": "Rozwiązane",
    "account.memoryStatusStale": "Wygasłe",
    "account.memoryStatusDismissed": "Odrzucone",
    "account.memoryStatusSuperseded": "Zastąpione",
    "account.memoryNeedsReview": "Wymaga sprawdzenia",
    "account.memoryTemporary": "Tymczasowe",
    "account.memoryUntil": ({ date }) => `Do ${date}`,
    "account.memoryReviewAfter": ({ date }) => `Sprawdź ponownie ${date}`,
    "account.memoryImport": "Importuj z AI",
    "account.memoryFromChatGPT": "Zaimportowane z ChatGPT",
    "account.memoryFromClaude": "Zaimportowane z Claude",
    "account.memoryFromGemini": "Zaimportowane z Gemini",
    "account.memoryFromOtherAI": "Zaimportowane z innego AI",
    "account.memoryEvidence": ({ count }) => count === 1 ? "1 sygnał" : `${count} sygnałów`,
    "account.memoryLastObserved": ({ date }) => `Zaktualizowano ${date}`,
    "account.memoryType.FACT": "Fakt",
    "account.memoryType.PREFERENCE": "Preferencja",
    "account.memoryType.CONSTRAINT": "Ograniczenie",
    "account.memoryType.DECISION": "Decyzja",
    "account.memoryType.BEHAVIOR_PATTERN": "Wzorzec zachowania",
    "account.memoryType.PROGRESS_PATTERN": "Wzorzec postępu",
    "account.memoryType.RECOMMENDATION_OUTCOME": "Wynik rekomendacji",
    "account.memoryType.RULE": "Reguła",
    "account.memoryType.EXPERIMENT": "Eksperyment",
    "account.memoryType.OPEN_LOOP": "Otwarta sprawa",
    "memoryImport.eyebrow": "IMPORT PAMIĘCI",
    "memoryImport.title": "Przenieś swój kontekst do Dio",
    "memoryImport.subtitle": "Wybierz asystenta AI, poproś go o kontekst, który chcesz zachować, a następnie przejrzyj wszystko przed dodaniem do pamięci.",
    "memoryImport.stepsLabel": "Etapy importu pamięci",
    "memoryImport.step.choose": "Wybierz asystenta",
    "memoryImport.step.paste": "Wklej odpowiedź",
    "memoryImport.step.review": "Przejrzyj i zastosuj",
    "memoryImport.chooseLead": "Który asystent ma kontekst, który chcesz przenieść?",
    "memoryImport.assistant.chatgpt": "ChatGPT",
    "memoryImport.assistant.chatgptHelp": "Otwórz przygotowaną prośbę lub skopiuj ją samodzielnie.",
    "memoryImport.assistant.claude": "Claude",
    "memoryImport.assistant.claudeHelp": "Otwórz Claude z automatycznie skopiowaną prośbą.",
    "memoryImport.assistant.gemini": "Gemini",
    "memoryImport.assistant.geminiHelp": "Otwórz Gemini z automatycznie skopiowaną prośbą.",
    "memoryImport.assistant.other": "Inne AI",
    "memoryImport.assistant.otherHelp": "Skopiuj prośbę do dowolnego asystenta.",
    "memoryImport.securityNote": "Uwzględniony zostanie tylko kontekst, o którego zwrot poprosisz asystenta. Nigdy nie podawaj haseł, kluczy, tokenów ani danych płatniczych.",
    "memoryImport.selectedAssistant": ({ assistant }) => `Wybrano: ${assistant}`,
    "memoryImport.loadingPrompt": "Przygotowywanie prośby…",
    "memoryImport.open.chatgpt": "Otwórz w ChatGPT",
    "memoryImport.open.claude": "Skopiuj i otwórz Claude",
    "memoryImport.open.gemini": "Skopiuj i otwórz Gemini",
    "memoryImport.copyPrompt": "Kopiuj prośbę",
    "memoryImport.pasteAnswer": "Mam odpowiedź",
    "memoryImport.promptError": "Nie udało się przygotować prośby. Spróbuj ponownie.",
    "memoryImport.copyError": "Nie udało się skopiować prośby. Spróbuj ponownie lub skopiuj ją ręcznie.",
    "memoryImport.externalOpenError": "Nie udało się otworzyć asystenta. Prośba jest nadal gotowa do skopiowania.",
    "memoryImport.copied": "Prośba została skopiowana do schowka.",
    "memoryImport.copiedGemini": "Prośba została skopiowana — wklej ją do Gemini.",
    "memoryImport.bringAnswerBack": "Przenieś odpowiedź z powrotem",
    "memoryImport.pasteLead": ({ assistant }) => `Wklej poniżej odpowiedź JSON z ${assistant}. Nic nie zostanie zapisane, dopóki nie przejrzysz i nie zastosujesz zmian.`,
    "memoryImport.pastePlaceholder.chatgpt": "Wklej tutaj odpowiedź ChatGPT…",
    "memoryImport.pastePlaceholder.claude": "Wklej tutaj odpowiedź Claude…",
    "memoryImport.pastePlaceholder.gemini": "Wklej tutaj odpowiedź Gemini…",
    "memoryImport.pastePlaceholder.other": "Wklej tutaj odpowiedź używanego asystenta AI…",
    "memoryImport.pasteLabel": "Odpowiedź JSON asystenta",
    "memoryImport.pastePrivacy": "Ta odpowiedź pozostaje tylko w otwartej sesji, dopóki nie wybierzesz Zastosuj. Nie trafia do adresu URL, analityki ani logów.",
    "memoryImport.reviewAnswer": "Przejrzyj pamięci",
    "memoryImport.back": "Wstecz",
    "memoryImport.reviewTitle": "Przejrzyj przed zapisaniem",
    "memoryImport.reviewCounts": ({ newCount, duplicateCount, conflictCount }) => `${newCount} nowych · ${duplicateCount} duplikatów · ${conflictCount} konfliktów`,
    "memoryImport.reviewPrivacy": "Wybierz, co Dio ma zachować. Nowa pamięć powstanie dopiero po wybraniu Zastosuj.",
    "memoryImport.groupNew": "Nowa pamięć",
    "memoryImport.groupConflicts": "Aktualizacje i konflikty",
    "memoryImport.groupReview": "Wymaga przejrzenia",
    "memoryImport.groupDuplicates": "Już w pamięci",
    "memoryImport.groupSkipped": "Bezpiecznie pominięte",
    "memoryImport.decisionLabel": "Decyzja",
    "memoryImport.memoryTypeLabel": "Typ pamięci",
    "memoryImport.summaryLabel": "Tekst",
    "memoryImport.editValidation": "Każda pamięć musi mieć typ i tekst o długości do 500 znaków, zanim będzie można ją zastosować.",
    "memoryImport.decision.import": "Importuj",
    "memoryImport.decision.skip": "Pomiń",
    "memoryImport.decision.replace": "Użyj zaimportowanej",
    "memoryImport.decision.keep_both": "Zachowaj obie",
    "memoryImport.decision.keep_existing": "Zachowaj obecną",
    "memoryImport.disposition.new": "Nowa",
    "memoryImport.disposition.duplicate": "Duplikat",
    "memoryImport.disposition.conflict": "Konflikt",
    "memoryImport.disposition.review_required": "Wymaga przejrzenia",
    "memoryImport.disposition.rejected": "Nie zaimportowano",
    "memoryImport.existingMemory": "Obecna pamięć",
    "memoryImport.reason.previously_imported": "Ta informacja została już zaimportowana. Zarządzaj jej bieżącym stanem w ustawieniach pamięci.",
    "memoryImport.reason.exact_duplicate": "To dokładnie pasuje do aktywnej pamięci.",
    "memoryImport.reason.explicit_replacement": "Asystent oznaczył tę pozycję jako zastępującą obecną pamięć.",
    "memoryImport.reason.replacement_target_not_found": "Nie udało się bezpiecznie dopasować wskazanego zastępstwa.",
    "memoryImport.reason.no_match": "Żadna obecna pamięć nie pasuje do tej pozycji.",
    "memoryImport.reason.comparison_pending": "Bezpieczne porównanie nie jest jeszcze dostępne.",
    "memoryImport.reason.semantic_duplicate": "Ta pozycja prawdopodobnie oznacza to samo co obecna pamięć.",
    "memoryImport.reason.semantic_conflict": "Ta pozycja prawdopodobnie koliduje z obecną pamięcią.",
    "memoryImport.reason.semantic_independent": "Ta pozycja wygląda na niezależny kontekst.",
    "memoryImport.reason.comparison_uncertain": "Porównanie nie dało pewnego wyniku, więc wybierz ręcznie.",
    "memoryImport.reason.invalid_item": "Ta pozycja nie była poprawnym JSON-em.",
    "memoryImport.reason.unsupported_type": "Ten typ pamięci nie jest obsługiwany podczas importu.",
    "memoryImport.reason.blank_summary": "Ta pozycja nie zawierała użytecznego podsumowania.",
    "memoryImport.reason.summary_too_long": "To podsumowanie jest zbyt długie, aby je zaimportować.",
    "memoryImport.reason.unsafe_content": "Pominięto tę pozycję, ponieważ może zawierać sekret lub dane uwierzytelniające.",
    "memoryImport.reason.invalid_replaces": "Pole zastępstwa było niepoprawne.",
    "memoryImport.reason.replaces_too_long": "Pole zastępstwa jest zbyt długie.",
    "memoryImport.reason.duplicate_candidate": "Ta pozycja powielała inną importowaną pozycję i została pominięta.",
    "memoryImport.applying": "Zastosowywanie…",
    "memoryImport.apply": ({ count }) => `Importuj ${count} ${count === 1 ? "pamięć" : "pamięci"}`,
    "memoryImport.backToPaste": "Wróć do odpowiedzi",
    "memoryImport.applyError": "Nie udało się zastosować importu. Nic nie zmieniono; przejrzyj odpowiedź i spróbuj ponownie.",
    "memoryImport.previewError": "Nie udało się przejrzeć tej odpowiedzi. Sprawdź, czy to JSON zwrócony przez asystenta, i spróbuj ponownie.",
    "memoryImport.successTitle": "Pamięć zaimportowana",
    "memoryImport.successSummary": ({ assistant }) => `Przejrzany kontekst z ${assistant} jest teraz częścią edytowalnej pamięci Disciplio.`,
    "memoryImport.successImported": "nowe",
    "memoryImport.successReplaced": "zaktualizowane",
    "memoryImport.successKeptBoth": "zachowane obie",
    "memoryImport.successSkipped": "pominięte",
    "memoryImport.done": "Gotowe",
    "memoryImport.close": "Zamknij import pamięci",
    "memoryImport.goToEnd": "Przejdź na koniec",
    "account.legalTitle": "Prawo i prywatność",
    "account.legalDates": ({ terms, privacy }) =>
      `Zaakceptowane warunki: ${terms} · Prywatność: ${privacy}`,
    "account.legalNotRecorded": "Brak danych",
    "account.terms": "Warunki",
    "account.privacy": "Prywatność",
    "account.cookies": "Cookies",
    "account.contact": "Kontakt",
    "account.support": "Pomoc",
    "account.deleteTitle": "Usuń konto",
    "account.deleteDescription":
      "Ta operacja jest natychmiastowa i trwała. Plany, refleksje, rekomendacje, ustawienia, tokeny i zgłoszenia pomocy zostaną usunięte.",
    "account.deleteCurrentPassword": "Obecne hasło",
    "account.deleteConfirmation": "Wpisz DELETE",
    "account.deleteButton": "Usuń konto na stałe",
    "account.deleteConfirmError":
      "Wpisz DELETE, aby potwierdzić usunięcie konta.",
    "account.deleteFailed":
      "Nie udało się usunąć konta. Sprawdź hasło i słowo potwierdzające.",
    "account.fallbackTitle": "Twoje konto Disciplio",
    "account.lead": "Osobisty kontekst i ustawienia Disciplio",
    "account.roles": "Role",
    "account.globalGoals": "Cele globalne",
    "account.yearGoals": "Cele roczne",
    "account.focusInputs": "Źródła fokusu",
    "account.workspaceReady": "Przestrzeń gotowa",
    "account.personalInfo": "Informacje osobiste",
    "account.profileContext": "Kontekst profilu",
    "account.identitySection": "Tożsamość",
    "account.directionSection": "Kierunek długoterminowy",
    "account.directionSectionHelp":
      "Oddzielaj cele przecinkami albo nowymi liniami",
    "account.focusSection": "Mapa fokusu",
    "account.focusSectionHelp": "Dane, których system używa do rekomendacji",
    "account.saveProfile": "Zapisz profil",
    "account.profileSaved": "Profil zapisany",
    "account.autoSaved": "Zapisano automatycznie",
    "account.autoSaveReady": "Autozapis po wyjściu z pola",
    "account.unsavedChanges": "Zmieniono",
    "account.passwordSaved": "Hasło zmienione",
    "account.passwordCurrentError": "Obecne hasło jest niepoprawne",
    "account.passwordLengthError": "Nowe hasło musi mieć co najmniej 8 znaków",
    "account.passwordMatchError": "Nowe hasła nie są takie same",
    "account.fullName": "Imię i nazwisko",
    "account.fullNameHelp": "Nazwa widoczna w całej przestrzeni Disciplio",
    "account.fullNamePlaceholder": "Nazwa widoczna w przestrzeni",
    "account.email": "Email",
    "account.emailHelp": "Adres email powiązany z tym kontem",
    "account.emailPlaceholder": "ty@example.com",
    "account.rolesSeason": "Role / etap życia",
    "account.rolesPlaceholder": "Founder, student, operator, atleta",
    "account.rolesHelp": "Role oddzielaj przecinkami",
    "account.globalGoalsHelp":
      "Długoterminowe rezultaty nadające kierunek planowaniu. Oddzielaj cele przecinkami albo nowymi liniami",
    "account.globalGoalsPlaceholder":
      "Zbudować zdyscyplinowane studio, skończyć pracę, poprawić zdrowie",
    "account.yearGoalsPlaceholder":
      "Wypuścić wersję v1, opublikować 24 eseje, przebiec półmaraton",
    "account.interests": "Zainteresowania",
    "account.interestsHelp":
      "Tematy i aktywności, które pomagają Disciplio zrozumieć Twój fokus",
    "account.interestsPlaceholder":
      "AI, pisanie, wytrzymałość, psychologia, design",
    "account.problemFields": "Obszary problemowe",
    "account.problemFieldsHelp":
      "Wyzwania, które mogą utrudniać regularne działanie",
    "account.problemFieldsPlaceholder":
      "Konsekwencja, przeplanowanie, rozproszenie, deficyt snu",
    "account.hobbies": "Hobby",
    "account.hobbiesHelp":
      "Aktywności i zainteresowania wspierające regenerację i równowagę",
    "account.hobbiesPlaceholder":
      "Wspinaczka, czytanie, spacery, szachy, gotowanie",
    "account.systemNeeds": "W czym system ma pomagać?",
    "account.systemNeedsHelp":
      "Rodzaj struktury, ochrony fokusu lub wsparcia, którego oczekujesz od Disciplio",
    "account.systemNeedsPlaceholder":
      "Poranna struktura, ochrona fokusu, planowanie regeneracji",
    "account.weeklyCommitment": "Tygodniowe zobowiązanie",
    "account.weeklyCommitmentHelp":
      "Realistyczna tygodniowa ilość czasu na skupioną pracę, trening lub inne zobowiązania",
    "account.weeklyCommitmentPlaceholder":
      "10 godzin fokusu, 4 treningi, 2 bloki pisania",
    "account.settingsLabel": "Ustawienia Disciplio",
    "account.systemPreferences": "Preferencje systemu",
    "account.saveSettings": "Zapisz ustawienia",
    "account.settingsSaved": "Ustawienia zapisane",
    "account.securityLabel": "Bezpieczeństwo",
    "account.passwordTitle": "Zmień hasło",
    "account.passwordLead":
      "Zaktualizuj lokalne hasło używane do dostępu do Disciplio na tym urządzeniu",
    "account.currentPassword": "Obecne hasło",
    "account.currentPasswordHelp":
      "Wymagane tylko wtedy, gdy hasło już istnieje",
    "account.newPassword": "Nowe hasło",
    "account.confirmPassword": "Potwierdź nowe hasło",
    "account.passwordPlaceholder": "Minimum 8 znaków",
    "account.savePassword": "Zapisz hasło",
    "account.interfaceLanguage": "Język interfejsu",
    "account.interfaceLanguageHelp": "Ustawia język używany w całym Disciplio",
    "account.dayStartTime": "Start dnia",
    "account.dayStartTimeHelp":
      "Przed tą godziną Dzisiaj nadal oznacza poprzedni dzień Disciplio",
    "account.dailyNoteUnlock": "Odblokowanie notatki dnia",
    "account.dailyNoteUnlockHelp":
      "Godzina, od której można edytować refleksję",
    "account.autoSuggestFocus": "Automatycznie sugeruj działania do planu",
    "account.autoSuggestFocusHelp":
      "Pokazuj sugestie AI automatycznie, gdy w celach są luki",
    "account.showCompletedDefault": "Pokazuj ukończone działania domyślnie",
    "account.showCompletedDefaultHelp":
      "Pozostaw ukończone działania widoczne po otwarciu planu",
    "account.dailyReminders": "Codzienne przypomnienia",
    "account.dailyRemindersHelp":
      "Spokojny sygnał czasu dla zaplanowanych działań i nawyków",
    "account.dailyRemindersEnabled": "Przypominaj o działaniach z godziną",
    "account.dailyRemindersEnabledHelp":
      "Pokazuje przypomnienie przy rozpoczęciu oraz, jeśli ustawisz, przed końcem działania",
    "account.dailyReminderLead": "Przed zakończeniem działania",
    "account.dailyReminderLeadHelp":
      "Czas wyprzedzenia działa tylko dla działań z godziną startu i końca",
    "account.reminderOff": "Wyłączone",
    "account.reminderMinutes": ({ minutes }) => `${minutes} min`,
    "account.reminderCustom": "Własny",
    "account.notificationSound": "Odtwarzaj dźwięk przypomnienia",
    "account.notificationSoundHelp":
      "Używa dostarczonego dźwięku, gdy Disciplio jest otwarte; przeglądarka może go wyciszyć",
    "account.browserNotifications": "Powiadomienia przeglądarki",
    "account.browserNotificationStatus.unsupported":
      "Ta przeglądarka nie obsługuje powiadomień systemowych.",
    "account.browserNotificationStatus.default":
      "Włącz powiadomienia systemowe dla przypomnień, gdy Disciplio jest zamknięte lub w tle.",
    "account.browserNotificationStatus.granted":
      "Uprawnienie przyznane. Dokończ konfigurację, aby utworzyć subskrypcję push dla tej przeglądarki.",
    "account.browserNotificationStatus.denied":
      "Zablokowane w ustawieniach przeglądarki. Włącz je tam, aby otrzymywać powiadomienia.",
    "account.enableBrowserNotifications": "Włącz powiadomienia przeglądarki",
    "account.finishBrowserNotifications": "Dokończ konfigurację powiadomień",
    "account.sendTestBrowserNotification": "Wyślij powiadomienie testowe",
    "account.browserNotificationsEnabled":
      "Powiadomienia przeglądarki włączone",
    "account.browserNotificationsNotEnabled":
      "Powiadomienia przeglądarki nie zostały włączone",
    "account.browserNotificationsSubscribed":
      "Włączone w tej przeglądarce i na tym urządzeniu, nawet gdy Disciplio jest zamknięte.",
    "account.browserNotificationsUnavailable":
      "Powiadomienia push nie są jeszcze skonfigurowane dla tego środowiska.",
    "account.browserNotificationsPermissionOnly":
      "Zezwolono w tej przeglądarce. Przypomnienia mogą się pojawiać, gdy karta Disciplio jest otwarta; push przy zamkniętej karcie nie jest tu jeszcze skonfigurowany.",
    "account.browserNotificationsSubscriptionMissing":
      "Uprawnienie jest przyznane, ale ta przeglądarka nadal potrzebuje subskrypcji push. Dokończ konfigurację, aby włączyć przypomnienia przy zamkniętej karcie.",
    "account.browserNotificationsTestBody":
      "Powiadomienia są gotowe. Tak będą wyglądać przypomnienia o zaplanowanych działaniach.",
    "account.browserNotificationsTestSent": ({ count }) =>
      count === 1
        ? "Wysłano powiadomienie testowe."
        : `Wysłano ${count} powiadomienia testowe.`,
    "account.browserNotificationsTestNone":
      "Nie znaleziono aktywnych subskrypcji push dla tego konta.",
    "account.browserNotificationsTestFailed":
      "Nie udało się dostarczyć powiadomienia testowego.",
    "account.browserNotificationsError.unsupported":
      "Powiadomienia przeglądarki nie są obsługiwane.",
    "account.browserNotificationsError.serviceWorker":
      "Service worker niedostępny",
    "account.browserNotificationsError.invalidConfig":
      "Nieprawidłowa konfiguracja powiadomień",
    "account.browserNotificationsError.subscribeFailed":
      "Nie udało się utworzyć subskrypcji przeglądarki",
    "account.browserNotificationsError.saveFailed":
      "Nie udało się zapisać subskrypcji",
    "account.browserNotificationsInstallRequired":
      "Na iPhonie lub iPadzie najpierw dodaj Disciplio do ekranu początkowego, a następnie włącz powiadomienia w zainstalowanej aplikacji.",
    "account.browserNotificationsDisabled":
      "Powiadomienia przeglądarki wyłączone na tym urządzeniu",
    "account.disableBrowserNotifications": "Wyłącz na tym urządzeniu",
    "account.metricUnits": "Jednostki postępu",
    "account.metricUnitsHelp": ({ count, max }) =>
      `${count}/${max} własnych jednostek. Domyślne zależą od języka interfejsu`,
    "account.defaultMetricUnits": "Jednostki domyślne",
    "account.customMetricUnits": "Jednostki własne",
    "account.noCustomMetricUnits": "Nie ma jeszcze własnych jednostek",
    "account.metricUnitPlaceholder": "Własna jednostka",
    "account.addMetricUnit": "Dodaj jednostkę",
    "account.weeklyGoalCategories": "Kategorie celów tygodniowych",
    "account.weeklyGoalCategoriesHelp": ({ count, max }) =>
      `${count}/${max} własnych kategorii. Domyślne zależą od języka interfejsu`,
    "account.defaultWeeklyGoalCategories": "Kategorie domyślne",
    "account.customWeeklyGoalCategories": "Kategorie własne",
    "account.noCustomWeeklyGoalCategories": "Nie ma jeszcze własnych kategorii",
    "account.weeklyGoalCategoryPlaceholder": "Własna kategoria",
    "account.addWeeklyGoalCategory": "Dodaj kategorię",
    "account.streakFreeDays": "Dni wolne dla serii",
    "account.streakFreeDaysHelp":
      "Dni, które nie przerywają serii i nie zwiększają jej licznika",
    "account.streakFreeWeekdays": "Cotygodniowe dni wolne",
    "account.streakFreeWeekdaysHelp":
      "Wybrane dni tygodnia nie przerywają serii ani nie zwiększają jej licznika",
    "account.streakFreeRanges": "Zakresy dat wolnych",
    "account.streakFreeRangesHelp":
      "Tymczasowe zakresy dat ignorowane przez serię",
    "account.streakFreeRangeStart": "Start",
    "account.streakFreeRangeStartHelp": "Pierwszy dzień zakresu wolnego",
    "account.streakFreeRangeEnd": "Koniec",
    "account.streakFreeRangeEndHelp":
      "Ostatni dzień zakresu. Puste pole oznacza jeden dzień",
    "account.addFreeRange": "Dodaj zakres",
    "account.removeFreeRange": "Usuń zakres",
    "account.noFreeRanges": "Brak zapisanych zakresów wolnych",
    "account.exitTitle": "Wyjdź z konta",
    "account.exitHelp":
      "Opuść przestrzeń i wróć do logowania. Dane lokalne zostają zapisane",
    "account.exitButton": "Wyjdź",
    "account.deleteCurrentPasswordHelp":
      "Potwierdź swoją tożsamość obecnym hasłem do konta",
    "account.deleteConfirmationHelp":
      "Wpisz dokładnie DELETE, aby potwierdzić trwałą operację",

    "today.label": "DZISIAJ",
    "today.streak": "Seria Disciplio",
    "today.day": "dzień",
    "today.days": "dni",
    "today.streakAria": ({ streak }) =>
      `${streak} dni z rzędu; siedem pól pokazuje aktualną serię`,
    "today.streakHint":
      "Aktywna, gdy dziś ukończysz przynajmniej jeden cel lub nawyk",
    "streak.openCalendar": "Otwórz kalendarz serii",
    "streak.calendarTitle": "Kalendarz serii",
    "streak.current": "Aktualna seria",
    "streak.restores": "Przywrócenia w tym miesiącu",
    "streak.resets": ({ date }) => `Reset ${date}`,
    "streak.legend": "Legenda kalendarza serii",
    "streak.completed": "Ukończono",
    "streak.restored": "Przywrócono",
    "streak.free": "Dzień wolny",
    "streak.missed": "Pominięto",
    "streak.calendarNote":
      "Ukończ przynajmniej jedno zadanie lub nawyk w każdym aktywnym dniu, aby wydłużyć serię. Dni wolne zachowują ciąg bez zwiększania go; po rozpoczęciu nowego dnia kwalifikujące się krótkie przerwy mogą zostać przywrócone, maksymalnie dwa pominięte dni w miesiącu.",
    "streak.close": "Zamknij kalendarz serii",
    "streak.loading": "Ładowanie serii",
    "streak.unavailable": "Jeszcze niedostępne",
    "streak.restoreTitle": "Seria przywrócona",
    "streak.restoreDescription": ({ dates }) => `Ochronione: ${dates}`,
    "streak.remaining": ({ count }) => `Pozostało przywróceń: ${count}`,
    "streak.continue": "Dalej",
    "today.completion": "Wykonanie dnia",
    "today.dayCompletion": "Wykonanie dnia",
    "today.actionsDone": ({ done, total }) =>
      `${done} / ${total} zadań ukończonych`,
    "today.pageTitle": "Dzisiaj",
    "today.nextAction": "Następne działanie",
    "today.noNextAction": "Brak kolejnego kroku",
    "today.exitPlanning": "Wyjdź z trybu planowania",
    "today.planTitle": "Plan dnia",
    "today.selectedPlanTitle": "Plan dnia",
    "today.planSubtitle":
      "Najpierw wykonaj najbliższy ruch, potem przechodź przez resztę dnia",
    "today.planningMode": "Tryb planowania",
    "today.quickAdd": "Szybkie dodanie",
    "today.quickAddLead":
      "Dodaj zadanie szybko, a szczegóły otwieraj tylko wtedy, gdy są potrzebne",
    "today.dailyNote": "Notatka dnia",
    "today.dailyNoteLead":
      "Opcjonalna. Otwiera się do pisania o 17:00. Zasila podsumowanie poniżej",
    "today.dailyNoteLeadLocked": ({ hour }) =>
      `Opcjonalna. Otwiera się do pisania o ${hour}. Zasila podsumowanie poniżej`,
    "today.dailyNoteLeadEditable": ({ hour }) =>
      `Opcjonalna. Otwiera się do pisania o ${hour}. Zasila podsumowanie poniżej`,
    "today.dailyNoteInfo": ({ hour }) =>
      `Otwiera się do pisania o ${hour} i zasila podsumowanie poniżej. Opisz, co było dziś bardzo dobre, dobre i złe, oraz swoje przemyślenia.`,
    "today.dailyReflection": "Refleksja dnia",
    "today.aiSummaryLabel": "Podsumowanie",
    "today.aiRecommendationLabel": "Rekomendacja na jutro",
    "today.generatingAdvice": "Generowanie rekomendacji",
    "today.reflectionAdvicePending":
      "Zapisz notatkę, aby wygenerować konkretną rekomendację na jutro",
    "today.reflectionPlaceholder":
      "np. Pominąłem wieczorne zadania, bo nie miałem ochoty ich robić; przez dwie godziny skupiłem się na pracy nad głównym celem",
    "today.saveNote": "Zapisz notatkę",
    "today.noteSaving": "Zapisywanie notatki…",
    "today.noteSaved": "Notatka zapisana. Możesz dalej edytować.",
    "today.noteUnsaved": "Zmiany nie zostały zapisane.",
    "today.noteSaveFailed": "Nie udało się zapisać notatki. Spróbuj ponownie.",
    "today.planTomorrow": "Zaplanuj jutro",
    "today.recommendationActionsHelp":
      "Nie pasuje? Ukryj ją lub usuń na stałe.",
    "today.rejectRecommendation": "Nie pasuje",
    "today.rejectRecommendationHelp": "Ukryj tę rekomendację dla tej notatki.",
    "today.deleteRecommendation": "Usuń na stałe",
    "today.deleteRecommendationHelp": "Trwale usuń tę rekomendację.",

    "todayReflectionWorkflow.eyebrow": "Refleksja",
    "todayReflectionWorkflow.note.helper":
      "Podsumuj dziś i opcjonalnie opisz jutro. Disciplio zamieni to w edytowalny plan.",
    "todayReflectionWorkflow.note.emptyPast": "Tego dnia nie zapisano notatki.",
    "todayReflectionWorkflow.tomorrowContext.label":
      "Coś ważnego na jutro? (opcjonalnie)",
    "todayReflectionWorkflow.tomorrowContext.placeholder":
      "Stałe godziny pracy, spotkania, ograniczenia energii lub rzeczy, które już planujesz zrobić",
    "todayReflectionWorkflow.action.generate": "Wygeneruj refleksję",
    "todayReflectionWorkflow.action.regenerate": "Wygeneruj ponownie",
    "todayReflectionWorkflow.action.retry": "Spróbuj ponownie",
    "todayReflectionWorkflow.regenerate.menuLabel": "Generuj ponownie…",
    "todayReflectionWorkflow.regenerate.FULL": "Wygeneruj refleksję ponownie",
    "todayReflectionWorkflow.regenerate.RECOMMENDATION_ONLY":
      "Wygeneruj ponownie rekomendację",
    "todayReflectionWorkflow.regenerate.PLAN_ONLY":
      "Wygeneruj ponownie plan na jutro",
    "todayReflectionWorkflow.state.resuming": "Wczytywanie Twojej refleksji…",
    "todayReflectionWorkflow.state.outdated":
      "Notatka zmieniła się od czasu wygenerowania tej refleksji. Wygeneruj ponownie, aby ją zaktualizować.",
    "todayReflectionWorkflow.live.resumed": "Refleksja wznowiona.",
    "todayReflectionWorkflow.error.consentRequired":
      "Zaakceptuj informację o AI, aby wygenerować refleksję.",
    "todayReflectionWorkflow.error.generic":
      "Nie udało się wygenerować refleksji. Spróbuj ponownie.",
    "todayReflectionWorkflow.error.unavailable":
      "Disciplio AI jest chwilowo niedostępne. Notatka dnia została zapisana; spróbuj ponownie za chwilę.",
    "todayReflectionWorkflow.error.contract":
      "Nie udało się przetworzyć danych refleksji. Notatka dnia została zapisana; zaktualizuj aplikację i spróbuj ponownie.",
    "todayReflectionWorkflow.error.generator":
      "Model nie zwrócił poprawnej refleksji. Notatka dnia została zapisana; spróbuj wygenerować ją ponownie.",
    "todayReflectionWorkflow.error.reviewer":
      "Nie udało się zakończyć weryfikacji refleksji. Bezpieczna notatka i plan zostały zachowane.",
    "todayReflectionWorkflow.error.responseInvalid":
      "Disciplio otrzymało nieprawidłową odpowiedź AI. Notatka dnia została zapisana; spróbuj ponownie.",
    "todayReflectionWorkflow.error.quota": ({ remaining, limit }) =>
      `Osiągnięto dzienny limit AI (pozostało ${remaining}/${limit}). Spróbuj ponownie po jego odnowieniu.`,
    "todayReflectionWorkflow.error.notFound":
      "Ta sesja refleksji jest już niedostępna. Rozpocznij nową.",
    "todayReflectionWorkflow.question.ariaLabel":
      "Krótkie pytanie od Disciplio",
    "todayReflectionWorkflow.question.badge": "Disciplio AI",
    "todayReflectionWorkflow.question.chipsLabel": "Opcje odpowiedzi",
    "todayReflectionWorkflow.question.somethingElse": "Coś innego",
    "todayReflectionWorkflow.question.freeTextPlaceholder":
      "Powiedz więcej własnymi słowami",
    "todayReflectionWorkflow.question.submitAnswer": "Wyślij odpowiedź",
    "todayReflectionWorkflow.question.helper":
      "Odpowiedź pomoże Disciplio przygotować dokładniejszą rekomendację i plan.",
    "todayReflectionWorkflow.question.skip": "Pomiń pytanie",
    "todayReflectionWorkflow.reflection.forTomorrow": "Na jutro:",
    "todayReflectionWorkflow.recommendation.label": "Rekomendacja",
    "todayReflectionWorkflow.recommendation.firstStep": ({ step }) =>
      `Pierwszy krok: ${step}`,
    "todayReflectionWorkflow.recommendation.generatedPlanItem":
      "Dodano sugerowane działanie do planu na jutro poniżej.",
    "todayReflectionWorkflow.feedback.helpful": "Pomocne",
    "todayReflectionWorkflow.feedback.notUseful": "Nieprzydatne",
    "todayReflectionWorkflow.feedback.reason.TOO_REPETITIVE":
      "Zbyt powtarzalne",
    "todayReflectionWorkflow.feedback.reason.TOO_GENERIC": "Zbyt ogólne",
    "todayReflectionWorkflow.feedback.reason.NOT_RELEVANT_TODAY":
      "Nie na temat dzisiaj",
    "todayReflectionWorkflow.feedback.reason.ALREADY_DOING_THIS":
      "Już to robię",
    "todayReflectionWorkflow.feedback.reason.OTHER": "Inne",
    "todayReflectionWorkflow.feedback.more": "Więcej",
    "todayReflectionWorkflow.feedback.wrongAssumption": "Błędne założenie",
    "todayReflectionWorkflow.feedback.wrongAssumptionPlaceholder":
      "Co było błędne? (opcjonalnie)",
    "todayReflectionWorkflow.feedback.submit": "Wyślij",
    "todayReflectionWorkflow.feedback.dontRecommend":
      "Nie rekomenduj tego więcej",
    "todayReflectionWorkflow.feedback.suppressed":
      "Nie będzie więcej rekomendowane",
    "todayReflectionWorkflow.feedback.duration.ONE_WEEK": "Przez 1 tydzień",
    "todayReflectionWorkflow.feedback.duration.ONE_MONTH": "Przez 1 miesiąc",
    "todayReflectionWorkflow.feedback.duration.PERMANENT": "Na stałe",
    "todayReflectionWorkflow.feedback.error":
      "Nie udało się zapisać opinii. Spróbuj ponownie.",
    "todayReflectionWorkflow.plan.eyebrow": "Plan",
    "todayReflectionWorkflow.plan.title": "Plan na jutro",
    "todayReflectionWorkflow.plan.helper":
      "Przejrzyj proponowane działania, popraw to, co wymaga zmian, i zaakceptuj to, co ma trafić do planu na jutro.",
    "todayReflectionWorkflow.plan.addAction": "Zaplanuj jutro",
    "todayReflectionWorkflow.plan.acceptAll": "Zaakceptuj wszystko",
    "todayReflectionWorkflow.plan.accept": "Akceptuj",
    "todayReflectionWorkflow.plan.accepted": "Dodano do planu",
    "todayReflectionWorkflow.plan.acceptError":
      "Nie udało się zaktualizować planu na jutro. Spróbuj ponownie.",
    "todayReflectionWorkflow.plan.skippedConflicted": ({ count }) =>
      `Pominięto ${count} pozycji, ponieważ nakładają się na stałe zobowiązanie.`,
    "todayReflectionWorkflow.plan.empty": "Brak propozycji planu.",
    "todayReflectionWorkflow.plan.origin.FIXED_COMMITMENT": "Stałe",
    "todayReflectionWorkflow.plan.origin.EXISTING_ACTION": "Istniejące",
    "todayReflectionWorkflow.plan.origin.MOVED_FROM_TODAY":
      "Przeniesione z dziś",
    "todayReflectionWorkflow.plan.origin.PARSED_FROM_NOTE": "Z Twojej notatki",
    "todayReflectionWorkflow.plan.origin.AI_SUGGESTED": "Sugerowane",
    "todayReflectionWorkflow.plan.sourceIcon.goal": "Cel",
    "todayReflectionWorkflow.plan.sourceIcon.habit": "Nawyk",
    "todayReflectionWorkflow.plan.sourceIcon.FIXED_COMMITMENT":
      "Stałe zobowiązanie",
    "todayReflectionWorkflow.plan.sourceIcon.MOVED_FROM_TODAY":
      "Przeniesione z dzisiaj",
    "todayReflectionWorkflow.plan.sourceIcon.PARSED_FROM_NOTE":
      "Z Twojej notatki",
    "todayReflectionWorkflow.plan.sourceIcon.AI_SUGGESTED": "Sugestia AI",
    "todayReflectionWorkflow.plan.sourceIcon.EXISTING_ACTION":
      "Już w Twoim planie",
    "todayReflectionWorkflow.plan.moveUp": "Przesuń w górę",
    "todayReflectionWorkflow.plan.moveDown": "Przesuń w dół",
    "todayReflectionWorkflow.plan.titleLabel": "Tytuł",
    "todayReflectionWorkflow.plan.startLabel": "Początek",
    "todayReflectionWorkflow.plan.endLabel": "Koniec",
    "todayReflectionWorkflow.plan.doneEditing": "Gotowe",
    "todayReflectionWorkflow.plan.firstStep": ({ step }) =>
      `Pierwszy krok: ${step}`,
    "todayReflectionWorkflow.plan.conflict": ({ title }) =>
      `Nakłada się na stałe zobowiązanie: ${title}`,
    "todayReflectionWorkflow.plan.edit": "Edytuj",
    "todayReflectionWorkflow.plan.remove": "Usuń",
    "todayReflectionWorkflow.plan.regenerateWarning":
      "Masz niezapisane zmiany w planie na jutro. Ponowne generowanie może je zmienić lub usunąć. Kontynuować?",

    "today.quoteTitle": "Cytat dnia",
    "today.quoteLead": "Niewielki akcent marki zamiast pełnego hero",
    "notifications.liveLabel": "Codzienne przypomnienia",
    "notifications.startNow": ({ time }) => `Zacznij teraz · ${time}`,
    "notifications.endsSoon": ({ minutes, time }) =>
      `Koniec za ${minutes} min · ${time}`,
    "notifications.openToday": "Otwórz Dzisiaj",
    "notifications.dismiss": "Zamknij przypomnienie",
    "notifications.promptTitle": "Włącz powiadomienia przeglądarki",
    "notifications.promptBody":
      "Zezwól Disciplio na wysyłanie przypomnień o zaplanowanych działaniach, nawet gdy ta karta jest zamknięta.",
    "notifications.promptEnable": "Zezwól na powiadomienia",
    "notifications.promptEnabling": "Włączanie…",
    "notifications.promptLater": "Nie teraz",
    "notifications.promptFailed":
      "Nie udało się włączyć powiadomień. Spróbuj ponownie w ustawieniach konta.",
    "notifications.promptUnavailable":
      "Powiadomienia push nie są tu jeszcze skonfigurowane. Spróbuj ponownie później w ustawieniach konta.",

    "add.title": "Tytuł zadania",
    "add.placeholder": "Co zrobisz?",
    "add.button": "Dodaj",
    "add.more": "Czas, typ i cel tygodniowy",
    "add.type": "Typ",
    "add.goal": "Cel",
    "add.habit": "Nawyk",
    "add.time": "Godzina",
    "add.date": "Data",
    "add.week": "Tydzień",
    "add.thisWeek": ({ range }) => `Ten tydzień · ${range}`,
    "add.nextWeek": ({ range }) => `Następny tydzień · ${range}`,
    "add.from": "Od",
    "add.to": "Do",
    "add.clear": "Wyczyść",
    "add.timeConstraintWorkWindow": "Okno pracy (inne działania mogą być w nim zaplanowane)",
    "add.timeConstraintWorkWindowHint":
      "Wyłączone: całkowicie blokuje ten przedział czasu. Włączone: inne działania mogą być w nim zaplanowane.",
    "add.weeklyGoal": "Cel tygodniowy",
    "add.weeklyGoalLink": "Powiązanie z celem tygodniowym",
    "add.monthlyGoal": "Cel miesięczny",
    "add.none": "Brak",
    "add.amount": "Ilość",
    "add.unit": "Jednostka",
    "add.unitPlaceholder": "godziny, strony",
    "add.useProposedAmount": ({ amount }) => `Użyj ${amount} z zakresu czasu`,
    "add.unitMismatchWarning": ({ goalUnit }) =>
      `Ta jednostka różni się od jednostki powiązanego celu (${goalUnit}), więc ilość nie zaliczy się do jego postępu.`,
    "weeklyGoal.progressSummary": ({ progress, target, unit }) =>
      `${progress} / ${target} ${unit}`,
    "weeklyGoal.noTarget": "Brak celu",
    "metricUnitInput.showOptions": "Pokaż opcje jednostek",
    "add.linkAlert":
      "Powiązanie mierzalnego celu tygodniowego wymaga dodatniej wartości (np. godziny, strony)",
    "add.saveFailed":
      "Nie udało się zapisać zadania. Sprawdź połączenie i spróbuj ponownie.",
    "repeat.label": "Powtarzanie",
    "repeat.frequencyLabel": "Wzór powtarzania",
    "repeat.frequencyHelp": "Wybierz, jak często ten nawyk ma wracać",
    "repeat.intervalLabel": "Interwał",
    "repeat.daily": "Codziennie",
    "repeat.withExceptions": ({ repeat, exceptions }) =>
      `${repeat} oprócz ${exceptions}`,
    "repeat.customDays": ({ count }) => `Co ${count} dni`,
    "repeat.customDaysShort": "Co X dni",
    "repeat.weekly": "Co tydzień",
    "repeat.monthly": "Co miesiąc",
    "repeat.everyXDays": "Interwał dni",
    "repeat.rulesTitle": "Zasady powtarzania",
    "repeat.exceptions": "Wyjątki",
    "repeat.exceptionsHelp":
      "Opcjonalne pominięcia. Dni tygodnia są cykliczne, daty są jednorazowe",
    "repeat.skipWeekdays": "Pomiń dni tygodnia",
    "repeat.skipWeekdaysHelp":
      "Kliknij dni, kiedy nawyk nie powinien się pojawiać",
    "repeat.skipDates": "Pomiń konkretne daty",
    "repeat.skipDatesHelp":
      "Dodaj święta, wyjazdy albo pojedynczy dzień do pominięcia",
    "repeat.skipDate": "Pomiń datę",
    "repeat.selectedSkipDates": "Wybrane pomijane daty",
    "weekday.short.0": "Nd",
    "weekday.short.1": "Pn",
    "weekday.short.2": "Wt",
    "weekday.short.3": "Śr",
    "weekday.short.4": "Cz",
    "weekday.short.5": "Pt",
    "weekday.short.6": "Sb",
    "weeklyCategories.filterLabel": "Kategorie celów tygodniowych",
    "weeklyCategories.all": "Wszystkie",
    "weeklyCategories.placeholder": "Kategoria, np. Nauka",
    "weeklyCategories.useCustom": ({ value }) => `Użyj „${value}”`,
    "weeklyCategories.limitReached":
      "Osiągnięto limit własnych kategorii. Istniejące kategorie są nadal dostępne.",

    "plan.unscheduled": "Bez harmonogramu",
    "plan.flexible": "Elastyczne · bez stałej godziny",
    "plan.noUnscheduled": "Nie ma jeszcze zadań bez godziny",
    "plan.scheduled": "Zaplanowane",
    "plan.chronological": "Chronologicznie",
    "plan.noScheduled": "Nie ma jeszcze zadań z godziną",
    "plan.newScheduledAction": "Nowe zaplanowane zadanie",
    "plan.newUnscheduledAction": "Nowe zadanie bez godziny",
    "plan.completed": "Ukończone",
    "plan.completedHint":
      "Domyślnie widoczne, aby można było przejrzeć postępy",
    "plan.noCompleted": "Brak ukończonych zadań",
    "plan.linked": ({ title, amount, unit }) =>
      `Cel tygodniowy: ${title} (+${amount} ${unit})`,
    "plan.linkedUnmeasured": ({ title }) => `Cel tygodniowy: ${title}`,
    "plan.habitMetric": ({ amount, unit }) => `${amount} ${unit}`,
    "plan.noLink": "Brak powiązania z celem tygodniowym",
    "plan.anyTime": "Dowolna pora",
    "plan.wrapsNextDay": "+1 dzień",
    "plan.wrapsNextDayAria": "Kończy się po północy, następnego dnia",
    "plan.sectionEmpty": "Na razie nic tutaj nie ma",
    "plan.edit": "Edytuj",
    "plan.moreActions": "Więcej działań celu",
    "plan.saving": "Zapisywanie…",
    "plan.duplicate": "Duplikuj cel",
    "plan.duplicateTitle": "Duplikuj cel",
    "plan.duplicateDescription": "Utwórz kopie tego celu w wybrane dni.",
    "plan.closeDuplicate": "Zamknij duplikowanie celu",
    "plan.duplicateModes": "Tryb duplikowania",
    "plan.duplicateSimple": "Proste",
    "plan.duplicateWeekdays": "Dni tygodnia",
    "plan.duplicateRange": "Zakres dat",
    "plan.duplicatePickDate": "Wybierz datę",
    "plan.duplicateRepeatOn": "Powtarzaj w",
    "plan.duplicateStartDate": "Data początkowa",
    "plan.duplicateEndDate": "Data końcowa",
    "plan.duplicateRangeInvalid":
      "Wybierz datę końcową nie wcześniejszą niż początkowa.",
    "plan.duplicateRangeTooLong":
      "Wybierz zakres obejmujący najwyżej 31 kopii.",
    "plan.duplicateDays": "Wybierz dni",
    "plan.duplicateAllWeek": "Cały tydzień",
    "plan.duplicateWorkdays": "Dni robocze",
    "plan.duplicateCustomDays": "Własne dni",
    "plan.duplicateSourceDay": ({ day }) =>
      `Dzień źródłowy: ${day}. Nie zostanie skopiowany.`,
    "plan.duplicateKeepDetails": "Zachowaj ten sam czas i szczegóły",
    "plan.duplicateKeepDetailsHelp":
      "Czas, ilość i inne szczegóły zostaną skopiowane.",
    "plan.duplicateKeepTime": "Zachowaj godzinę",
    "plan.duplicateKeepTimeHelp":
      "Skopiuj zaplanowaną godzinę do każdego nowego celu.",
    "plan.duplicateKeepWeekly": "Zachowaj połączenie z celem tygodniowym",
    "plan.duplicateKeepWeeklyHelp":
      "Skopiuj wkład i zachowaj połączenie w hierarchii planowania.",
    "plan.duplicateKeepWeeklyUnavailable":
      "Ten cel nie jest połączony z celem tygodniowym.",
    "plan.duplicateHierarchyWarning":
      "Dla innego tygodnia Disciplio może utworzyć lub ponownie wykorzystać cel tygodniowy i jego miesięczny cel nadrzędny. Nowe cele nadrzędne zaczynają z zerowym postępem i zachowują połączenie z celem rocznym.",
    "plan.duplicateCopyOptions": "Opcje kopiowania",
    "plan.duplicateToDays": ({ count }) =>
      `Duplikuj do ${count} ${count === 1 ? "dnia" : "dni"}`,
    "plan.duplicating": "Duplikowanie…",
    "plan.duplicateFailed":
      "Nie udało się zduplikować celów. Sprawdź połączenie i spróbuj ponownie.",
    "plan.duplicateSuccessSingle": ({ date }) => `Cel dodano na ${date}.`,
    "plan.duplicateSuccessList": ({ dates }) => `Cel dodano na: ${dates}.`,
    "plan.duplicateSuccessMany": ({ count, from, to }) =>
      `Cel dodano na ${count} dat (${from} – ${to}).`,
    "plan.closeDuplicateSuccess": "Zamknij potwierdzenie duplikowania",
    "plan.delete": "Usuń",
    "plan.deleteTaskTitle": ({ title }) => `Usunąć „${title}”?`,
    "plan.deleteTaskDescription": "Tej akcji nie można cofnąć.",
    "plan.deleteGoalTitle": ({ title }) => `Usunąć „${title}”?`,
    "plan.deleteGoalDescription":
      "Usunięte zostaną też wszystkie powiązania z tym celem. Tej akcji nie można cofnąć.",
    "plan.deleteHabitTitle": ({ title }) =>
      `Usunąć powtarzający się nawyk „${title}”?`,
    "plan.deleteHabitDescription":
      "Wybierz, czy usunąć tylko to wystąpienie, czy wszystkie wystąpienia.",
    "plan.changeHabitTimeTitle": ({ title }) =>
      `Zmienić godzinę dla „${title}”?`,
    "plan.changeHabitTimeDescription":
      "Wybierz, czy ta godzina ma obowiązywać tylko w tym dniu, czy od tego dnia w przyszłości.",
    "plan.changeHabitTimeOne": "Tylko ten dzień",
    "plan.changeHabitTimeFuture": "Ten dzień i przyszłe",
    "plan.deleteThisDate": "Usuń tę datę",
    "plan.deleteAllOccurrences": "Usuń wszystkie wystąpienia",
    "plan.deletedUndoMessage": ({ title }) => `Usunięto „${title}”`,
    "plan.undo": "Cofnij",
    "plan.closeUndo": "Zamknij",
    "plan.save": "Zapisz",
    "plan.cancel": "Anuluj",
    "plan.show": "Pokaż",
    "plan.hide": "Ukryj",
    "plan.suggestionOnly": "Tylko sugestia",
    "plan.scheduledSuggestionHint":
      "Tego jeszcze nie ma w planie dnia. Użyj, aby utworzyć zaplanowane zadanie, jeśli pasuje na dziś",
    "plan.unscheduledSuggestionHint":
      "Tego jeszcze nie ma w planie dnia. Użyj jako elastycznego zadania tylko wtedy, gdy pasuje na dziś",
    "plan.useSuggestion": "Użyj sugestii",

    "planning.workspace": "Przestrzeń planowania",
    "planning.subtitle": "Dzisiejsze zadania obok kontekstu tygodniowego",
    "planning.weekHeaderTitle": "Plan tygodnia",
    "planning.weekSubtitle":
      "Przejrzyj cele miesięczne i ułóż priorytety na ten tydzień",
    "planning.monthHeaderTitle": ({ month }) => `Plan miesiąca: ${month}`,
    "planning.monthSubtitle":
      "Porównaj cele miesięczne z rezultatami rocznymi, które mają wspierać",
    "planning.yearHeaderTitle": ({ year }) => `Plan roku: ${year}`,
    "planning.yearSubtitle":
      "Porównaj cele roczne z globalnym kierunkiem, który mają wspierać",
    "planning.dayMode": "Dzień",
    "planning.weekMode": "Tydzień",
    "planning.monthMode": "Miesiąc",
    "planning.yearMode": "Rok",
    "planning.today": "Dzisiaj",
    "planning.addToday": "Dodaj na dziś",
    "planning.list": "Twoja lista",
    "planning.weeklyPlan": "Plan tygodnia",
    "planning.weekLead":
      "Użyj tego, aby zdecydować, co powinno trafić na dzisiejszą listę",
    "planning.avgCompletion": "Średnia realizacja",
    "planning.goalsTracked": "Liczba celów",
    "planning.weekReview": "Przegląd tygodnia",
    "planning.weekReviewText": ({
      aligned,
      total,
      percent,
      monthlyAvg,
      focus,
    }) =>
      `Zgodne z ${aligned}/${total} celami miesięcznymi (${percent}%). Średnia realizacja miesiąca to ${monthlyAvg}%; utrzymaj ten tydzień wokół: ${focus}`,
    "planning.monthReview": "Przegląd miesiąca",
    "planning.monthReviewText": ({
      aligned,
      total,
      percent,
      yearGoals,
      monthlyAvg,
      focus,
    }) =>
      yearGoals
        ? `Zgodne z ${aligned}/${total} celami miesięcznymi (${percent}%) względem ${yearGoals} celów rocznych. Średnia realizacja miesiąca to ${monthlyAvg}%; chroń uwagę wokół: ${focus}`
        : `Brak celów rocznych, więc miesiąca nie da się jeszcze sprawdzić względem rezultatów roku. Średnia realizacja miesiąca to ${monthlyAvg}%; doprecyzuj cele roczne albo pchnij: ${focus}`,
    "planning.noMonthlyFocus": "najsłabszego celu miesięcznego",
    "planning.suggestedFocus": "Sugerowany fokus",
    "planning.suggestionsLoading": "Generuję dla Ciebie zadania-sugestie",
    "planning.generateDaySuggestions": "Generuj sugestie na dzień",
    "planning.generateWeekSuggestions": "Generuj sugestie na tydzień",
    "planning.generateMonthSuggestions": "Generuj sugestie na miesiąc",
    "planning.generateYearSuggestions": "Generuj sugestie na rok",
    "planning.suggestionsEmpty":
      "Wszystkie cele zaplanowane na dziś — nie ma więcej sugestii",
    "planning.suggestionsEmptyDay": "Brak sugestii na dziś",
    "planning.suggestionsEmptyWeek": "Brak sugestii na ten tydzień",
    "planning.suggestionsEmptyMonth": "Brak sugestii na ten miesiąc",
    "planning.suggestionsEmptyYear": "Brak sugestii na ten rok",
    "planning.suggestionPersistenceError":
      "Nie udało się zapisać odrzucenia. Spróbuj ponownie.",
    "planning.remainingGap": "Na podstawie pozostałej luki tygodniowej",
    "planning.focusFallback":
      "Zacznij planowanie od celu z najniższym pokryciem",
    "planning.monthGoals": "Cele miesiąca",
    "planning.monthGoalsFor": ({ month }) => `Twoje cele na ${month}`,
    "planning.monthTitle":
      "Zaplanuj tydzień na podstawie priorytetów miesięcznych",
    "planning.monthLead":
      "Wybierz miesięczne cele z niskim postępem i zamień je na zwycięstwa tego tygodnia",
    "planning.monthPlanningLead":
      "Zdecyduj, które miesięczne rezultaty wymagają teraz najmocniejszego pchnięcia",
    "planning.yearGoalsFor": ({ year }) => `Cele na ${year}`,
    "planning.yearGoalsLead":
      "Użyj ich jako kontekstu po prawej stronie przy wyborze priorytetów miesiąca",
    "planning.yearPlanningLead":
      "Ustal roczne rezultaty, które Twoja miesięczna praca ma realnie przesuwać do przodu",
    "planning.yearGoal": "Cel roczny",
    "planning.yearGoalAnchor":
      "Utrzymuj cele miesięczne w zgodzie z tym rezultatem",
    "planning.noWeeklyGoals":
      "Nie ma jeszcze zapisanych celów tygodniowych. Dodaj cele tygodniowe, aby zobaczyć je tutaj",
    "planning.noMonthGoals":
      "Nie ma jeszcze zapisanych celów miesięcznych. Dodaj cele miesięczne, aby zobaczyć je tutaj",
    "planning.noYearGoals":
      "Nie ma jeszcze zapisanych celów rocznych. Dodaj cele roczne w Koncie, aby zobaczyć je tutaj",
    "planning.addYearGoal": "Dodaj",
    "planning.yearGoalPlaceholder": "Dodaj cel roczny",
    "planning.yearGoalExamples": "Przykłady celów rocznych",
    "planning.yearGoalExampleProduct": "Wypuścić produkt v1",
    "planning.yearGoalExampleHealth": "Przebiec półmaraton",
    "planning.yearGoalExampleMoney": "Oszczędzić 20% dochodu",
    "planning.addMonthGoal": "Dodaj cel miesięczny",
    "planning.goalAddedToast": ({ title }) => `Cel dodany: „${title}”`,
    "planning.monthGoalPlaceholder": "Nazwij działanie lub rezultat",
    "planning.monthGoalTitleLabel": "Działanie miesięczne",
    "planning.monthGoalTargetLabel": "Wartość docelowa",
    "planning.monthGoalProgressLabel": "Bieżący postęp",
    "planning.monthGoalTargetPlaceholder": "np. 20",
    "planning.monthGoalUnitLabel": "Jednostka",
    "planning.monthGoalUnitPlaceholder": "np. godziny",
    "planning.weekGoalTitleLabel": "Cel tygodniowy",
    "planning.weekGoalPlaceholder": "Nazwij priorytet tygodnia",
    "planning.weekGoalTargetLabel": "Wartość docelowa",
    "planning.weekGoalProgressLabel": "Bieżący postęp",
    "planning.weekGoalUnitLabel": "Jednostka",
    "planning.weekGoalCategoryLabel": "Kategoria",
    "planning.yearGoalTitleLabel": "Cel roczny",
    "planning.globalGoalTitleLabel": "Cel globalny",
    "planning.goalPlanLabel.week": "Plan na tydzień",
    "planning.goalPlanLabel.month": "Plan na miesiąc",
    "planning.goalPlanLabel.year": "Plan na rok",
    "planning.goalPlanLabel.global": "Plan długoterminowy",
    "planning.goalProgressLabel": "Postęp",
    "planning.goalProgressSummary": ({ progress, target, unit }) =>
      `${progress} z ${target} ${unit}`,
    "planning.goalProgressAutoHint":
      "Aktualizuje się automatycznie z wykonanych działań",
    "planning.goalManualProgressCta": "+ Już zacząłem realizować ten cel",
    "planning.monthGoalComposerHint":
      "Wartość docelową i jednostkę wpisz tylko wtedy, gdy chcesz mierzyć postęp",
    "planning.monthGoalRequiredError": "Wpisz działanie miesięczne",
    "planning.monthGoalTargetError": "Wpisz wartość docelową większą od zera",
    "planning.monthGoalMetricPairError":
      "Jeśli wpiszesz wartość docelową, uzupełnij też jednostkę",
    "planning.monthGoalLinkError":
      "Ten cel roczny nie jest już dostępny. Wybierz inny cel albo brak powiązania.",
    "planning.monthGoalSaveError":
      "Nie udało się zapisać tego celu miesięcznego. Spróbuj ponownie.",
    "planning.weekGoalSaveError":
      "Nie udało się zapisać tego celu tygodniowego. Spróbuj ponownie.",
    "planning.monthGoalDefaultUnit": "rezultat",
    "planning.noProgressMetric": "Brak miernika postępu",
    "planning.monthGoalSuggestionTitle": ({ title }) =>
      `Krok miesięczny dla: ${title}`,
    "planning.monthGoalSuggestionHint": () => "Z celu rocznego",
    "planning.monthGoalSuggestionReason": ({ yearGoal }) =>
      `Miesięczny wynik dla ${yearGoal}.`,
    "planning.linkYearGoal": "Połączony cel roczny",
    "planning.linkedYearGoal": ({ goal }) => `Wspiera cel roczny: ${goal}`,
    "planning.noLinkedYearGoal": "Brak połączonego celu rocznego",
    "planning.linkMonthlyGoal": "Połączony cel miesięczny",
    "planning.linkedMonthlyGoal": ({ goal }) =>
      `Wspiera cel miesięczny: ${goal}`,
    "planning.noLinkedMonthlyGoal": "Brak połączonego celu miesięcznego",
    "planning.globalGoals": "Cele globalne",
    "planning.globalGoalsLead":
      "Szerszy kierunek, z którego powinny wynikać cele roczne",
    "planning.globalGoal": "Cel globalny",
    "planning.linkGlobalGoal": "Połączony cel globalny",
    "planning.linkedGlobalGoal": ({ goal }) => `Wspiera cel globalny: ${goal}`,
    "planning.noLinkedGlobalGoal": "Brak połączonego celu globalnego",
    "planning.addGlobalGoal": "Dodaj cel globalny",
    "planning.globalGoalPlaceholder": "Dodaj cel globalny",
    "planning.globalGoalExamples": "Przykłady celów globalnych",
    "planning.globalGoalExampleDiscipline":
      "Zbudować zdyscyplinowany system pracy własnej",
    "planning.globalGoalExampleHealth": "Chronić zdrowie długoterminowo",
    "planning.globalGoalExampleFamily": "Być obecnym dla rodziny",
    "planning.noGlobalGoals":
      "Nie ma jeszcze zapisanych celów globalnych. Dodaj je w Koncie",
    "planning.monthFocus": ({ title }) =>
      `Zacznij od celu miesięcznego z najniższym pokryciem: ${title}`,
    "planning.yearFocus": ({ title }) =>
      `Trzymaj pierwszy cel roczny w zasięgu wzroku: ${title}`,
    "planning.addWeek": "Dodaj do planu tygodnia",
    "planning.weekQuickAddLead":
      "Wybierz tydzień, a potem dodaj priorytet tygodniowy wspierający cel miesięczny",
    "planning.weekSuggestionHint": "Tylko jeśli pasuje do tego tygodnia",
    "planning.monthSuggestionHint": "Tylko jeśli pasuje do tego miesiąca",
    "planning.monthSuggestionHintShort": "Tylko jeśli pasuje do tego miesiąca",
    "planning.yearSuggestionHintShort": "Tylko jeśli pasuje do tego roku",
    "planning.useInWeekPlan": "Użyj w planie tygodnia",
    "planning.remainingMonth": ({ percent, remaining, unit }) =>
      `${percent}% ukończone · zostało ${remaining} ${unit} w tym miesiącu`,
    "planning.completedGoalStatus": "Ukończone",
    "planning.markGoalCompleted": "Oznacz cel jako ukończony",
    "planning.markGoalActive": "Przywróć cel do aktywnych",
    "planning.reorderHandle": "Przeciągnij, aby zmienić kolejność",
    "planning.moveGoalUp": "Przesuń w górę",
    "planning.moveGoalDown": "Przesuń w dół",
    "planning.onTrack": "Na dobrej drodze",
    "planning.inMotion": "W ruchu",
    "planning.addGoal": "Dodaj cel",
    "planning.copyGoal": "Kopiuj",
    "planning.copyTitleSuffix": "(kopia)",
    "planning.copyTitleSuffixNumbered": ({ count }) => `(kopia ${count})`,
    "ragAdmin.label": "ADMIN",
    "ragAdmin.title": "Źródła RAG",
    "ragAdmin.lead":
      "Zarządzaj źródłami RAG używanymi później przez rekomendacje AI",
    "ragAdmin.sourceCount": ({ count }) => `${count} źródeł`,
    "ragAdmin.newSource": "Nowe źródło",
    "ragAdmin.editSource": "Edytuj źródło",
    "ragAdmin.formLead":
      "Użyj uporządkowanych pól, aby późniejsze rekomendacje mogły cytować jasne źródła",
    "ragAdmin.topic": "Temat",
    "ragAdmin.principle": "W czym może pomóc",
    "ragAdmin.principlePlaceholder": "fokus, sen, konsekwencja",
    "ragAdmin.principleHelp": "Kilka obszarów rozdziel przecinkiem",
    "ragAdmin.content": "Treść",
    "ragAdmin.actionableAdvice": "Praktyczna rada",
    "ragAdmin.sourceType": "Typ źródła",
    "ragAdmin.confidence": "Pewność",
    "ragAdmin.sourceUrl": "Link źródła",
    "ragAdmin.sourceType.book": "Książka",
    "ragAdmin.sourceType.blog": "Blog",
    "ragAdmin.sourceType.research": "Badanie",
    "ragAdmin.confidence.high": "Wysoka",
    "ragAdmin.confidence.medium": "Średnia",
    "ragAdmin.confidence.low": "Niska",
    "ragAdmin.addSource": "Dodaj źródło",
    "ragAdmin.saveChanges": "Zapisz zmiany",
    "ragAdmin.sources": "Źródła",
    "ragAdmin.sourcesLead": "Lista ładowana z backendowej bazy wiedzy",
    "ragAdmin.errorCount": ({ count }) => `${count} błędów indeksowania`,
    "ragAdmin.indexError": "Błąd indeksowania",
    "ragAdmin.reindex": "Indeksuj ponownie",
    "ragAdmin.reindexAll": "Indeksuj wszystkie",
    "ragAdmin.reindexing": "Indeksowanie",
    "ragAdmin.reindexingOne": "Indeksowanie źródła",
    "ragAdmin.reindexedOne": "Źródło zaindeksowane ponownie",
    "ragAdmin.reindexFailed":
      "Indeksowanie nie powiodło się. Sprawdź logi AI service",
    "ragAdmin.reindexingAll": ({ count }) => `Indeksowanie ${count} źródeł`,
    "ragAdmin.reindexedAll": "Wszystkie źródła zaindeksowane ponownie",
    "ragAdmin.ingestion.ready": "Gotowe",
    "ragAdmin.ingestion.chunked": "Podzielone",
    "ragAdmin.ingestion.embedded": "Osadzone",
    "ragAdmin.ingestion.error": "Błąd",
    "ragAdmin.empty": "Nie ma jeszcze źródeł RAG",
    "ragAdmin.required": "Uzupełnij wszystkie pola źródła przed zapisem",
    "ragAdmin.added": "Źródło dodane",
    "ragAdmin.updated": "Źródło zaktualizowane",
    "ragAdmin.deleted": "Źródło usunięte",

    "aiUsage.label": "ADMIN",
    "aiUsage.title": "Użycie AI",
    "aiUsage.lead":
      "Sprawdź wywołania AI, tokeny i szacowany koszt dla każdego użytkownika.",
    "aiUsage.heroCount": ({ count }) => `${count} użytkowników`,
    "aiUsage.backToRag": "Admin RAG",
    "aiUsage.currentView": "Tabela użycia",
    "aiUsage.stats": "Podsumowanie użycia",
    "aiUsage.totalCalls": "Wywołania AI",
    "aiUsage.totalTokens": "Tokeny razem",
    "aiUsage.totalCost": "Szacowany koszt",
    "aiUsage.users": "Użytkownicy",
    "aiUsage.tableTitle": "Użycie na użytkownika",
    "aiUsage.tableLead":
      "Jedna linia na użytkownika z sumami wywołań, tokenów i kosztów.",
    "aiUsage.loading": "Ładowanie",
    "aiUsage.empty": "Brak jeszcze zapisanych wywołań AI",
    "aiUsage.loadError": "Nie udało się wczytać podsumowania użycia AI",
    "aiUsage.user": "Użytkownik",
    "aiUsage.calls": "Wywołania",
    "aiUsage.promptTokens": "Tokeny promptu",
    "aiUsage.completionTokens": "Tokeny odpowiedzi",
    "aiUsage.cost": "Koszt",
    "aiUsage.firstCallAt": "Pierwsze wywołanie",
    "aiUsage.lastCallAt": "Ostatnie wywołanie",

    "week.label": "TYDZIEŃ",
    "week.heroTitle": "Tygodniowe cele i postęp",
    "week.heroLead":
      "Śledź, jak codzienne działania wspierają tygodniowe rezultaty",
    "week.avgCompletion": "Śr. realizacja",
    "week.goalsOnTrack": "Cele na kursie",
    "week.bestMomentum": "Najlepszy impet",
    "week.goalsProgress": "Postęp celów",
    "week.progressLead":
      "Najpierw pchaj cele z niskim pokryciem, aby ochronić wynik tygodnia",
    "week.completed": "Ukończone",
    "week.remaining": ({ percent, remaining, unit }) =>
      `${percent}% ukończone · zostało ${remaining} ${unit}`,
    "longPlans.label": "CELE DŁUGOTERMINOWE",
    "longPlans.heroTitle": "Cele długoterminowe",
    "longPlans.switcher": "Zakres celów",
    "longPlans.week": "Tydzień",
    "longPlans.month": "Miesiąc",
    "longPlans.year": "Rok",
    "longPlans.global": "Globalne",
    "longPlans.weekLead":
      "Śledź rezultaty, które powinny ruszyć w tym tygodniu",
    "longPlans.monthLead":
      "Przejrzyj miesięczne rezultaty, które nadają kierunek najbliższym tygodniom",
    "longPlans.yearLead":
      "Trzymaj kierunek roku w zasięgu wzroku przy wyborze bliższych celów",
    "longPlans.globalLead":
      "Przejrzyj najszersze cele, które wyznaczają ogólny kierunek",
    "longPlans.avgCompletion": "Śr. realizacja",
    "longPlans.goalsOnTrack": "Cele na kursie",
    "longPlans.bestMomentum": "Najlepszy impet",
    "longPlans.activeScope": "Aktywny zakres",
    "longPlans.yearItems": "Cele roczne",
    "longPlans.globalItems": "Cele globalne",
    "longPlans.yearAnchor": "Główna kotwica",
    "longPlans.globalAnchor": "Główny kierunek",
    "longPlans.weekGoals": "Cele tygodnia",
    "longPlans.monthGoals": "Cele miesiąca",
    "longPlans.yearGoals": "Cele roku",
    "longPlans.globalGoals": "Cele globalne",
    "longPlans.weekProgressLead":
      "Najpierw pchaj cele z niskim pokryciem, aby ochronić wynik tygodnia",
    "longPlans.monthProgressLead":
      "Użyj postępu miesiąca, aby zdecydować, co wymaga mocniejszego pchnięcia w tygodniu",
    "longPlans.emptyTitle": "Nie ma tu jeszcze celów",
    "longPlans.emptyLead":
      "Wyznacz jeden jasny rezultat na ten okres, a potem zamień go w cel, nad którym możesz pracować.",
    "longPlans.emptyFilteredTitle": "Żadne cele nie pasują do tego filtra",
    "longPlans.emptyFilteredLead":
      "Spróbuj innej kategorii albo pokaż wszystkie cele, aby znaleźć kolejny fokus.",
    "longPlans.clearFilter": "Pokaż wszystkie cele",
    "longPlans.yearProgressLead": "To długoterminowe kotwice z profilu Konta",
    "longPlans.completed": "Ukończone",
    "longPlans.remaining": ({ percent, remaining, unit }) =>
      `${percent}% ukończone · zostało ${remaining} ${unit}`,
    "longPlans.yearGoalMeta": "Rezultat roczny",
    "longPlans.yearGoalLead":
      "Rozbij to na cele miesięczne, a potem zobowiązania tygodniowe",
    "longPlans.noYearGoals":
      "Nie ma jeszcze zapisanych celów rocznych. Dodaj cele roczne w Koncie, aby zobaczyć je tutaj",
    "longPlans.globalGoalsLead":
      "Są szersze niż cele roczne i wyznaczają ogólny kierunek",
    "longPlans.globalGoal": "Cel globalny",
    "longPlans.globalGoalMeta": "Kierunek globalny",
    "longPlans.globalGoalLead":
      "Użyj tego do kształtowania celów rocznych, a potem rezultatów miesięcznych",
    "longPlans.noGlobalGoals":
      "Nie ma jeszcze zapisanych celów globalnych. Dodaj je w Koncie",
    "goalImage.generate": "Wygeneruj obraz",
    "goalImage.title": "Obraz celów",
    "goalImage.preview": "Podgląd",
    "goalImage.download": "Pobierz PNG",
    "goalImage.copyImage": "Kopiuj obraz",
    "goalImage.copySuccess": "Obraz został skopiowany do schowka.",
    "goalImage.copyError":
      "Nie udało się skopiować obrazu. Pobierz go zamiast tego.",
    "goalImage.copyUnavailable":
      "Kopiowanie obrazów nie jest dostępne w tej przeglądarce.",
    "goalImage.generating": "Generowanie…",
    "goalImage.close": "Zamknij",
    "goalImage.generationError":
      "Nie udało się wygenerować obrazu. Spróbuj ponownie.",
    "goalImage.noGoals": "Brak celów do eksportu",
    "goalImage.totalGoals": "Wszystkie cele",
    "goalImage.completed": "Ukończone",
    "goalImage.averageProgress": "Średni postęp",
    "goalImage.action": "Działanie",
    "goalImage.generated": "Wygenerowano",
    "goalImage.footerTagline":
      "Zamieniaj długoterminowe cele w codzienne działanie",
    "goalImage.longTermDirection": "Kierunek długoterminowy",
    "goalImage.weekTitle": "Moje cele tygodnia",
    "goalImage.monthTitle": "Moje cele miesiąca",
    "goalImage.yearTitle": "Moje cele roku",
    "goalImage.globalTitle": "Moje cele globalne",
    "goalImage.weekSubtitle":
      "Przegląd wszystkich celów i działań na ten tydzień.",
    "goalImage.monthSubtitle": "Priorytety, które kształtują ten miesiąc.",
    "goalImage.yearSubtitle": "Rezultaty wyznaczające kierunek tego roku.",
    "goalImage.globalSubtitle":
      "Kierunek stojący za Twoimi długoterminowymi planami.",

    "calendar.label": "KALENDARZ",
    "calendar.heroSub": ({ label }) =>
      `Widok ${label.toLowerCase()} · godziny od 06:00 do 23:00`,
    "calendar.tablist": "Widok kalendarza",
    "calendar.day": "Dzień",
    "calendar.week": "Tydzień",
    "calendar.threeDays": "3 dni",
    "calendar.month": "Miesiąc",
    "calendar.noFixedTime": "Bez stałej godziny",
    "calendar.done": "Gotowe",
    "calendar.todo": "Do zrobienia",
    "calendar.status": "Status",
    "calendar.editTask": "Edytuj zadanie",
    "calendar.editTaskHint": "Zmień tytuł, typ, godzinę i status wykonania",
    "calendar.editTaskAria": ({ title }) => `Edytuj ${title}`,
    "calendar.titleRequired": "Wpisz tytuł przed duplikowaniem",
    "calendar.invalidTime": "Godzina końca musi się różnić od godziny startu",
    "calendar.wrapsContinuesAria": ({ title }) =>
      `${title} jest kontynuacją z poprzedniego dnia`,
    "calendar.groupMoveFailed":
      "Niektórych celów nie udało się przenieść. Daty zostały odświeżone.",
    "calendar.habitDayMoveBlocked":
      "Nawyk zostaje w swoim dniu. Zamiast tego zmieniono jego godzinę w tym dniu.",
    "calendar.now": "Teraz",
    "calendar.setStartTimeToNow": "Ustaw godzinę rozpoczęcia na teraz",
    "calendar.setEndTimeToNow": "Ustaw godzinę zakończenia na teraz",
    "calendar.weekdays.mon": "Pon",
    "calendar.weekdays.tue": "Wt",
    "calendar.weekdays.wed": "Śr",
    "calendar.weekdays.thu": "Czw",
    "calendar.weekdays.fri": "Pt",
    "calendar.weekdays.sat": "Sob",
    "calendar.weekdays.sun": "Nd",
    "calendar.month.todayPlan": "Dzisiejszy plan",
    "calendar.month.planForDate": "Plan na ten dzień",
    "calendar.month.noPlan": "Brak zapisanego planu na ten dzień",
    "calendar.month.noActions":
      "Nie ma jeszcze zadań. Dodaj je z widoku Dzisiaj albo Planowanie",
    "calendar.month.noActionsForDate":
      "Nie ma jeszcze zadań zapisanych na ten dzień",
    "calendar.month.pickToday":
      "Tutaj pokazują się tylko dzisiejsze zadania. Wybierz dzisiaj, aby zobaczyć listę",
    "calendar.anyTime": "Dowolna pora",
    "dateNav.controls": "Sterowanie datą",
    "dateNav.previous": "Wstecz",
    "dateNav.current": "Bieżący",
    "dateNav.next": "Dalej",
    "dateNav.pickDate": "Wybierz datę",
    "dateNav.close": "Zamknij wybór daty",

    "insights.label": "ANALIZY",
    "insights.title": "Informacja zwrotna AI o dyscyplinie",
    "insights.emptyTitle": "Brak analiz AI",
    "insights.emptyPrompt":
      "Wygeneruj analizę AI, aby zobaczyć spersonalizowane wnioski.",
    "insights.eligibilityProgress": ({ count, required }) =>
      `Zapisane dni planowania: ${count}/${required}.`,
    "insights.eligibilityRemaining": ({ count }) =>
      `Potrzeba jeszcze ${count} dnia planowania, aby odblokować generowanie.`,
    "insights.generate": "Wygeneruj analizę",
    "insights.loadError":
      "Nie udało się załadować danych analiz. Spróbuj ponownie.",
    "insights.lead":
      "Coaching pod zachowanie: historia, cele, refleksja i sygnały realizacji",
    "insights.executionStrong": "Mocne wykonanie",
    "insights.executionSteady": "Stabilne wykonanie",
    "insights.executionRisk": "Ryzyko wykonania",
    "insights.executionNeedsWork": "Wymaga wsparcia wykonania",
    "insights.lockedIn": "Mocny rytm",
    "insights.building": "Budujesz rytm",
    "insights.fresh": "Nowa seria",
    "insights.reflectionCaptured": "Refleksja zapisana",
    "insights.reflectionMissing": "Brak refleksji",
    "insights.reflectionCount": ({ count }) => `${count}/7`,
    "insights.summary": "Podsumowanie AI",
    "insights.completionPercent": ({ percent }) => `${percent}% realizacji`,
    "insights.metrics": "Metryki analizy",
    "insights.completed": "Ukończone w tym tygodniu",
    "insights.actionsToday": "zadań dzisiaj",
    "insights.currentStreak": "Aktualna seria",
    "insights.consistencySignal": "sygnał regularności",
    "insights.reflection": "Refleksja",
    "insights.added": "Dodana",
    "insights.missing": "Brak",
    "insights.inputQuality": "jakość wejścia",
    "insights.sourceSignals": "Sygnały źródłowe",
    "insights.inputsUsed": "Wejścia użyte do rekomendacji",
    "insights.date": "Data",
    "insights.completionSignal": "Sygnał wykonania",
    "insights.streakSignal": "Sygnał serii",
    "insights.dayStreakActive": ({ streak }) => `${streak} dni aktywnej serii`,
    "insights.reflectionSignal": "Sygnał refleksji",
    "insights.noReflection": "Brak refleksji na dziś",
    "insights.aiHealth": ({ score }) => `Kondycja AI ${score}%`,
    "insights.nextActions": "Następne działania",
    "insights.priorityMoves":
      "Priorytetowe ruchy na podstawie luk tygodniowych",
    "insights.contribution": ({ amount, unit }) => `Wkład: ${amount} ${unit}`,
    "insights.noPriorityGap":
      "Nie wykryto pilnej luki. Trzymaj obecny plan i chroń bloki skupienia",
    "insights.addFromSuggestion":
      "Zamień rekomendację w realny element planu przed wyjściem z tego widoku",
    "insights.modelReading": "Odczyt modelu",
    "insights.weakestSignal": ({ signal }) =>
      `Popraw ${signal.toLowerCase()}, aby podnieść jakość rekomendacji`,
    "insights.aiGenerated":
      "Uwzględnia ostatnie tygodnie wykonania, powiązania celów, refleksję i baza wiedzy — nie tylko dzisiejszy migawkowy widok",
    "insights.todayDone": "Wykonanie dnia",
    "insights.todayDoneValue": ({ done, total, percent }) =>
      `${done}/${total} działań wykonano (${percent}%)`,
    "insights.whatBlocked": "Co mogło przeszkodzić",
    "insights.blockerLow":
      "Nie widać krytycznej blokady; chroń rytm i nie dodawaj zbędnego zakresu",
    "insights.blockerNoReflection":
      "Brak refleksji, więc system może wnioskować o blokadach tylko z niewykonanych działań",
    "insights.blockerFromReflection": ({ reflection }) =>
      `Twoja refleksja wskazuje na: ${reflection}`,
    "insights.tomorrowMove": "Co zrobić jutro",
    "insights.tomorrowProtect":
      "Zostaw podobny układ planu i zarezerwuj jeden chroniony blok skupienia rano",
    "insights.tomorrowRecover":
      "Przenieś najtrudniejszy niedokończony cel do pierwszego bloku skupienia i zmniejsz resztę dnia",
    "insights.problemZonesTitle": "Walka z problemowymi strefami",
    "insights.problemZonesLead":
      "Jak trudne obszary przejawiają się w działaniu — i konkretne eksperymenty dla każdej strefy",
    "insights.problemZoneFallback": "Brak ustawionej problemowej strefy",
    "insights.problemZoneStable":
      "Widać ruch. Jutro zostaw jedno małe ograniczenie dla tej strefy",
    "insights.problemZoneNeedsWork":
      "Ta strefa potrzebuje wyraźniejszej bariery: jeden blok czasu, jeden limit, jedna następna akcja",
    "insights.alignmentTitle": "Zgodność celów",
    "insights.alignmentLead":
      "Jak mocno dzienne warstwy łączą się z wyższymi — silniejsza spójność to mniej chaosu",
    "insights.alignDailyWeekly": "Cele dzienne → cele tygodnia",
    "insights.alignWeeklyMonth": "Cele tygodnia → cele miesiąca",
    "insights.alignMonthYear": "Cele miesiąca → cele roku",
    "insights.alignYearGlobal": "Cele roku → cele globalne",
    "insights.alignDailyWeeklyDetail": ({ linked, total }) =>
      `${linked}/${total} dziennych działań celu jest połączonych z celem tygodnia`,
    "insights.alignParentDetail": ({ linked, total }) =>
      `${linked}/${total} celów wygląda na połączone z poziomem wyżej`,
    "insights.reloadWithAI": "Generuj ponownie z AI",
    "insights.reloading": "Generowanie",
    "insights.generatedBy": ({ provider, model }) =>
      `Wygenerowano przez ${provider}/${model}`,
    "insights.reloadError": "Nie udało się teraz wygenerować analiz AI",
    "insights.analysisStepsTitle": "Kroki analizy",
    "insights.analysisStepsLead":
      "Obserwowalne sygnały ważone przez AI — nie same instrukcje",
    "insights.patternWarningLabel": "Wzorzec do pilnowania",
    "insights.zoneHowYouManage": "Jak zwykle sobie z tym radzisz:",
    "insights.zoneAdvice": "Spróbuj następnie:",
    "insights.primaryRecommendation": "Rekomendacja",
    "insights.sourcesTitle": "Źródła (RAG)",
    "insights.sourcesLead":
      "Fragmenty bazy wiedzy użyte w tej analizie. Pusto, gdy nie znaleziono dobrego dopasowania",
    "insights.sourcesEmpty":
      "Brak fragmentów z bazy wiedzy przy tej analizie (słabe trafienia lub pusty indeks)",
    "insights.sourceUntitled": "Źródło bez tytułu",
    "insights.sourceRank": ({ rank }) => `#${rank}`,
    "insights.sourceScore": ({ score }) => `wynik ${score}`,
    "insights.sourcesCompactTitle": "Źródła",
    "insights.sourcesCaption": "Źródła bazy wiedzy użyte w tej analizie",
    "insights.sourceColTopic": "Temat",
    "insights.sourceColType": "Typ",
    "insights.sourceColLink": "Link",
    "insights.sourceColConfidence": "Pewność",
    "insights.sourceOpenLink": "Otwórz",
    "insights.sourcesEmptyShort": "Brak wierszy źródeł w tym uruchomieniu",

    "aside.aiRecommendations": "Rekomendacje AI",
    "aside.aiTitle": "Rekomendacje",
    "aside.systemStatus": "Status systemu",
    "aside.todayOps": "Operacje dnia",
    "aside.unscheduledMeta": "Nadal wymaga godziny albo świadomej decyzji",
    "aside.savedLocally": "Zapisano lokalnie",
    "aside.localState": "Stan lokalny aktualizuje się przy każdej zmianie",
    "aside.modelAssisted": "Wspomagane modelem",
    "aside.beforeNoon": ({ title }) =>
      `${title}. Nadaj temu priorytet przed południem`,
    "aside.consistency":
      "Utrzymaj regularność: jedno strategiczne działanie i jeden nawyk",
    "aside.weeklyFocus": "Fokus tygodnia",
    "aside.weeklyFocusLead":
      "Przy planowaniu jutra zacznij od celów poniżej 60% realizacji",
    "aside.planningRule": "Zasada planowania",
    "aside.planningRuleLead":
      "Na jeden dzień utrzymuj maksymalnie 3 strategiczne cele i 2 wspierające nawyki",
    "logic.strongExecution": "Mocny rytm wykonania",
    "logic.balancedExecution":
      "Stabilne wykonanie z miejscem na jeszcze jeden mocny ruch",
    "logic.lowExecution":
      "Niska realizacja dzisiaj. Ogranicz zakres i zabezpiecz jeden blok skupienia jutro",
    "logic.recapWithFeedback": ({ done, total, percent, cadence, feedback }) =>
      `Ukończono ${done}/${total} działań (${percent}%). ${cadence} Twoja notatka mówi: "${feedback}". Rekomendacja AI: zachowaj pierwsze 90 minut jutra na najbardziej strategiczny cel i zaplanuj jeden nawyk regeneracyjny wieczorem`,
    "logic.recapWithoutFeedback": ({ done, total, percent, cadence }) =>
      `Ukończono ${done}/${total} działań (${percent}%). ${cadence} Dodaj krótką refleksję, aby jutro poprawić jakość rekomendacji`,
    "logic.pushGoal": ({ title, amount, unit }) =>
      `Dopchnij "${title}" o ${amount} ${unit}`,
    "recommendation.none":
      "Brak rekomendacji na teraz. Trzymaj obecny plan i chroń czas skupienia",
    "recommendation.apply": "Zastosuj w planie",
    "recommendation.dismiss": "Odrzuć",
    "goalScope.global": "Długoterminowy",
    "goalScope.year": "Roczny",
    "goalScope.month": "Miesięczny",
    "goalScope.week": "Tygodniowy",
    "goalDetails.back": "Wstecz",
    "goalDetails.close": "Zamknij",
    "goalDetails.completed": "Ukończony",
    "goalDetails.loadError": "Nie udało się wczytać tego celu",
    "goalDetails.loadingNotes": "Wczytywanie notatek…",
    "goalDetails.notes": "Notatki",
    "goalDetails.parent": ({ title }) => `Część celu „${title}”`,
    "goalDetails.breakdown": "Rozbicie postępu",
    "goalDetails.loadingChildren": "Wczytywanie elementów…",
    "goalDetails.noChildren": "Brak bezpośrednich elementów",
    "goalDetails.active": "Aktywny",
    "goalDetails.contributed": "Wniesiono wkład",
    "goalDetails.dailyItem": "Element dzienny",
    "goalDetails.habit": "Nawyk",
    "goalNotes.edit": "Edytuj",
    "goalNotes.preview": "Podgląd",
    "goalNotes.saving": "Zapisywanie…",
    "goalNotes.saved": "Zapisano",
    "goalNotes.saveError": "Nie udało się zapisać notatek",
    "goalNotes.placeholder": "Dodaj kontekst do tego celu. Wpisz @, aby powiązać inny cel.",
    "goalNotes.emptyPreview": "Nic tu jeszcze nie ma. Przełącz na Edytuj, aby dodać notatki.",
    "goalNotes.mentionSearch": "Szukaj celów",
    "goalNotes.noGoalsFound": "Nie znaleziono celów",
    "goalNotes.mentionUnavailable": "Ten cel nie jest już dostępny",
    "goalNotes.mentionedIn": "Wspomniano w",
    "goalNotes.editorHint": "Pisz naturalnie · użyj / dla bloków · @ dla celów",
    "goalNotes.commandSearch": "Polecenia notatki",
    "goalNotes.noCommandsFound": "Nie znaleziono poleceń",
    "goalNotes.commandText": "Tekst",
    "goalNotes.commandHeading1": "Nagłówek 1",
    "goalNotes.commandHeading2": "Nagłówek 2",
    "goalNotes.commandHeading3": "Nagłówek 3",
    "goalNotes.commandTodo": "Lista zadań",
    "goalNotes.commandBulletList": "Lista punktowana",
    "goalNotes.commandOrderedList": "Lista numerowana",
    "goalNotes.commandQuote": "Cytat",
    "goalNotes.commandCodeBlock": "Blok kodu",
    "goalNotes.commandDivider": "Separator",
    "goalNotes.taskCheckbox": "Element listy zadań",
    "goalNotes.untitledTask": "Zadanie bez nazwy",
  },
  uk: {
    "app.site": "Навігація",
    "app.brandTag": "Система щоденної дисципліни",
    "app.today": "Сьогодні",
    "app.week": "Довгі плани",
    "app.calendar": "Календар",
    "app.planning": "Кімната планування",
    "app.insights": "AI-аналітика",
    "app.coach": "Dio",
    "app.title.coach": "Disciplio · Dio",
    "app.collapseNavigation": "Згорнути навігацію",
    "app.expandNavigation": "Розгорнути навігацію",
    "app.resizeNavigation": "Змінити ширину навігації",
    "app.scroll": "Прокрутка",
    "app.language": "Мова",
    "sessionLoad.label": "Завантаження акаунта",
    "sessionLoad.title": "Не вдалося завантажити акаунт",
    "sessionLoad.description":
      "Перевір підключення й спробуй ще раз. Твоє прийняття політик не змінилося.",
    "sessionLoad.rateLimited": ({ seconds }) =>
      `Забагато спроб. Спробуй ще раз через ${seconds} с.`,
    "sessionLoad.retry": "Спробувати ще раз",
    "browserStorage.title": "Cookies і пам’ять",
    "browserStorage.description":
      "Збережи незавершене налаштування та вподобання на цьому пристрої, а також — лише за твоєю згодою — вмикай аналітику продукту, що поважає приватність, і необов’язкове відтворення сесій у застосунку, аби допомогти нам зрозуміти використання функцій і покращувати Disciplio. Ми ніколи не збираємо текст твоїх завдань, цілей, рефлексій чи розмов зі ШІ.",
    "browserStorage.learnMore": "Дізнатися більше",
    "browserStorage.accept": "Дозволити",
    "browserStorage.reject": "Відхилити",
    "browserStorage.acceptAria": "Дозволити необов’язкове сховище",
    "browserStorage.rejectAria": "Відхилити необов’язкове сховище",
    "browserStorage.preferencesTitle": "Налаштування сховища браузера",
    "browserStorage.preferencesAccepted":
      "Необов’язкове сховище браузера та аналітика, що поважає приватність, зокрема відтворення сесій, якщо його ввімкнено, активні на цьому пристрої.",
    "browserStorage.preferencesRejected":
      "Необов’язкове сховище браузера та аналітика вимкнені на цьому пристрої.",
    "browserStorage.change": "Змінити налаштування",
    "ai.recommendationDisclaimer":
      "Рекомендація ШІ · може бути неточною та не має обов’язкової сили",
    "ai.generatedDisclaimer":
      "Згенеровано ШІ · може бути неточним і не має обов’язкової сили",
    "ai.recommendationLabel": "Рекомендація ШІ",
    "aiAllowance.status": ({ feature, remaining, limit, period, reset }) =>
      `Безкоштовний ШІ для ${feature}: ${remaining}/${limit} залишилось ${period} · оновлення ${reset}`,
    "aiAllowance.feature.REFLECTION_ADVICE": "Порад для рефлексії",
    "aiAllowance.feature.INSIGHTS_DAILY": "Інсайтів ШІ",
    "aiAllowance.feature.PLANNING_TASK_SUGGESTION": "Пропозицій для планування",
    "aiAllowance.feature.COACH_GENERATION": "Повідомлень Coach Dio",
    "aiAllowance.feature.COACH_TRANSCRIPTION": "Голосових транскрипцій Dio",
    "aiAllowance.today": "сьогодні",
    "aiAllowance.thisWeek": "цього тижня",
    "aiAllowance.later": "пізніше",
    "aiLimits.title": "Ліміти ШІ",
    "aiLimits.description": "Ліміти тарифу Free для кожної функції ШІ.",
    "aiLimits.row": ({ remaining, limit, period, reset }) =>
      `${remaining}/${limit} залишилось ${period} · оновлення ${reset}`,
    "aiLimits.unlimited": "Безлімітно локально",
    "aiLimits.unavailable": "Ліміти ШІ наразі недоступні.",
    "app.quickActions": "Швидкі дії",
    "app.settings": "Налаштування",
    "app.account": "Акаунт",
    "app.profileEmpty": "Додай свою карту фокусу",
    "app.accountFallbackName": "Налаштуй свій профіль",
    "completion.taskMessage": ({ title }) => `Завдання виконано: «${title}»`,
    "completion.goalMessage": ({ title }) => `Ціль виконано: «${title}»`,
    "onboarding.aria": "Чекліст налаштування Disciplio",
    "onboarding.label": "Твій шлях до моменту aha",
    "onboarding.title": "Основний процес продукту",
    "onboarding.account": "Зареєструватися або увійти",
    "onboarding.profile": "Завершити онбординг",
    "onboarding.progress": ({ completed, total }) =>
      `${completed} з ${total} завершено`,
    "onboarding.open": "Відкрити наступний крок",
    "onboarding.verify": "Перевірити збережені дані",
    "onboarding.clickHere": "Натисни тут, щоб почати",
    "onboarding.moveGuide": "Перемістити чекліст",
    "onboarding.collapse": "Згорнути чекліст",
    "onboarding.expand": "Розгорнути чекліст",
    "onboarding.pause": "Призупинити підказки",
    "onboarding.resume": "Продовжити гід",
    "onboarding.skip": "Пропустити чекліст",
    "onboarding.skipTitle": "Пропустити чекліст налаштування?",
    "onboarding.skipDescription":
      "Ти впевнений? Пропускай лише якщо вже знаєш весь процес Disciplio. Ти зможеш користуватися застосунком як завжди, але цей чекліст більше не з’явиться.",
    "onboarding.skipCancel": "Залишити чекліст",
    "onboarding.skipConfirm": "Так, пропустити онбординг",
    "onboarding.skipFailed": "Не вдалося пропустити чекліст. Спробуй ще раз.",
    "onboarding.statusSkipped": "Пропущено",
    "onboarding.statusDone": "Готово",
    "onboarding.statusNext": "Далі",
    "onboarding.step.globalGoal.title": "Уточни глобальну ціль",
    "onboarding.step.globalGoal.description":
      "Додай довгостроковий результат або перейменуй зареєстровану ціль.",
    "onboarding.step.yearGoal.title": "Створи річну ціль",
    "onboarding.step.yearGoal.description":
      "Створи річну ціль і пов’яжи її з однією з глобальних цілей.",
    "onboarding.step.monthlyPriority.title": "Обери місячний пріоритет",
    "onboarding.step.monthlyPriority.description":
      "Перетвори річну ціль на один конкретний пріоритет цього місяця.",
    "onboarding.step.weeklyGoal.title": "Встанови вимірювану тижневу ціль",
    "onboarding.step.weeklyGoal.description":
      "Обери показник і одиницю, а потім пов’яжи їх із місячним пріоритетом.",
    "onboarding.step.todayPlan.title": "Сплануй сьогодні",
    "onboarding.step.todayPlan.description":
      "Додай завдання на сьогодні, вибери для нього вимірювану тижневу ціль і вкажи додатний внесок.",
    "onboarding.step.dailyTask.title": "Виконай щоденне завдання",
    "onboarding.step.dailyTask.description":
      "Повернися до розділу «Сьогодні» та познач будь-яке завдання зі списку виконаним.",
    "onboarding.step.streak.title": "Дізнайся про свою серію Disciplio",
    "onboarding.step.streak.description":
      "Серія рахує послідовні активні дні з виконаним завданням або звичкою. Відкрий картку серії, щоб побачити ланцюг і дізнатися, як його підтримувати.",
    "onboarding.step.reflection.title": "Надішли рефлексію",
    "onboarding.step.reflection.description":
      "Напиши одну чесну нотатку про день і збережи її.",
    "onboarding.step.aiRecommendation.title": "Отримай корисну рекомендацію",
    "onboarding.step.aiRecommendation.description":
      "Збережи рефлексію та переглянь рекомендацію або свідомо пропусти AI.",
    "onboarding.step.return.title": "Повернися та перевір",
    "onboarding.step.return.description":
      "Перезавантаж Disciplio, щоб підтвердити, що твою роботу збережено.",
    "onboarding.step.aiConsent.title": "Обери налаштування ШІ",
    "onboarding.step.aiConsent.description":
      "Виріши, чи може Disciplio генерувати додаткові рекомендації перед початком дня.",
    "aiConsent.label": "Перед взаємодією з ШІ",
    "aiConsent.title": "Disciplio може генерувати додаткові рекомендації",
    "aiConsent.description":
      "Ця необов’язкова функція використовує ШІ. Вибрані цілі, завдання, плани, нотатки, рефлексії та інший доречний контекст Disciplio можуть бути надіслані зовнішнім постачальникам ШІ. Disciplio наразі використовує OpenRouter, щоб спрямовувати ці запити до вибраних постачальників моделей ШІ. Ваш e-mail не включається. Результат може бути неточним або непридатним і не є професійною порадою. Його можна редагувати, відхилити, ігнорувати чи видалити; ви відповідаєте за рішення та можете користуватися Disciplio без ШІ.",
    "aiConsent.enable": "Увімкнути ШІ",
    "aiConsent.decline": "Продовжити без ШІ",
    "app.title.today": "Disciplio · Сьогодні",
    "app.title.week": "Disciplio · Довгі плани",
    "app.title.calendar": "Disciplio · Календар",
    "app.title.planning": "Disciplio · Кімната планування",
    "app.title.insights": "Disciplio · AI-аналітика",
    "coach.brand": "Dio",
    "coach.brandSub": "AI COACH",
    "coach.newChat": "Новий чат",
    "coach.dashboard": "Сьогодні",
    "coach.recent": "Останні",
    "coach.history": "Історія розмов",
    "coach.collapseHistory": "Згорнути історію розмов",
    "coach.expandHistory": "Розгорнути історію розмов",
    "coach.noConversations": "Тут з’являться твої розмови.",
    "coach.greeting": "Привіт, я Dio. Над чим сьогодні працюємо?",
    "coach.emptyHint": "Я бачу твої цілі, плани й рефлексії. Запитай про цей тиждень, про рішення, яке тебе гальмує, або про те, що постійно відкладається.",
    "coach.placeholder": "Запитай Dio про що завгодно…",
    "coach.send": "Надіслати повідомлення",
    "coach.stopResponse": "Зупинити відповідь",
    "coach.thinking": "Dio думає",
    "coach.retry": "Спробувати ще раз",
    "coach.delete": "Видалити розмову",
    "coach.deleteFailed": "Dio не зміг видалити цю розмову. Її відновлено.",
    "coach.historyRefreshFailed": "Розмову видалено, але Dio не зміг оновити історію.",
    "coach.confirmDeleteGoalPrompt": "«{title}» — довгострокова ціль. Видалити її?",
    "coach.confirmDeleteHabitPrompt": "Видалити всі повторення «{title}»?",
    "coach.confirmDeletePlanItemPrompt": "Видалити елемент плану «{title}»?",
    "coach.confirmBatchDeletePrompt": "Dio знайшов {count} змін, які потребують підтвердження:",
    "coach.confirmDeleteButton": "Видалити",
    "coach.cancelDeleteButton": "Залишити",
    "coach.taskProposalPrompt": "Dio підготував це завдання на основі вашого запиту. Додати його до плану?",
    "coach.taskProposalDetails": "Перед додаванням можна змінити назву, додати час або пов’язати завдання з тижневою ціллю.",
    "coach.taskProposalTitle": "Назва",
    "coach.taskProposalStart": "Початок",
    "coach.taskProposalEnd": "Кінець",
    "coach.taskProposalWeeklyGoal": "Тижнева ціль (необов’язково)",
    "coach.noWeeklyGoal": "Без тижневої цілі",
    "coach.taskProposalDate": "Дата: {date}",
    "coach.addProposedTask": "Додати завдання",
    "coach.skipProposedTask": "Не зараз",
    "coach.capabilities.open": "Показати, що вміє Dio",
    "coach.capabilities.tooltip": "Натисни, щоб переглянути всі доступні функції Dio.",
    "coach.capabilities.title": "Що вміє Dio",
    "coach.capabilities.description": "Сьогодні Dio може виконувати ці дії в Disciplio.",
    "coach.capabilities.close": "Закрити список можливостей Dio",
    "coach.capabilities.tasks.title": "Щоденні завдання",
    "coach.capabilities.tasks.description": "Створювати, редагувати, завершувати, переміщувати, дублювати й видаляти завдання.",
    "coach.capabilities.goals.title": "Довгострокові цілі",
    "coach.capabilities.goals.description": "Створювати, редагувати, завершувати, відновлювати й видаляти глобальні або річні цілі.",
    "coach.capabilities.plans.title": "Місячні та тижневі плани",
    "coach.capabilities.plans.description": "Створювати, редагувати, завершувати, відновлювати й видаляти елементи планів.",
    "coach.capabilities.batch.title": "Пакетні зміни",
    "coach.capabilities.batch.description": "Послідовно виконувати до 10 підтримуваних змін з одного повідомлення.",
    "coach.capabilities.coaching.title": "Коучинг і рефлексія",
    "coach.capabilities.coaching.description": "Відповідати на питання про твої цілі, плани, завдання та рефлексії в Disciplio.",
    "coach.capabilities.voice.title": "Голосові повідомлення",
    "coach.capabilities.voice.description": "Транскрибувати голосове повідомлення, щоб Dio міг з ним працювати.",
    "coach.capabilities.confirmations.title": "Безпечні підтвердження",
    "coach.capabilities.confirmations.description": "Просити підтвердження перед деструктивним видаленням і залишати рішення за тобою.",
    "coach.capabilities.limitations.title": "Dio зосереджений на Disciplio",
    "coach.capabilities.limitations.description": "Dio не надсилає електронні листи й не виконує зовнішні дії.",
    "coach.batchActionsCompleted": "Виконані зміни:",
    "coach.batchActionFailed": "Не вдалося виконати: {action}",
    "coach.batchActionPending": "Очікує на підтвердження: {action}",
    "coach.actionLabel.planningChange": "цю зміну в плані",
    "coach.actionLabel.listTasks": "переглянути завдання",
    "coach.actionLabel.createTask": "створити завдання",
    "coach.actionLabel.updateTask": "оновити завдання",
    "coach.actionLabel.completeTask": "завершити завдання",
    "coach.actionLabel.deleteTask": "видалити завдання",
    "coach.actionLabel.moveTask": "перемістити завдання",
    "coach.actionLabel.duplicateTask": "дублювати завдання",
    "coach.actionLabel.listGoals": "переглянути цілі",
    "coach.actionLabel.createGoal": "створити ціль",
    "coach.actionLabel.updateGoal": "оновити ціль",
    "coach.actionLabel.completeGoal": "завершити ціль",
    "coach.actionLabel.deleteGoal": "видалити ціль",
    "coach.actionLabel.listMonthlyPlan": "переглянути місячний план",
    "coach.actionLabel.createMonthlyPlanItem": "створити елемент місячного плану",
    "coach.actionLabel.updateMonthlyPlanItem": "оновити елемент місячного плану",
    "coach.actionLabel.completeMonthlyPlanItem": "завершити елемент місячного плану",
    "coach.actionLabel.deleteMonthlyPlanItem": "видалити елемент місячного плану",
    "coach.actionLabel.listWeeklyPlan": "переглянути тижневий план",
    "coach.actionLabel.createWeeklyPlanItem": "створити елемент тижневого плану",
    "coach.actionLabel.updateWeeklyPlanItem": "оновити елемент тижневого плану",
    "coach.actionLabel.completeWeeklyPlanItem": "завершити елемент тижневого плану",
    "coach.actionLabel.deleteWeeklyPlanItem": "видалити елемент тижневого плану",
    "coach.actionCancelled": "Добре, залишаю без змін.",
    "coach.actionConfirmedTask": "Додано «{title}» до вашого плану.",
    "coach.actionCancelledTask": "Добре — я не додав цю пропозицію.",
    "coach.actionFailedTask": "Не вдалося додати завдання, тому план не змінено.",
    "coach.actionConfirmedDeleteGoal": "Видалено «{title}».",
    "coach.actionConfirmedDeleteHabit": "Видалено «{title}» (усі повторення).",
    "coach.actionConfirmedDeletePlanItem": "Видалено «{title}».",
    "coach.actionFailedTargetGone": "Цього вже немає — можливо, воно вже змінилося. Нічого не видалено.",
    "coach.free": "Безкоштовний",
    "coach.fast": "Швидкий",
    "coach.moreThinking": "Більше роздумів",
    "coach.freeHint": "Щоденні питання",
    "coach.fastHint": "Коротші, швидші відповіді",
    "coach.moreThinkingHint": "Складніші рішення, повільніше",
    "coach.effortLabel": "Профіль відповіді",
    "coach.effortAria": "Обрати профіль відповіді",
    "coach.startVoice": "Записати голосове повідомлення",
    "coach.stopVoice": "Зупинити запис",
    "coach.transcribing": "Транскрибування…",
    "coach.voiceUnsupported": "Запис голосу не підтримується в цьому браузері.",
    "coach.voicePermission": "Дозволь доступ до мікрофона, щоб записати повідомлення.",
    "coach.voiceTooLong": "Запис був надто довгим. Спробуй коротше повідомлення.",
    "coach.voiceTooLarge": "Запис завеликий. Спробуй коротше повідомлення.",
    "coach.voiceUnavailable": "Транскрипція голосу тимчасово недоступна. Спробуй ще раз або напиши повідомлення.",
    "coach.voiceFailed": "Не вдалося транскрибувати голос. Спробуй ще раз або напиши повідомлення.",
    "coach.streamFailed": "Dio не зміг завершити відповідь.",
    "coach.actionCommitted": "Зміну збережено.",
    "coach.authRequired": "Сесія завершилася. Увійди знову, щоб продовжити розмову з Dio.",
    "coach.logIn": "Увійти",
    "coach.responseStopped": "Відповідь зупинено. Спробуй ще раз, коли будеш готовий.",
    "coach.retryLimit": "Ця відповідь досягла ліміту повторних спроб.",
    "coach.invalidRequest": "Цей запит Coach некоректний. Перевір повідомлення й спробуй ще раз.",
    "coach.quota": "Ліміт Coach наразі використано.",
    "coach.rateLimited": ({ seconds }) => `Dio тимчасово обмежений. Спробуй ще раз через ${seconds} с.`,
    "coach.rateLimitedReady": "Dio був тимчасово обмежений. Тепер можна спробувати ще раз.",
    "coach.consent": "Прийми використання ШІ в акаунті, щоб спілкуватися з Dio.",
    "coach.openAccount": "Відкрити налаштування ШІ в акаунті",
    "coach.disclaimer": "ШІ може помилятися. Перевіряй важливі деталі у своїх планах Disciplio.",
    "coach.copyAnswer": "Копіювати відповідь",
    "coach.copiedAnswer": "Відповідь скопійовано.",
    "coach.copyAnswerError": "Не вдалося скопіювати відповідь.",
    "coach.feedback.title": "Розкажи про цю відповідь",
    "coach.feedback.positive": "Корисна",
    "coach.feedback.negative": "Некорисна",
    "coach.feedback.prompt": "Що вплинуло на твою оцінку? (необов’язково)",
    "coach.feedback.reasonLabel": "Необов’язкова примітка",
    "coach.feedback.reasonPlaceholder": "Додай коротку примітку…",
    "coach.feedback.send": "Надіслати відгук",
    "coach.feedback.sending": "Надсилання…",
    "coach.feedback.close": "Закрити",
    "coach.feedback.change": "Змінити відгук",
    "coach.feedback.thanks": "Дякуємо за відгук.",
    "coach.feedback.error": "Не вдалося зберегти відгук. Спробуй ще раз.",
    "coach.timestamp.answer": "Відповідь написано",
    "coach.timestamp.yesterday": "Вчора",
    "coach.scrollLatest": "Прокрутити до останніх",
    "coach.mobileHistory": "Відкрити історію розмов",
    "coach.closeHistory": "Закрити історію розмов",
    "coach.starter.plan": "Сплануй мій тиждень",
    "coach.starter.decision": "Обдумай рішення",
    "coach.starter.review": "Переглянь прогрес",
    "app.title.account": "Disciplio · Акаунт",
    "app.title.welcome": "Disciplio · Доступ і онбординг",
    "app.title.login": "Disciplio · Вхід",
    "app.title.ragAdmin": "Disciplio · Адмін RAG",
    "app.title.aiUsageAdmin": "Disciplio · Використання AI",
    "app.skipToMain": "Перейти до основного вмісту",
    "welcome.logInShort": "Увійти",
    "welcome.logInAria": "Вже є акаунт? Увійти",
    "welcome.kicker": "Доступ + онбординг",
    "welcome.heroTitle": "Налаштуй свій профіль Disciplio",
    "welcome.heroLead":
      "Додай дані акаунта й контекст фокусу, які Disciplio використовує для планування та зворотного зв'язку. Профіль можна уточнити пізніше",
    "welcome.liveMap": "Жива карта профілю",
    "welcome.futureProfile": "Твій майбутній профіль Disciplio",
    "welcome.identityFallback": "Ідентичність і напрям з'являться тут",
    "welcome.rolesLabel": "Ролі",
    "welcome.rolesEmpty": "Додай ролі через кому, щоб побудувати контекст",
    "welcome.globalGoals": "Глобальні цілі",
    "welcome.noGoalsYet": "Цілей поки немає",
    "welcome.focusInputs": "Джерела фокусу",
    "welcome.focusInputsEmpty": "Інтереси та проблемні зони з'являться тут",
    "welcome.recoveryLifestyle": "Відновлення + стиль життя",
    "welcome.recoveryEmpty": "Хобі та потреби підтримки з'являться тут",
    "welcome.profileLabel": "Профіль",
    "welcome.startLabel": "Почни тут",
    "welcome.updateTitle": "Онови профіль",
    "welcome.createTitle": "Створи профіль",
    "welcome.fullNameLabel": "Повне ім'я",
    "welcome.fullNamePlaceholder": "напр. Alex Morgan",
    "welcome.roleLabel": "Ролі / етап життя",
    "welcome.rolePlaceholder": "напр. студент, засновник, батько / мати",
    "welcome.emailLabel": "Email",
    "welcome.emailPlaceholder": "you@example.com",
    "welcome.emailFormatHint": "Введи email у форматі name@example.com",
    "welcome.passwordLabel": "Пароль",
    "welcome.passwordForAccessLabel": "Пароль доступу",
    "welcome.passwordPlaceholder": "Щонайменше 8 символів",
    "welcome.goalsPlaceholder":
      "напр. завершити навчання, побудувати бізнес, покращити здоров'я",
    "welcome.goalsHelp": "Ваші цілі бачите лише ви",
    "welcome.tagInputHint": "Натисни Enter або введи кому, щоб додати елемент.",
    "welcome.suggested": "Пропозиції",
    "welcome.removeTag": ({ tag }) => `Видалити ${tag}`,
    "welcome.interestsLabel": "Інтереси",
    "welcome.interestsPlaceholder": "напр. AI, фітнес, психологія",
    "welcome.interestsHelp":
      "Теми, розділені комою або новим рядком, які природно притягують твою увагу",
    "welcome.problemsLabel": "Проблемні зони",
    "welcome.problemsPlaceholder":
      "напр. прокрастинація, відволікання, несталі звички",
    "welcome.problemsHelp":
      "Місця, де система зазвичай ламається. Необов'язково — якщо залишити порожнім, Disciplio допоможе виявити вузькі місця згодом, коли ви користуватиметесь застосунком",
    "welcome.hobbiesLabel": "Хобі",
    "welcome.hobbiesPlaceholder": "напр. біг, читання, фотографія",
    "welcome.hobbiesHelp":
      "Корисно для балансу, відновлення та ідентичності поза чистим результатом",
    "welcome.needsLabel": "У чому система має допомагати?",
    "welcome.needsPlaceholder":
      "напр. планувати дні, тримати фокус, відновлюватися після невдач",
    "welcome.needsHelp":
      "Вхідні дані, навколо яких система має оптимізувати роботу",
    "welcome.commitmentLabel": "Тижневе навантаження",
    "welcome.commitmentPlaceholder":
      "напр. 8 годин фокусу, 3 тренування, 2 навчальні сесії",
    "welcome.commitmentHelp":
      "Disciplio використовує це як межу тижневої місткості, щоб уникати перепланування та тримати план реалістичним",
    "welcome.heardAboutUsLabel": "Звідки ви дізналися про нас? (необов’язково)",
    "welcome.heardAboutUsPlaceholder":
      "наприклад, від друга, із соцмереж або пошуку",
    "welcome.errorName":
      "Додай повне ім'я, щоб Disciplio міг персоналізувати профіль",
    "welcome.errorEmail":
      "Додай email, щоб створити або продовжити доступ до Disciplio",
    "welcome.errorEmailFormat":
      "Введи коректний email, наприклад name@example.com",
    "welcome.errorExistingAccount":
      "Акаунт із цією адресою email уже існує в Disciplio. Увійди.",
    "welcome.errorPasswordTooShort": ({ count }) =>
      `Використай пароль щонайменше з ${count} символів`,
    "welcome.errorPasswordTooLong": ({ count }) =>
      `Використай не більше ${count} символів`,
    "welcome.errorPasswordReused":
      "Використай пароль, якого ще не було в цьому акаунті.",
    "welcome.errorRegistration":
      "Не вдалося створити акаунт. Перевір дані й спробуй ще раз.",
    "welcome.errorContext":
      "Додай щонайменше один інтерес і одну глобальну ціль, щоб система мала реальний контекст",
    "welcome.errorSave": "Не вдалося зберегти профіль. Спробуй ще раз.",
    "welcome.errorTerms":
      "Прийми Умови використання та Політику конфіденційності, щоб створити акаунт",
    "welcome.saveAndReturn": "Зберегти профіль і повернутися",
    "welcome.enterDisciplio": "Увійти в Disciplio",
    "welcome.termsAgreementPrefix": "Я погоджуюся з",
    "welcome.termsLabel": "Умовами",
    "welcome.privacyAgreementPrefix": "Я прочитав(-ла)",
    "welcome.privacyLabel": "повідомлення про приватність",
    "plan.toggleAria": ({ title, done }) =>
      `${title}, ${done ? "виконано" : "не виконано"}`,

    "lang.en": "English",
    "lang.pl": "Polski",
    "lang.uk": "Українська",
    "lang.enShort": "EN",
    "lang.plShort": "PL",
    "lang.ukShort": "UA",

    "login.label": "Доступ",
    "login.title": "Увійти",
    "login.heroTitle": "Повернись до своєї системи дисципліни",
    "login.heroLead":
      "Відкрий той самий профіль, мову, плани й цілі без повторного налаштування",
    "login.proofLabel": "Переваги входу",
    "login.localOnly": "Той самий акаунт",
    "login.localOnlyText": "Увійди тією ж поштою, щоб знову відкрити профіль",
    "login.profileReady": "Профіль готовий",
    "login.profileReadyText":
      "Плани й налаштування доступні одразу після входу",
    "login.fastReturn": "Швидке повернення",
    "login.fastReturnText": "Повернись саме туди, де зупинився",
    "login.password": "Пароль",
    "login.showPassword": "Показати пароль",
    "login.hidePassword": "Приховати пароль",
    "login.submit": "Увійти",
    "login.create": "Створити профіль",
    "login.noAccount":
      "На цьому пристрої ще немає збереженого акаунта. Спочатку створи профіль",
    "login.invalid": "Email або пароль неправильні",
    "auth.rateLimited": ({ seconds }) =>
      `Забагато спроб — спробуй ще раз через ${seconds} с`,
    "auth.serverUnavailable":
      "Сервер не відповів. Ми вже працюємо над цим — спробуй ще раз за кілька хвилин.",
    "login.attemptPolicy":
      "Після 5 неправильних спроб вхід буде призупинено на 15 хвилин.",
    "login.forgotPassword": "Забув(ла) пароль?",
    "login.throttled":
      "Забагато спроб входу. Спробуй ще раз приблизно через 15 хвилин.",
    "resetPassword.label": "Безпечне відновлення доступу",
    "resetPassword.title": "Установи новий пароль",
    "resetPassword.heroTitle": "Безпечно віднови доступ",
    "resetPassword.heroLead":
      "Це посилання працює лише один раз і скоро спливає. Новий пароль вийде з усіх активних сесій.",
    "resetPassword.newPassword": "Новий пароль",
    "resetPassword.confirmPassword": "Підтверди новий пароль",
    "resetPassword.tooShort": ({ count }) =>
      `Використай пароль щонайменше з ${count} символів`,
    "resetPassword.tooLong": ({ count }) =>
      `Використай не більше ${count} символів`,
    "resetPassword.reused":
      "Використай пароль, якого ще не було в цьому акаунті.",
    "resetPassword.match": "Нові паролі не збігаються",
    "resetPassword.invalid":
      "Це посилання недійсне, прострочене або вже використане.",
    "resetPassword.invalidTitle": "Посилання недійсне або відсутнє",
    "resetPassword.requestNew": "Запросити нове посилання",
    "resetPassword.submit": "Зберегти новий пароль",
    "resetPassword.saving": "Збереження…",
    "resetPassword.back": "Назад до входу",
    "resetPassword.successTitle": "Пароль оновлено",
    "resetPassword.successLead":
      "Інші сесії вийшли з акаунта. Увійди з новим паролем.",
    "resetPassword.login": "Перейти до входу",

    "verifyEmail.label": "Підтвердь свою електронну пошту",
    "verifyEmail.heroTitle": "Ще один крок",
    "verifyEmail.heroLead":
      "Введи шестизначний код, який ми надіслали, щоб завершити створення акаунта.",
    "verifyEmail.title": "Введи код підтвердження",
    "verifyEmail.sentTo": ({ email }) =>
      `Ми надіслали код на ${email}. Перевір папки Спам і Промоакції, якщо не бачиш листа.`,
    "verifyEmail.codeLabel": "Код підтвердження",
    "verifyEmail.submit": "Підтвердити",
    "verifyEmail.verifying": "Підтвердження…",
    "verifyEmail.resend": "Надіслати код ще раз",
    "verifyEmail.resendCooldown": ({ seconds }) =>
      `Повторне надсилання через ${seconds}с`,
    "verifyEmail.hint": "Працює лише найновіший код. Коди діють обмежений час.",
    "verifyEmail.invalidCode":
      "Цей код неправильний або застарів. Запроси новий.",
    "verifyEmail.missingEmail":
      "Ми не знайшли очікувану реєстрацію для цієї сесії. Увійди або зареєструйся знову.",
    "verifyEmail.successTitle": "Електронну пошту підтверджено",
    "verifyEmail.successLead": "Твій акаунт тепер активний.",

    "app.title.register": "Disciplio · Реєстрація",
    "register.label": "Створити акаунт",
    "register.heroTitle": "Створи акаунт Disciplio",
    "register.heroLead":
      "Створи акаунт через Google або електронну пошту. Профіль налаштуєш одразу після цього.",
    "register.title": "Реєстрація",
    "register.divider": "або продовжити через Google",
    "register.submit": "Створити акаунт",
    "register.logInLink": "Увійти",

    "welcome.accountLabel": "Акаунт",
    "welcome.logOutShort": "Вийти",
    "welcome.logOutAria": "Вийти та повернутися до входу",

    "google.continueWithGoogle": "Продовжити через Google",
    "google.loading": "Завантаження входу через Google…",
    "google.unavailable": "Вхід через Google недоступний",
    "google.signInFailed": "Не вдалося увійти через Google",
    "google.accountNotFound":
      "Жоден акаунт Disciplio не пов'язаний із цим акаунтом Google. Створи акаунт або увійди за паролем.",
    "google.linkRequired":
      "Акаунт із цією електронною поштою вже існує; увійди за паролем і підключи Google у налаштуваннях акаунту",
    "google.connected": "Акаунт Google підключено",
    "google.disconnected": "Акаунт Google відключено",
    "google.onlyMethod": "Неможливо відключити єдиний спосіб входу",
    "google.emailMismatch":
      "Електронна пошта акаунту Google має збігатися з поштою акаунту Disciplio",
    "google.identityOwnedByAnotherUser":
      "Цей акаунт Google вже підключено до іншого акаунту Disciplio",

    "account.authMethodsTitle": "Способи входу",
    "account.authMethodsHelp": "Як ти можеш увійти в цей акаунт",
    "account.authMethodEmail": "Електронна пошта і пароль",
    "account.authMethodGoogle": "Google",
    "account.authMethodGoogleNotConnected": "Не підключено",
    "account.passwordSet": "Пароль встановлено",
    "account.noPasswordSet": "Пароль не встановлено",
    "account.setPasswordFirst": "Спочатку встанови пароль",
    "account.setPasswordLead":
      "Додай пароль, щоб входити без Google і керувати безпекою акаунту.",
    "account.setPasswordNew": "Новий пароль",
    "account.setPasswordConfirm": "Підтвердь новий пароль",
    "account.setPasswordSubmit": "Зберегти пароль",
    "account.setPasswordSaving": "Збереження…",
    "account.setPasswordCancel": "Скасувати",
    "account.setPasswordSuccess": "Пароль встановлено",
    "account.setPasswordFailed":
      "Не вдалося встановити пароль. Спробуй ще раз.",
    "account.disconnectGoogle": "Відключити Google",
    "account.deleteRequiresPassword":
      "Встанови пароль перед видаленням акаунту, щоб ми могли підтвердити, що це справді ти.",

    "forgotPassword.label": "Відновлення пароля",
    "forgotPassword.heroTitle": "Скинь пароль",
    "forgotPassword.heroLead":
      "Введи email свого акаунта, і якщо він існує, ми надішлемо одноразове посилання для скидання.",
    "forgotPassword.formTitle": "Забув(ла) пароль?",
    "forgotPassword.emailLabel": "Email",
    "forgotPassword.emailPlaceholder": "imya@example.com",
    "forgotPassword.invalidEmail": "Введи дійсну електронну адресу",
    "forgotPassword.submit": "Надіслати посилання для скидання",
    "forgotPassword.submitting": "Надсилання…",
    "forgotPassword.back": "Назад до входу",
    "forgotPassword.successTitle": "Перевір свою пошту",
    "forgotPassword.successLead":
      "Якщо акаунт із такою поштою існує, ми надіслали посилання для скидання пароля.",
    "forgotPassword.login": "Перейти до входу",

    "account.label": "Акаунт",
    "account.navLabel": "Меню акаунта",
    "account.navProfile": "Профіль",
    "account.navProfileHint": "Ідентичність і напрям",
    "account.navSettings": "Налаштування",
    "account.navSettingsHint": "Правила щоденної роботи",
    "account.navAi": "AI",
    "account.navAiHint": "Згода та використання",
    "account.navPrivacy": "Приватність і акаунт",
    "account.navPrivacyHint": "Право та доступ",
    "account.infoLabel": ({ label }) => `Більше інформації про: ${label}`,
    "account.backToMenu": "Меню акаунта",
    "account.aiTitle": "Рекомендації AI",
    "account.aiDescription":
      "Рекомендації можуть бути неточними й не є професійною порадою.",
    "account.aiEnabled": "Увімкнено",
    "account.aiDeclined": "Відхилено",
    "account.aiUndecided": "Не вирішено",
    "account.aiEnable": "Увімкнути AI",
    "account.aiDecline": "Вимкнути AI",
    "account.aiEnabledHelp": "Користуватися додатковими рекомендаціями AI.",
    "account.aiDeclinedHelp": "Залишити рекомендації AI вимкненими.",
    "account.aiEnabledStatus": "Рекомендації AI увімкнено.",
    "account.aiDisabledStatus":
      "Рекомендації AI вимкнено. Функції без AI залишаються доступними.",
    "account.aiUpdateFailed": "Не вдалося оновити налаштування AI.",
    "account.memoryTitle": "Пам’ять",
    "account.memoryDescription": "Переглядай інформацію, яку AI Disciplio може використовувати для доречніших відповідей і рекомендацій.",
    "account.memoryPrivacy": "Пам’ять належить твоєму акаунту. Вона може формувати контекст AI, але не змінює цілі, завдання чи рефлексії.",
    "account.memoryFilterLabel": "Видимість пам’яті",
    "account.memoryActive": "Активна",
    "account.memorySignals": "Сигнали",
    "account.memorySignalsDescription": "Це спостереження з твоїх планів, а не особисті факти. Dio може використовувати їх, щоб допомогти вирішити, що робити далі.",
    "account.memoryAll": "Уся",
    "account.memoryAdd": "Додати пам’ять",
    "account.memoryAddTitle": "Додати пам’ять",
    "account.memoryType": "Тип",
    "account.memorySummary": "Що Disciplio має запам’ятати?",
    "account.memorySummaryPlaceholder": "наприклад: найкраще зосереджуюся до обіду",
    "account.memorySave": "Зберегти пам’ять",
    "account.memorySaving": "Збереження…",
    "account.memoryCancel": "Скасувати",
    "account.memoryEdit": "Редагувати",
    "account.memorySaveEdit": "Зберегти",
    "account.memoryDelete": "Прибрати",
    "account.memoryRemoving": "Видалення…",
    "account.memoryRemoveConfirm": "Прибрати цю пам’ять?",
    "account.memoryReset": "Очистити всю пам’ять",
    "account.memoryResetConfirm": "Очистити всю збережену пам’ять? Цілі, завдання та рефлексії залишаться.",
    "account.memoryResetConfirmAction": "Очистити пам’ять",
    "account.memoryResetCancel": "Залишити пам’ять",
    "account.memoryResetting": "Очищення…",
    "account.memoryEmpty": "Активної пам’яті ще немає.",
    "account.memoryEmptySignals": "Активних сигналів прогресу ще немає.",
    "account.memoryEmptyAll": "Збереженої пам’яті ще немає.",
    "account.memoryLoading": "Завантаження пам’яті…",
    "account.memoryError": "Не вдалося завантажити пам’ять. Спробуй ще раз.",
    "account.memoryActionError": "Не вдалося змінити пам’ять. Спробуй ще раз.",
    "account.memoryYouAdded": "Додано тобою",
    "account.memoryFromCoach": "Ти сказав це Dio",
    "account.memorySignal": "Сигнал прогресу",
    "account.memoryLearned": "Вивчено",
    "account.memoryRemoved": "Прибрано",
    "account.memoryStatusResolved": "Вирішено",
    "account.memoryStatusStale": "Протерміновано",
    "account.memoryStatusDismissed": "Відхилено",
    "account.memoryStatusSuperseded": "Замінено",
    "account.memoryNeedsReview": "Потребує перевірки",
    "account.memoryTemporary": "Тимчасове",
    "account.memoryUntil": ({ date }) => `До ${date}`,
    "account.memoryReviewAfter": ({ date }) => `Перевір ще раз ${date}`,
    "account.memoryImport": "Імпорт з AI",
    "account.memoryFromChatGPT": "Імпортовано з ChatGPT",
    "account.memoryFromClaude": "Імпортовано з Claude",
    "account.memoryFromGemini": "Імпортовано з Gemini",
    "account.memoryFromOtherAI": "Імпортовано з іншого AI",
    "account.memoryEvidence": ({ count }) => count === 1 ? "1 сигнал" : `${count} сигналів`,
    "account.memoryLastObserved": ({ date }) => `Оновлено ${date}`,
    "account.memoryType.FACT": "Факт",
    "account.memoryType.PREFERENCE": "Уподобання",
    "account.memoryType.CONSTRAINT": "Обмеження",
    "account.memoryType.DECISION": "Рішення",
    "account.memoryType.BEHAVIOR_PATTERN": "Поведінковий патерн",
    "account.memoryType.PROGRESS_PATTERN": "Патерн прогресу",
    "account.memoryType.RECOMMENDATION_OUTCOME": "Результат рекомендації",
    "account.memoryType.RULE": "Правило",
    "account.memoryType.EXPERIMENT": "Експеримент",
    "account.memoryType.OPEN_LOOP": "Відкрите питання",
    "memoryImport.eyebrow": "ІМПОРТ ПАМ’ЯТІ",
    "memoryImport.title": "Перенеси свій контекст до Dio",
    "memoryImport.subtitle": "Обери AI-асистента, попроси його про контекст, який хочеш зберегти, а потім переглянь усе перед додаванням у пам’ять.",
    "memoryImport.stepsLabel": "Кроки імпорту пам’яті",
    "memoryImport.step.choose": "Обери асистента",
    "memoryImport.step.paste": "Встав відповідь",
    "memoryImport.step.review": "Переглянь і застосуй",
    "memoryImport.chooseLead": "У якого асистента є контекст, який ти хочеш перенести?",
    "memoryImport.assistant.chatgpt": "ChatGPT",
    "memoryImport.assistant.chatgptHelp": "Відкрий підготовлений запит або скопіюй його самостійно.",
    "memoryImport.assistant.claude": "Claude",
    "memoryImport.assistant.claudeHelp": "Відкрий Claude із автоматично скопійованим запитом.",
    "memoryImport.assistant.gemini": "Gemini",
    "memoryImport.assistant.geminiHelp": "Відкрий Gemini із автоматично скопійованим запитом.",
    "memoryImport.assistant.other": "Інший AI",
    "memoryImport.assistant.otherHelp": "Скопіюй запит у будь-якого асистента.",
    "memoryImport.securityNote": "Враховується лише контекст, про повернення якого ти попросиш асистента. Ніколи не додавай паролі, ключі, токени чи платіжні дані.",
    "memoryImport.selectedAssistant": ({ assistant }) => `Обрано: ${assistant}`,
    "memoryImport.loadingPrompt": "Підготовка запиту…",
    "memoryImport.open.chatgpt": "Відкрити в ChatGPT",
    "memoryImport.open.claude": "Скопіювати й відкрити Claude",
    "memoryImport.open.gemini": "Скопіювати й відкрити Gemini",
    "memoryImport.copyPrompt": "Скопіювати запит",
    "memoryImport.pasteAnswer": "Я маю відповідь",
    "memoryImport.promptError": "Не вдалося підготувати запит. Спробуй ще раз.",
    "memoryImport.copyError": "Не вдалося скопіювати запит. Спробуй ще раз або скопіюй його вручну.",
    "memoryImport.externalOpenError": "Не вдалося відкрити асистента. Запит усе ще готовий до копіювання.",
    "memoryImport.copied": "Запит скопійовано в буфер обміну.",
    "memoryImport.copiedGemini": "Запит скопійовано — встав його в Gemini.",
    "memoryImport.bringAnswerBack": "Поверни відповідь назад",
    "memoryImport.pasteLead": ({ assistant }) => `Встав нижче JSON-відповідь від ${assistant}. Нічого не буде збережено, доки ти не переглянеш і не застосуєш її.`,
    "memoryImport.pastePlaceholder.chatgpt": "Встав сюди відповідь ChatGPT…",
    "memoryImport.pastePlaceholder.claude": "Встав сюди відповідь Claude…",
    "memoryImport.pastePlaceholder.gemini": "Встав сюди відповідь Gemini…",
    "memoryImport.pastePlaceholder.other": "Встав сюди відповідь твого AI-асистента…",
    "memoryImport.pasteLabel": "JSON-відповідь асистента",
    "memoryImport.pastePrivacy": "Ця відповідь зберігається лише у відкритій сесії, доки ти не обереш «Застосувати». Вона не потрапляє в URL, аналітику чи логи.",
    "memoryImport.reviewAnswer": "Переглянути пам’ять",
    "memoryImport.back": "Назад",
    "memoryImport.reviewTitle": "Переглянь перед збереженням",
    "memoryImport.reviewCounts": ({ newCount, duplicateCount, conflictCount }) => `${newCount} нових · ${duplicateCount} дублікатів · ${conflictCount} конфліктів`,
    "memoryImport.reviewPrivacy": "Обери, що Dio має зберегти. Нова пам’ять створиться лише після натискання «Застосувати».",
    "memoryImport.groupNew": "Нова пам’ять",
    "memoryImport.groupConflicts": "Оновлення та конфлікти",
    "memoryImport.groupReview": "Потребує перегляду",
    "memoryImport.groupDuplicates": "Вже в пам’яті",
    "memoryImport.groupSkipped": "Безпечно пропущено",
    "memoryImport.decisionLabel": "Рішення",
    "memoryImport.memoryTypeLabel": "Тип пам’яті",
    "memoryImport.summaryLabel": "Текст",
    "memoryImport.editValidation": "Кожна пам’ять має мати тип і текст не довший за 500 символів, перш ніж її можна буде застосувати.",
    "memoryImport.decision.import": "Імпортувати",
    "memoryImport.decision.skip": "Пропустити",
    "memoryImport.decision.replace": "Використати імпортоване",
    "memoryImport.decision.keep_both": "Зберегти обидва",
    "memoryImport.decision.keep_existing": "Зберегти наявне",
    "memoryImport.disposition.new": "Нове",
    "memoryImport.disposition.duplicate": "Дублікат",
    "memoryImport.disposition.conflict": "Конфлікт",
    "memoryImport.disposition.review_required": "Потрібен перегляд",
    "memoryImport.disposition.rejected": "Не імпортовано",
    "memoryImport.existingMemory": "Наявна пам’ять",
    "memoryImport.reason.previously_imported": "Це твердження вже імпортовано. Керуй його поточним станом у налаштуваннях пам’яті.",
    "memoryImport.reason.exact_duplicate": "Це точно відповідає активній пам’яті.",
    "memoryImport.reason.explicit_replacement": "Асистент позначив це як заміну наявної пам’яті.",
    "memoryImport.reason.replacement_target_not_found": "Не вдалося безпечно знайти вказану заміну.",
    "memoryImport.reason.no_match": "Жодна наявна пам’ять не відповідає цьому пункту.",
    "memoryImport.reason.comparison_pending": "Безпечне порівняння поки недоступне.",
    "memoryImport.reason.semantic_duplicate": "Схоже, це означає те саме, що й наявна пам’ять.",
    "memoryImport.reason.semantic_conflict": "Схоже, це суперечить наявній пам’яті.",
    "memoryImport.reason.semantic_independent": "Схоже, це незалежний контекст.",
    "memoryImport.reason.comparison_uncertain": "Порівняння не дало певного результату, тому обери вручну.",
    "memoryImport.reason.invalid_item": "Цей пункт не був коректним JSON.",
    "memoryImport.reason.unsupported_type": "Цей тип пам’яті не підтримується для імпорту.",
    "memoryImport.reason.blank_summary": "У цьому пункті не було корисного опису.",
    "memoryImport.reason.summary_too_long": "Цей опис занадто довгий для імпорту.",
    "memoryImport.reason.unsafe_content": "Пункт пропущено, бо він може містити секрет або облікові дані.",
    "memoryImport.reason.invalid_replaces": "Поле заміни було некоректним.",
    "memoryImport.reason.replaces_too_long": "Поле заміни занадто довге.",
    "memoryImport.reason.duplicate_candidate": "Цей пункт дублював інший імпортований пункт, тому його пропущено.",
    "memoryImport.applying": "Застосування…",
    "memoryImport.apply": ({ count }) => `Імпортувати ${count} ${count === 1 ? "пам’ять" : "пам’яті"}`,
    "memoryImport.backToPaste": "Назад до відповіді",
    "memoryImport.applyError": "Не вдалося застосувати імпорт. Нічого не змінено; переглянь відповідь і спробуй ще раз.",
    "memoryImport.previewError": "Не вдалося переглянути цю відповідь. Переконайся, що це JSON від асистента, і спробуй ще раз.",
    "memoryImport.successTitle": "Пам’ять імпортовано",
    "memoryImport.successSummary": ({ assistant }) => `Переглянутий контекст із ${assistant} тепер є частиною редагованої пам’яті Disciplio.`,
    "memoryImport.successImported": "нових",
    "memoryImport.successReplaced": "оновлено",
    "memoryImport.successKeptBoth": "збережено обидва",
    "memoryImport.successSkipped": "пропущено",
    "memoryImport.done": "Готово",
    "memoryImport.close": "Закрити імпорт пам’яті",
    "memoryImport.goToEnd": "Перейти в кінець",
    "account.legalTitle": "Право та приватність",
    "account.legalDates": ({ terms, privacy }) =>
      `Прийняті умови: ${terms} · Приватність: ${privacy}`,
    "account.legalNotRecorded": "Не зафіксовано",
    "account.terms": "Умови",
    "account.privacy": "Приватність",
    "account.cookies": "Cookies",
    "account.contact": "Контакти",
    "account.support": "Підтримка",
    "account.deleteTitle": "Видалити акаунт",
    "account.deleteDescription":
      "Це негайна й остаточна дія. Плани, рефлексії, рекомендації, налаштування, токени та пов'язані звернення до підтримки буде видалено.",
    "account.deleteCurrentPassword": "Поточний пароль",
    "account.deleteConfirmation": "Введи DELETE",
    "account.deleteButton": "Видалити акаунт назавжди",
    "account.deleteConfirmError":
      "Введи DELETE, щоб підтвердити видалення акаунта.",
    "account.deleteFailed":
      "Не вдалося видалити акаунт. Перевір пароль і слово підтвердження.",
    "account.fallbackTitle": "Твій акаунт Disciplio",
    "account.lead": "Особистий контекст і налаштування Disciplio",
    "account.roles": "Ролі",
    "account.globalGoals": "Глобальні цілі",
    "account.yearGoals": "Річні цілі",
    "account.focusInputs": "Джерела фокусу",
    "account.workspaceReady": "Простір готовий",
    "account.personalInfo": "Особиста інформація",
    "account.profileContext": "Контекст профілю",
    "account.identitySection": "Ідентичність",
    "account.directionSection": "Довгостроковий напрям",
    "account.directionSectionHelp": "Розділяй цілі комами або новими рядками",
    "account.focusSection": "Карта фокусу",
    "account.focusSectionHelp":
      "Дані, які система використовує для рекомендацій",
    "account.saveProfile": "Зберегти профіль",
    "account.profileSaved": "Профіль збережено",
    "account.autoSaved": "Збережено автоматично",
    "account.autoSaveReady": "Автозбереження після виходу з поля",
    "account.unsavedChanges": "Змінено",
    "account.passwordSaved": "Пароль змінено",
    "account.passwordCurrentError": "Поточний пароль неправильний",
    "account.passwordLengthError":
      "Новий пароль має містити щонайменше 8 символів",
    "account.passwordMatchError": "Нові паролі не збігаються",
    "account.fullName": "Повне ім'я",
    "account.fullNameHelp": "Ім'я, яке видно в усьому просторі Disciplio",
    "account.fullNamePlaceholder": "Ім'я, яке видно в просторі",
    "account.email": "Email",
    "account.emailHelp": "Адреса email, пов'язана з цим акаунтом",
    "account.emailPlaceholder": "you@example.com",
    "account.rolesSeason": "Ролі / етап життя",
    "account.rolesPlaceholder": "Founder, student, operator, athlete",
    "account.rolesHelp": "Розділяй ролі комами",
    "account.globalGoalsHelp":
      "Довгострокові результати, що задають напрям плануванню. Розділяй цілі комами або новими рядками",
    "account.globalGoalsPlaceholder":
      "Побудувати дисципліновану студію, завершити диплом, покращити здоров'я",
    "account.yearGoalsPlaceholder":
      "Запустити v1 продукту, опублікувати 24 есе, пробігти півмарафон",
    "account.interests": "Інтереси",
    "account.interestsHelp":
      "Теми й активності, які допомагають Disciplio зрозуміти твій фокус",
    "account.interestsPlaceholder":
      "AI, письмо, витривалість, психологія, дизайн",
    "account.problemFields": "Проблемні зони",
    "account.problemFieldsHelp":
      "Виклики, які можуть заважати послідовному виконанню",
    "account.problemFieldsPlaceholder":
      "Послідовність, перепланування, відволікання, нестача сну",
    "account.hobbies": "Хобі",
    "account.hobbiesHelp":
      "Активності та інтереси, що підтримують відновлення й баланс",
    "account.hobbiesPlaceholder":
      "Скелелазіння, читання, прогулянки, шахи, готування",
    "account.systemNeeds": "З чим система має допомагати?",
    "account.systemNeedsHelp":
      "Структура, захист фокусу або підтримка, яку ти очікуєш від Disciplio",
    "account.systemNeedsPlaceholder":
      "Ранкова структура, захист фокусу, планування відновлення",
    "account.weeklyCommitment": "Тижнева зайнятість",
    "account.weeklyCommitmentHelp":
      "Реалістична тижнева спроможність для зосередженої роботи, тренувань або інших справ",
    "account.weeklyCommitmentPlaceholder":
      "10 годин фокусу, 4 тренування, 2 блоки письма",
    "account.settingsLabel": "Налаштування Disciplio",
    "account.systemPreferences": "Системні налаштування",
    "account.saveSettings": "Зберегти налаштування",
    "account.settingsSaved": "Налаштування збережено",
    "account.securityLabel": "Безпека",
    "account.passwordTitle": "Змінити пароль",
    "account.passwordLead":
      "Онови локальний пароль для доступу до Disciplio на цьому пристрої",
    "account.currentPassword": "Поточний пароль",
    "account.currentPasswordHelp": "Потрібен лише якщо пароль уже існує",
    "account.newPassword": "Новий пароль",
    "account.confirmPassword": "Підтверди новий пароль",
    "account.passwordPlaceholder": "Мінімум 8 символів",
    "account.savePassword": "Зберегти пароль",
    "account.interfaceLanguage": "Мова інтерфейсу",
    "account.interfaceLanguageHelp":
      "Визначає мову, яка використовується в Disciplio",
    "account.dayStartTime": "Початок дня",
    "account.dayStartTimeHelp":
      "До цього часу Сьогодні ще означає попередній день Disciplio",
    "account.dailyNoteUnlock": "Розблокування нотатки дня",
    "account.dailyNoteUnlockHelp":
      "Година, після якої можна редагувати рефлексію",
    "account.autoSuggestFocus": "Автоматично пропонувати дії для плану",
    "account.autoSuggestFocusHelp":
      "Автоматично показувати пропозиції ШІ, коли в цілях є прогалини",
    "account.showCompletedDefault": "Показувати завершені дії за замовчуванням",
    "account.showCompletedDefaultHelp":
      "Залишати завершені дії видимими після відкриття плану",
    "account.dailyReminders": "Щоденні нагадування",
    "account.dailyRemindersHelp":
      "Спокійний сигнал часу для запланованих дій і звичок",
    "account.dailyRemindersEnabled": "Нагадувати про дії з часом",
    "account.dailyRemindersEnabledHelp":
      "Показує нагадування на початку та, за налаштуванням, перед завершенням дії",
    "account.dailyReminderLead": "Перед завершенням дії",
    "account.dailyReminderLeadHelp":
      "Час випередження працює лише для дій із часом початку та завершення",
    "account.reminderOff": "Вимкнено",
    "account.reminderMinutes": ({ minutes }) => `${minutes} хв`,
    "account.reminderCustom": "Власний",
    "account.notificationSound": "Відтворювати звук нагадування",
    "account.notificationSoundHelp":
      "Використовує наданий звук, коли Disciplio відкрите; браузер може його заблокувати",
    "account.browserNotifications": "Сповіщення браузера",
    "account.browserNotificationStatus.unsupported":
      "Цей браузер не підтримує системні сповіщення.",
    "account.browserNotificationStatus.default":
      "Увімкніть системні сповіщення для нагадувань, коли Disciplio закрито або працює у фоні.",
    "account.browserNotificationStatus.granted":
      "Дозвіл надано. Завершіть налаштування, щоб створити push-підписку для цього браузера.",
    "account.browserNotificationStatus.denied":
      "Заблоковано в налаштуваннях браузера. Увімкніть там, щоб отримувати системні сповіщення.",
    "account.enableBrowserNotifications": "Увімкнути сповіщення браузера",
    "account.finishBrowserNotifications": "Завершити налаштування сповіщень",
    "account.sendTestBrowserNotification": "Надіслати тестове сповіщення",
    "account.browserNotificationsEnabled": "Сповіщення браузера увімкнено",
    "account.browserNotificationsNotEnabled":
      "Сповіщення браузера не увімкнено",
    "account.browserNotificationsSubscribed":
      "Увімкнено в цьому браузері й на цьому пристрої, навіть коли Disciplio закрито.",
    "account.browserNotificationsUnavailable":
      "Push-сповіщення ще не налаштовані для цього середовища.",
    "account.browserNotificationsPermissionOnly":
      "Дозволено в цьому браузері. Нагадування можуть з’являтися, поки вкладка Disciplio відкрита; push при закритій вкладці тут ще не налаштовано.",
    "account.browserNotificationsSubscriptionMissing":
      "Дозвіл надано, але цьому браузеру все ще потрібна push-підписка. Завершіть налаштування, щоб увімкнути нагадування при закритій вкладці.",
    "account.browserNotificationsTestBody":
      "Сповіщення готові. Так виглядатимуть нагадування про заплановані дії.",
    "account.browserNotificationsTestSent": ({ count }) =>
      count === 1
        ? "Тестове сповіщення надіслано."
        : `Надіслано ${count} тестових сповіщень.`,
    "account.browserNotificationsTestNone":
      "Для цього облікового запису не знайдено активних push-підписок.",
    "account.browserNotificationsTestFailed":
      "Не вдалося доставити тестове сповіщення.",
    "account.browserNotificationsError.unsupported":
      "Сповіщення браузера не підтримуються.",
    "account.browserNotificationsError.serviceWorker":
      "Service worker недоступний",
    "account.browserNotificationsError.invalidConfig":
      "Недійсна конфігурація сповіщень",
    "account.browserNotificationsError.subscribeFailed":
      "Не вдалося створити підписку браузера",
    "account.browserNotificationsError.saveFailed":
      "Не вдалося зберегти підписку",
    "account.browserNotificationsInstallRequired":
      "На iPhone або iPad спершу додайте Disciplio на головний екран, а потім увімкніть сповіщення у встановленій програмі.",
    "account.browserNotificationsDisabled":
      "Сповіщення браузера вимкнено на цьому пристрої",
    "account.disableBrowserNotifications": "Вимкнути на цьому пристрої",
    "account.metricUnits": "Одиниці прогресу",
    "account.metricUnitsHelp": ({ count, max }) =>
      `${count}/${max} власних одиниць. Дефолтні залежать від мови інтерфейсу`,
    "account.defaultMetricUnits": "Дефолтні одиниці",
    "account.customMetricUnits": "Власні одиниці",
    "account.noCustomMetricUnits": "Власних одиниць ще немає",
    "account.metricUnitPlaceholder": "Власна одиниця",
    "account.addMetricUnit": "Додати одиницю",
    "account.weeklyGoalCategories": "Категорії тижневих цілей",
    "account.weeklyGoalCategoriesHelp": ({ count, max }) =>
      `${count}/${max} власних категорій. Дефолтні залежать від мови інтерфейсу`,
    "account.defaultWeeklyGoalCategories": "Дефолтні категорії",
    "account.customWeeklyGoalCategories": "Власні категорії",
    "account.noCustomWeeklyGoalCategories": "Власних категорій ще немає",
    "account.weeklyGoalCategoryPlaceholder": "Власна категорія",
    "account.addWeeklyGoalCategory": "Додати категорію",
    "account.streakFreeDays": "Вільні дні для серії",
    "account.streakFreeDaysHelp":
      "Дні, які не переривають серію і не збільшують її лічильник",
    "account.streakFreeWeekdays": "Щотижневі вільні дні",
    "account.streakFreeWeekdaysHelp":
      "Вибрані дні тижня не переривають серію й не збільшують її лічильник",
    "account.streakFreeRanges": "Вільні діапазони дат",
    "account.streakFreeRangesHelp":
      "Тимчасові діапазони дат, які серія ігнорує",
    "account.streakFreeRangeStart": "Початок",
    "account.streakFreeRangeStartHelp": "Перший день вільного діапазону",
    "account.streakFreeRangeEnd": "Кінець",
    "account.streakFreeRangeEndHelp":
      "Останній день діапазону. Порожнє поле означає один день",
    "account.addFreeRange": "Додати діапазон",
    "account.removeFreeRange": "Видалити діапазон",
    "account.noFreeRanges": "Вільних діапазонів ще немає",
    "account.exitTitle": "Вийти з акаунта",
    "account.exitHelp":
      "Вийди з простору і повернись до входу. Локальні дані залишаться збереженими",
    "account.exitButton": "Вийти",
    "account.deleteCurrentPasswordHelp":
      "Підтверди особу поточним паролем акаунта",
    "account.deleteConfirmationHelp":
      "Введи DELETE точно, щоб підтвердити остаточну дію",

    "today.label": "СЬОГОДНІ",
    "today.streak": "Серія Disciplio",
    "today.day": "день",
    "today.days": "днів",
    "today.streakAria": ({ streak }) =>
      `${streak} днів поспіль; сім індикаторів показують твою серію`,
    "today.streakHint":
      "Активна, якщо сьогодні завершено хоча б одну ціль або звичку",
    "streak.openCalendar": "Відкрити календар серії",
    "streak.calendarTitle": "Календар серії",
    "streak.current": "Поточна серія",
    "streak.restores": "Відновлення цього місяця",
    "streak.resets": ({ date }) => `Скидання ${date}`,
    "streak.legend": "Легенда календаря серії",
    "streak.completed": "Виконано",
    "streak.restored": "Відновлено",
    "streak.free": "Вільний день",
    "streak.missed": "Пропущено",
    "streak.calendarNote":
      "Виконуй щонайменше одне завдання або звичку в кожен активний день, щоб продовжувати серію. Вільні дні зберігають ланцюг, але не додають день; після початку нового дня придатні короткі перерви можуть бути відновлені — до двох пропущених днів на місяць.",
    "streak.close": "Закрити календар серії",
    "streak.loading": "Завантаження серії",
    "streak.unavailable": "Ще недоступно",
    "streak.restoreTitle": "Серію відновлено",
    "streak.restoreDescription": ({ dates }) => `Захищено: ${dates}`,
    "streak.remaining": ({ count }) => `Залишилось відновлень: ${count}`,
    "streak.continue": "Продовжити",
    "today.completion": "Виконання дня",
    "today.dayCompletion": "Виконання дня",
    "today.actionsDone": ({ done, total }) => `${done} / ${total} дій виконано`,
    "today.pageTitle": "Сьогодні",
    "today.nextAction": "Наступна дія",
    "today.noNextAction": "Наступної дії немає",
    "today.exitPlanning": "Вийти з режиму планування",
    "today.planTitle": "План дня",
    "today.selectedPlanTitle": "План дня",
    "today.planSubtitle":
      "Спочатку виконай найближчий крок, потім рухайся далі по дню",
    "today.planningMode": "Режим планування",
    "today.quickAdd": "Швидке додавання",
    "today.quickAddLead":
      "Додай задачу швидко, а деталі відкривай лише коли вони справді потрібні",
    "today.dailyNote": "Нотатка дня",
    "today.dailyNoteLead":
      "Необов'язково. Відкривається для написання о 17:00. Впливає на підсумок нижче",
    "today.dailyNoteLeadLocked": ({ hour }) =>
      `Необов'язково. Відкривається для написання о ${hour}. Впливає на підсумок нижче`,
    "today.dailyNoteLeadEditable": ({ hour }) =>
      `Необов'язково. Відкривається для написання о ${hour}. Впливає на підсумок нижче`,
    "today.dailyNoteInfo": ({ hour }) =>
      `Відкривається для написання о ${hour} і впливає на підсумок нижче. Опиши, що сьогодні було дуже добрим, добрим і поганим, а також свої думки.`,
    "today.dailyReflection": "Рефлексія дня",
    "today.aiSummaryLabel": "Підсумок",
    "today.aiRecommendationLabel": "Рекомендація на завтра",
    "today.generatingAdvice": "Генерація рекомендації",
    "today.reflectionAdvicePending":
      "Збережи нотатку, щоб згенерувати конкретну рекомендацію на завтра",
    "today.reflectionPlaceholder":
      "напр. Я пропустив вечірні завдання, бо не хотів їх виконувати; дві години зосереджено працював над головною ціллю",
    "today.saveNote": "Зберегти нотатку",
    "today.noteSaving": "Зберігаємо нотатку…",
    "today.noteSaved": "Нотатку збережено. Її можна редагувати.",
    "today.noteUnsaved": "Зміни ще не збережено.",
    "today.noteSaveFailed": "Не вдалося зберегти нотатку. Спробуйте ще раз.",
    "today.planTomorrow": "Спланувати завтра",
    "today.recommendationActionsHelp":
      "Не підходить? Приховайте рекомендацію або видаліть її назавжди.",
    "today.rejectRecommendation": "Не підходить",
    "today.rejectRecommendationHelp":
      "Приховати цю рекомендацію для цієї нотатки.",
    "today.deleteRecommendation": "Видалити назавжди",
    "today.deleteRecommendationHelp": "Видалити цю рекомендацію назавжди.",

    "todayReflectionWorkflow.eyebrow": "Рефлексія",
    "todayReflectionWorkflow.note.helper":
      "Підсумуйте сьогодні й за бажанням опишіть завтра. Disciplio перетворить це на редагований план.",
    "todayReflectionWorkflow.note.emptyPast": "Цього дня нотатку не збережено.",
    "todayReflectionWorkflow.tomorrowContext.label":
      "Щось важливе на завтра? (необов'язково)",
    "todayReflectionWorkflow.tomorrowContext.placeholder":
      "Фіксовані години роботи, зустрічі, обмеження енергії або те, що ви вже плануєте зробити",
    "todayReflectionWorkflow.action.generate": "Згенерувати рефлексію",
    "todayReflectionWorkflow.action.regenerate": "Згенерувати знову",
    "todayReflectionWorkflow.action.retry": "Повторити",
    "todayReflectionWorkflow.regenerate.menuLabel": "Згенерувати знову…",
    "todayReflectionWorkflow.regenerate.FULL": "Згенерувати рефлексію знову",
    "todayReflectionWorkflow.regenerate.RECOMMENDATION_ONLY":
      "Згенерувати рекомендацію знову",
    "todayReflectionWorkflow.regenerate.PLAN_ONLY":
      "Згенерувати план на завтра знову",
    "todayReflectionWorkflow.state.resuming": "Завантаження вашої рефлексії…",
    "todayReflectionWorkflow.state.outdated":
      "Нотатка змінилася після генерації цієї рефлексії. Згенеруйте знову, щоб оновити її.",
    "todayReflectionWorkflow.live.resumed": "Рефлексію відновлено.",
    "todayReflectionWorkflow.error.consentRequired":
      "Прийміть повідомлення про використання AI, щоб згенерувати рефлексію.",
    "todayReflectionWorkflow.error.generic":
      "Не вдалося згенерувати рефлексію. Спробуйте ще раз.",
    "todayReflectionWorkflow.error.unavailable":
      "Disciplio AI тимчасово недоступна. Щоденну нотатку збережено; спробуйте ще раз трохи пізніше.",
    "todayReflectionWorkflow.error.contract":
      "Не вдалося обробити дані рефлексії. Щоденну нотатку збережено; оновіть застосунок і спробуйте ще раз.",
    "todayReflectionWorkflow.error.generator":
      "Модель не повернула коректну рефлексію. Щоденну нотатку збережено; спробуйте згенерувати її ще раз.",
    "todayReflectionWorkflow.error.reviewer":
      "Не вдалося завершити перевірку рефлексії. Безпечну нотатку та план збережено.",
    "todayReflectionWorkflow.error.responseInvalid":
      "Disciplio отримала некоректну відповідь AI. Щоденну нотатку збережено; спробуйте ще раз.",
    "todayReflectionWorkflow.error.quota": ({ remaining, limit }) =>
      `Досягнуто денний ліміт AI (залишилось ${remaining}/${limit}). Спробуйте після оновлення ліміту.`,
    "todayReflectionWorkflow.error.notFound":
      "Ця сесія рефлексії більше недоступна. Розпочніть нову.",
    "todayReflectionWorkflow.question.ariaLabel":
      "Коротке запитання від Disciplio",
    "todayReflectionWorkflow.question.badge": "Disciplio AI",
    "todayReflectionWorkflow.question.chipsLabel": "Варіанти відповіді",
    "todayReflectionWorkflow.question.somethingElse": "Щось інше",
    "todayReflectionWorkflow.question.freeTextPlaceholder":
      "Розкажіть детальніше своїми словами",
    "todayReflectionWorkflow.question.submitAnswer": "Надіслати відповідь",
    "todayReflectionWorkflow.question.helper":
      "Відповідь допоможе Disciplio дати точнішу рекомендацію та план.",
    "todayReflectionWorkflow.question.skip": "Пропустити запитання",
    "todayReflectionWorkflow.reflection.forTomorrow": "На завтра:",
    "todayReflectionWorkflow.recommendation.label": "Рекомендація",
    "todayReflectionWorkflow.recommendation.firstStep": ({ step }) =>
      `Перший крок: ${step}`,
    "todayReflectionWorkflow.recommendation.generatedPlanItem":
      "Додано пропоновану дію до плану на завтра нижче.",
    "todayReflectionWorkflow.feedback.helpful": "Корисно",
    "todayReflectionWorkflow.feedback.notUseful": "Не корисно",
    "todayReflectionWorkflow.feedback.reason.TOO_REPETITIVE":
      "Занадто повторюване",
    "todayReflectionWorkflow.feedback.reason.TOO_GENERIC": "Занадто загальне",
    "todayReflectionWorkflow.feedback.reason.NOT_RELEVANT_TODAY":
      "Не актуально сьогодні",
    "todayReflectionWorkflow.feedback.reason.ALREADY_DOING_THIS":
      "Я вже це роблю",
    "todayReflectionWorkflow.feedback.reason.OTHER": "Інше",
    "todayReflectionWorkflow.feedback.more": "Більше",
    "todayReflectionWorkflow.feedback.wrongAssumption": "Хибне припущення",
    "todayReflectionWorkflow.feedback.wrongAssumptionPlaceholder":
      "Що саме було невірним? (необов'язково)",
    "todayReflectionWorkflow.feedback.submit": "Надіслати",
    "todayReflectionWorkflow.feedback.dontRecommend": "Більше не рекомендувати",
    "todayReflectionWorkflow.feedback.suppressed":
      "Більше не рекомендуватиметься",
    "todayReflectionWorkflow.feedback.duration.ONE_WEEK": "На 1 тиждень",
    "todayReflectionWorkflow.feedback.duration.ONE_MONTH": "На 1 місяць",
    "todayReflectionWorkflow.feedback.duration.PERMANENT": "Назавжди",
    "todayReflectionWorkflow.feedback.error":
      "Не вдалося зберегти відгук. Спробуйте ще раз.",
    "todayReflectionWorkflow.plan.eyebrow": "План",
    "todayReflectionWorkflow.plan.title": "План на завтра",
    "todayReflectionWorkflow.plan.helper":
      "Перегляньте запропоновані дії, відредагуйте те, що потребує змін, і прийміть те, що має потрапити до плану на завтра.",
    "todayReflectionWorkflow.plan.addAction": "Спланувати завтра",
    "todayReflectionWorkflow.plan.acceptAll": "Прийняти все",
    "todayReflectionWorkflow.plan.accept": "Прийняти",
    "todayReflectionWorkflow.plan.accepted": "Додано до плану",
    "todayReflectionWorkflow.plan.acceptError":
      "Не вдалося оновити план на завтра. Спробуйте ще раз.",
    "todayReflectionWorkflow.plan.skippedConflicted": ({ count }) =>
      `Пропущено ${count} пункт(и), оскільки вони перетинаються з фіксованим зобов'язанням.`,
    "todayReflectionWorkflow.plan.empty": "Поки що немає пропозицій плану.",
    "todayReflectionWorkflow.plan.origin.FIXED_COMMITMENT": "Фіксоване",
    "todayReflectionWorkflow.plan.origin.EXISTING_ACTION": "Наявне",
    "todayReflectionWorkflow.plan.origin.MOVED_FROM_TODAY":
      "Перенесено з сьогодні",
    "todayReflectionWorkflow.plan.origin.PARSED_FROM_NOTE": "З вашої нотатки",
    "todayReflectionWorkflow.plan.origin.AI_SUGGESTED": "Пропоновано",
    "todayReflectionWorkflow.plan.sourceIcon.goal": "Ціль",
    "todayReflectionWorkflow.plan.sourceIcon.habit": "Звичка",
    "todayReflectionWorkflow.plan.sourceIcon.FIXED_COMMITMENT":
      "Фіксоване зобовʼязання",
    "todayReflectionWorkflow.plan.sourceIcon.MOVED_FROM_TODAY":
      "Перенесено з сьогодні",
    "todayReflectionWorkflow.plan.sourceIcon.PARSED_FROM_NOTE":
      "З вашої нотатки",
    "todayReflectionWorkflow.plan.sourceIcon.AI_SUGGESTED": "Пропозиція AI",
    "todayReflectionWorkflow.plan.sourceIcon.EXISTING_ACTION":
      "Вже у вашому плані",
    "todayReflectionWorkflow.plan.moveUp": "Перемістити вгору",
    "todayReflectionWorkflow.plan.moveDown": "Перемістити вниз",
    "todayReflectionWorkflow.plan.titleLabel": "Назва",
    "todayReflectionWorkflow.plan.startLabel": "Початок",
    "todayReflectionWorkflow.plan.endLabel": "Кінець",
    "todayReflectionWorkflow.plan.doneEditing": "Готово",
    "todayReflectionWorkflow.plan.firstStep": ({ step }) =>
      `Перший крок: ${step}`,
    "todayReflectionWorkflow.plan.conflict": ({ title }) =>
      `Перетинається з фіксованим зобов'язанням: ${title}`,
    "todayReflectionWorkflow.plan.edit": "Редагувати",
    "todayReflectionWorkflow.plan.remove": "Видалити",
    "todayReflectionWorkflow.plan.regenerateWarning":
      "У вас є незбережені зміни в плані на завтра. Повторна генерація може їх змінити або видалити. Продовжити?",

    "today.quoteTitle": "Цитата дня",
    "today.quoteLead": "Невеликий бренд-момент замість повного hero-блоку",
    "notifications.liveLabel": "Щоденні нагадування",
    "notifications.startNow": ({ time }) => `Почніть зараз · ${time}`,
    "notifications.endsSoon": ({ minutes, time }) =>
      `Завершення за ${minutes} хв · ${time}`,
    "notifications.openToday": "Відкрити Сьогодні",
    "notifications.dismiss": "Закрити нагадування",
    "notifications.promptTitle": "Увімкнути сповіщення браузера",
    "notifications.promptBody":
      "Дозвольте Disciplio надсилати нагадування про заплановані дії, навіть коли цю вкладку закрито.",
    "notifications.promptEnable": "Дозволити сповіщення",
    "notifications.promptEnabling": "Увімкнення…",
    "notifications.promptLater": "Не зараз",
    "notifications.promptFailed":
      "Не вдалося увімкнути сповіщення. Спробуйте ще раз у налаштуваннях облікового запису.",
    "notifications.promptUnavailable":
      "Push-сповіщення тут ще не налаштовані. Спробуйте пізніше в налаштуваннях облікового запису.",

    "add.title": "Назва дії",
    "add.placeholder": "Що ти зробиш?",
    "add.button": "Додати",
    "add.more": "Час, тип і тижнева ціль",
    "add.type": "Тип",
    "add.goal": "Ціль",
    "add.habit": "Звичка",
    "add.time": "Час",
    "add.date": "Дата",
    "add.week": "Тиждень",
    "add.thisWeek": ({ range }) => `Цей тиждень · ${range}`,
    "add.nextWeek": ({ range }) => `Наступний тиждень · ${range}`,
    "add.from": "Від",
    "add.to": "До",
    "add.clear": "Очистити",
    "add.timeConstraintWorkWindow": "Робоче вікно (інші дії можна запланувати всередині)",
    "add.timeConstraintWorkWindowHint":
      "Вимкнено: повністю блокує цей проміжок часу. Увімкнено: інші дії можна запланувати всередині нього.",
    "add.weeklyGoal": "Тижнева ціль",
    "add.weeklyGoalLink": "Привʼязка до тижневої цілі",
    "add.monthlyGoal": "Місячна ціль",
    "add.none": "Немає",
    "add.amount": "Кількість",
    "add.unit": "Одиниця",
    "add.unitPlaceholder": "години, сторінки",
    "add.useProposedAmount": ({ amount }) => `Взяти ${amount} з проміжку часу`,
    "add.unitMismatchWarning": ({ goalUnit }) =>
      `Ця одиниця відрізняється від одиниці пов'язаної цілі (${goalUnit}), тому кількість не зарахується до її прогресу.`,
    "weeklyGoal.progressSummary": ({ progress, target, unit }) =>
      `${progress} / ${target} ${unit}`,
    "weeklyGoal.noTarget": "Без цілі",
    "metricUnitInput.showOptions": "Показати варіанти одиниць",
    "add.linkAlert":
      "Зв'язок із вимірюваною тижневою ціллю вимагає додатного значення (наприклад, години, сторінки)",
    "add.saveFailed":
      "Не вдалося зберегти завдання. Перевір з’єднання та спробуй ще раз.",
    "repeat.label": "Повторення",
    "repeat.frequencyLabel": "Шаблон повторення",
    "repeat.frequencyHelp": "Вибери, як часто ця звичка має повертатися",
    "repeat.intervalLabel": "Інтервал",
    "repeat.daily": "Щодня",
    "repeat.withExceptions": ({ repeat, exceptions }) =>
      `${repeat}, крім ${exceptions}`,
    "repeat.customDays": ({ count }) => `Кожні ${count} дні`,
    "repeat.customDaysShort": "Кожні X днів",
    "repeat.weekly": "Щотижня",
    "repeat.monthly": "Щомісяця",
    "repeat.everyXDays": "Інтервал у днях",
    "repeat.rulesTitle": "Правила повторення",
    "repeat.exceptions": "Винятки",
    "repeat.exceptionsHelp":
      "Необов'язкові пропуски. Дні тижня повторюються, дати діють один раз",
    "repeat.skipWeekdays": "Пропустити дні тижня",
    "repeat.skipWeekdaysHelp": "Натисни дні, коли звичка не має з'являтися",
    "repeat.skipDates": "Пропустити конкретні дати",
    "repeat.skipDatesHelp": "Додай свята, поїздки або будь-яку окрему дату",
    "repeat.skipDate": "Пропустити дату",
    "repeat.selectedSkipDates": "Вибрані пропущені дати",
    "weekday.short.0": "Нд",
    "weekday.short.1": "Пн",
    "weekday.short.2": "Вт",
    "weekday.short.3": "Ср",
    "weekday.short.4": "Чт",
    "weekday.short.5": "Пт",
    "weekday.short.6": "Сб",
    "weeklyCategories.filterLabel": "Категорії тижневих цілей",
    "weeklyCategories.all": "Усі",
    "weeklyCategories.placeholder": "Категорія, напр. Навчання",
    "weeklyCategories.useCustom": ({ value }) => `Використати «${value}»`,
    "weeklyCategories.limitReached":
      "Ліміт власних категорій досягнуто. Наявні категорії залишаються доступними.",

    "plan.unscheduled": "Без розкладу",
    "plan.flexible": "Гнучко · без фіксованого часу",
    "plan.noUnscheduled": "Поки немає дій без часу",
    "plan.scheduled": "Заплановано",
    "plan.chronological": "Хронологічно",
    "plan.noScheduled": "Поки немає дій із часом",
    "plan.newScheduledAction": "Нова запланована дія",
    "plan.newUnscheduledAction": "Нова дія без часу",
    "plan.completed": "Завершено",
    "plan.completedHint":
      "Відображаються за замовчуванням, щоб ви могли переглянути свій прогрес",
    "plan.noCompleted": "Ще немає завершених дій",
    "plan.linked": ({ title, amount, unit }) =>
      `Тижнева ціль: ${title} (+${amount} ${unit})`,
    "plan.linkedUnmeasured": ({ title }) => `Тижнева ціль: ${title}`,
    "plan.habitMetric": ({ amount, unit }) => `${amount} ${unit}`,
    "plan.noLink": "Немає зв'язку з тижневою ціллю",
    "plan.anyTime": "У будь-який час",
    "plan.wrapsNextDay": "+1 день",
    "plan.wrapsNextDayAria": "Закінчується після півночі, наступного дня",
    "plan.sectionEmpty": "Тут поки нічого немає",
    "plan.edit": "Редагувати",
    "plan.moreActions": "Інші дії з ціллю",
    "plan.saving": "Збереження…",
    "plan.duplicate": "Дублювати ціль",
    "plan.duplicateTitle": "Дублювати ціль",
    "plan.duplicateDescription": "Створи копії цієї цілі на вибрані дні.",
    "plan.closeDuplicate": "Закрити дублювання цілі",
    "plan.duplicateModes": "Режим дублювання",
    "plan.duplicateSimple": "Просто",
    "plan.duplicateWeekdays": "Дні тижня",
    "plan.duplicateRange": "Діапазон дат",
    "plan.duplicatePickDate": "Вибери дату",
    "plan.duplicateRepeatOn": "Повторювати в",
    "plan.duplicateStartDate": "Дата початку",
    "plan.duplicateEndDate": "Дата завершення",
    "plan.duplicateRangeInvalid":
      "Вибери дату завершення не раніше дати початку.",
    "plan.duplicateRangeTooLong": "Вибери діапазон не більш як на 31 копію.",
    "plan.duplicateDays": "Вибери дні",
    "plan.duplicateAllWeek": "Увесь тиждень",
    "plan.duplicateWorkdays": "Робочі дні",
    "plan.duplicateCustomDays": "Власні дні",
    "plan.duplicateSourceDay": ({ day }) =>
      `Вихідний день: ${day}. Його не буде скопійовано.`,
    "plan.duplicateKeepDetails": "Зберегти той самий час і деталі",
    "plan.duplicateKeepDetailsHelp":
      "Час, кількість та інші деталі буде скопійовано.",
    "plan.duplicateKeepTime": "Зберегти час",
    "plan.duplicateKeepTimeHelp":
      "Скопіювати запланований час до кожної нової цілі.",
    "plan.duplicateKeepWeekly": "Зберегти зв’язок із тижневою ціллю",
    "plan.duplicateKeepWeeklyHelp":
      "Скопіювати внесок і зберегти зв’язок у планувальній ієрархії.",
    "plan.duplicateKeepWeeklyUnavailable":
      "Ця ціль не пов’язана з тижневою ціллю.",
    "plan.duplicateHierarchyWarning":
      "Для іншого тижня Disciplio може створити або повторно використати тижневу ціль і її місячну батьківську ціль. Нові батьківські цілі починають із нульовим прогресом і зберігають зв’язок із річною ціллю.",
    "plan.duplicateCopyOptions": "Параметри копіювання",
    "plan.duplicateToDays": ({ count }) =>
      `Дублювати на ${count} ${count === 1 ? "день" : "днів"}`,
    "plan.duplicating": "Дублювання…",
    "plan.duplicateFailed":
      "Не вдалося дублювати цілі. Перевір з’єднання та спробуй ще раз.",
    "plan.duplicateSuccessSingle": ({ date }) => `Ціль додано на ${date}.`,
    "plan.duplicateSuccessList": ({ dates }) => `Ціль додано на: ${dates}.`,
    "plan.duplicateSuccessMany": ({ count, from, to }) =>
      `Ціль додано на ${count} дат (${from} – ${to}).`,
    "plan.closeDuplicateSuccess": "Закрити підтвердження дублювання",
    "plan.delete": "Видалити",
    "plan.deleteTaskTitle": ({ title }) => `Видалити «${title}»?`,
    "plan.deleteTaskDescription": "Цю дію не можна скасувати.",
    "plan.deleteGoalTitle": ({ title }) => `Видалити «${title}»?`,
    "plan.deleteGoalDescription":
      "Також буде видалено всі зв’язки з цією ціллю. Цю дію не можна скасувати.",
    "plan.deleteHabitTitle": ({ title }) =>
      `Видалити повторювану звичку «${title}»?`,
    "plan.deleteHabitDescription":
      "Вибери, чи видалити лише це повторення, чи всі повторення.",
    "plan.changeHabitTimeTitle": ({ title }) => `Змінити час для «${title}»?`,
    "plan.changeHabitTimeDescription":
      "Вибери, чи цей час має діяти лише цього дня, чи з цього дня надалі.",
    "plan.changeHabitTimeOne": "Лише ця дата",
    "plan.changeHabitTimeFuture": "Ця дата й майбутні",
    "plan.deleteThisDate": "Видалити на цю дату",
    "plan.deleteAllOccurrences": "Видалити всі повторення",
    "plan.deletedUndoMessage": ({ title }) => `Видалено «${title}»`,
    "plan.undo": "Скасувати",
    "plan.closeUndo": "Закрити",
    "plan.save": "Зберегти",
    "plan.cancel": "Скасувати",
    "plan.show": "Показати",
    "plan.hide": "Сховати",
    "plan.suggestionOnly": "Пропозиція",
    "plan.scheduledSuggestionHint":
      "Цього ще немає у твоєму плані дня. Використай, щоб створити заплановану задачу, якщо вона підходить на сьогодні",
    "plan.unscheduledSuggestionHint":
      "Цього ще немає у твоєму плані дня. Використай як гнучку задачу лише якщо вона підходить на сьогодні",
    "plan.useSuggestion": "Використати пропозицію",

    "planning.workspace": "Простір планування",
    "planning.subtitle": "Сьогоднішні завдання поруч із тижневим контекстом",
    "planning.weekHeaderTitle": "План тижня",
    "planning.weekSubtitle":
      "Переглянь місячні цілі та сформуй пріоритети на цей тиждень",
    "planning.monthHeaderTitle": ({ month }) => `План на місяць: ${month}`,
    "planning.monthSubtitle":
      "Зістав місячні цілі з річними результатами, які вони мають підтримувати",
    "planning.yearHeaderTitle": ({ year }) => `План на рік: ${year}`,
    "planning.yearSubtitle":
      "Зістав річні цілі з глобальним напрямом, який вони мають підтримувати",
    "planning.dayMode": "День",
    "planning.weekMode": "Тиждень",
    "planning.monthMode": "Місяць",
    "planning.yearMode": "Рік",
    "planning.today": "Сьогодні",
    "planning.addToday": "Додати на сьогодні",
    "planning.list": "Твій список",
    "planning.weeklyPlan": "План тижня",
    "planning.weekLead":
      "Використовуй це, щоб вирішити, що має потрапити в список на сьогодні",
    "planning.avgCompletion": "Середнє виконання",
    "planning.goalsTracked": "Цілей у фокусі",
    "planning.weekReview": "Огляд тижня",
    "planning.weekReviewText": ({
      aligned,
      total,
      percent,
      monthlyAvg,
      focus,
    }) =>
      `Узгоджено з ${aligned}/${total} місячними цілями (${percent}%). Середнє виконання місяця: ${monthlyAvg}%; тримай цей тиждень навколо: ${focus}`,
    "planning.monthReview": "Огляд місяця",
    "planning.monthReviewText": ({
      aligned,
      total,
      percent,
      yearGoals,
      monthlyAvg,
      focus,
    }) =>
      yearGoals
        ? `Узгоджено з ${aligned}/${total} місячними цілями (${percent}%) щодо ${yearGoals} річних цілей. Середнє виконання місяця: ${monthlyAvg}%; тримай увагу на: ${focus}`
        : `Річних цілей ще немає, тому місяць не можна перевірити щодо результатів року. Середнє виконання місяця: ${monthlyAvg}%; уточни річні цілі або просунь: ${focus}`,
    "planning.noMonthlyFocus": "найслабшої місячної цілі",
    "planning.suggestedFocus": "Рекомендований фокус",
    "planning.suggestionsLoading": "Генерую пропозицію для тебе",
    "planning.generateDaySuggestions": "Згенерувати пропозиції на день",
    "planning.generateWeekSuggestions": "Згенерувати пропозиції на тиждень",
    "planning.generateMonthSuggestions": "Згенерувати пропозиції на місяць",
    "planning.generateYearSuggestions": "Згенерувати пропозиції на рік",
    "planning.suggestionsEmpty":
      "Всі цілі заплановані на сьогодні — більше нічого запропонувати",
    "planning.suggestionsEmptyDay": "Поки немає пропозицій на сьогодні",
    "planning.suggestionsEmptyWeek": "Поки немає пропозицій на цей тиждень",
    "planning.suggestionsEmptyMonth": "Поки немає пропозицій на цей місяць",
    "planning.suggestionsEmptyYear": "Поки немає пропозицій на цей рік",
    "planning.suggestionPersistenceError":
      "Не вдалося зберегти відхилення. Спробуй ще раз.",
    "planning.remainingGap": "На основі тижневого відставання",
    "planning.focusFallback": "Почни планування з цілі з найнижчим покриттям",
    "planning.monthGoals": "Місячні цілі",
    "planning.monthGoalsFor": ({ month }) => `Твої цілі на ${month}`,
    "planning.monthTitle": "Плануй тиждень із місячних пріоритетів",
    "planning.monthLead":
      "Обирай місячні цілі з низьким прогресом і перетворюй їх на перемоги цього тижня",
    "planning.monthPlanningLead":
      "Виріши, які місячні результати зараз потребують найсильнішого поштовху",
    "planning.yearGoalsFor": ({ year }) => `Цілі на ${year}`,
    "planning.yearGoalsLead":
      "Використовуй їх як контекст праворуч для вибору пріоритетів місяця",
    "planning.yearPlanningLead":
      "Визнач річні результати, які твоя щомісячна робота має реально просувати вперед",
    "planning.yearGoal": "Річна ціль",
    "planning.yearGoalAnchor":
      "Тримай місячні цілі узгодженими з цим результатом",
    "planning.noWeeklyGoals":
      "Тижневі цілі ще не збережені. Додай тижневі цілі, щоб бачити їх тут",
    "planning.noMonthGoals":
      "Місячні цілі ще не збережені. Додай місячні цілі, щоб бачити їх тут",
    "planning.noYearGoals":
      "Річні цілі ще не збережені. Додай річні цілі в акаунті, щоб бачити їх тут",
    "planning.addYearGoal": "Додати",
    "planning.yearGoalPlaceholder": "Додай річну ціль",
    "planning.yearGoalExamples": "Приклади річних цілей",
    "planning.yearGoalExampleProduct": "Запустити продукт v1",
    "planning.yearGoalExampleHealth": "Пробігти півмарафон",
    "planning.yearGoalExampleMoney": "Заощадити 20% доходу",
    "planning.addMonthGoal": "Додати місячну ціль",
    "planning.goalAddedToast": ({ title }) => `Ціль додано: «${title}»`,
    "planning.monthGoalPlaceholder": "Назви дію або результат",
    "planning.monthGoalTitleLabel": "Місячна дія",
    "planning.monthGoalTargetLabel": "Цільове значення",
    "planning.monthGoalProgressLabel": "Поточний прогрес",
    "planning.monthGoalTargetPlaceholder": "напр. 20",
    "planning.monthGoalUnitLabel": "Одиниця",
    "planning.monthGoalUnitPlaceholder": "напр. години",
    "planning.weekGoalTitleLabel": "Тижнева ціль",
    "planning.weekGoalPlaceholder": "Назви тижневий пріоритет",
    "planning.weekGoalTargetLabel": "Цільове значення",
    "planning.weekGoalProgressLabel": "Поточний прогрес",
    "planning.weekGoalUnitLabel": "Одиниця",
    "planning.weekGoalCategoryLabel": "Категорія",
    "planning.yearGoalTitleLabel": "Річна ціль",
    "planning.globalGoalTitleLabel": "Глобальна ціль",
    "planning.goalPlanLabel.week": "План на тиждень",
    "planning.goalPlanLabel.month": "План на місяць",
    "planning.goalPlanLabel.year": "План на рік",
    "planning.goalPlanLabel.global": "Довгостроковий план",
    "planning.goalProgressLabel": "Прогрес",
    "planning.goalProgressSummary": ({ progress, target, unit }) =>
      `${progress} з ${target} ${unit}`,
    "planning.goalProgressAutoHint": "Оновлюється автоматично з виконаних дій",
    "planning.goalManualProgressCta": "+ Я вже почав виконувати цю ціль",
    "planning.monthGoalComposerHint":
      "Додавай цільове значення й одиницю лише коли хочеш міряти прогрес",
    "planning.monthGoalRequiredError": "Вкажи місячну дію",
    "planning.monthGoalTargetError": "Вкажи цільове значення більше нуля",
    "planning.monthGoalMetricPairError":
      "Якщо додаєш цільове значення, додай і одиницю",
    "planning.monthGoalLinkError":
      "Ця річна ціль більше недоступна. Вибери іншу ціль або без зв’язку.",
    "planning.monthGoalSaveError":
      "Не вдалося зберегти цю місячну ціль. Спробуй ще раз.",
    "planning.weekGoalSaveError":
      "Не вдалося зберегти цю тижневу ціль. Спробуй ще раз.",
    "planning.monthGoalDefaultUnit": "результат",
    "planning.noProgressMetric": "Немає метрики прогресу",
    "planning.monthGoalSuggestionTitle": ({ title }) =>
      `Місячний крок для: ${title}`,
    "planning.monthGoalSuggestionHint": () => "З річної цілі",
    "planning.monthGoalSuggestionReason": ({ yearGoal }) =>
      `Місячний результат для ${yearGoal}.`,
    "planning.linkYearGoal": "Пов'язана річна ціль",
    "planning.linkedYearGoal": ({ goal }) => `Підтримує річну ціль: ${goal}`,
    "planning.noLinkedYearGoal": "Немає пов'язаної річної цілі",
    "planning.linkMonthlyGoal": "Пов'язана місячна ціль",
    "planning.linkedMonthlyGoal": ({ goal }) =>
      `Підтримує місячну ціль: ${goal}`,
    "planning.noLinkedMonthlyGoal": "Немає пов'язаної місячної цілі",
    "planning.globalGoals": "Глобальні цілі",
    "planning.globalGoalsLead":
      "Ширший напрям, з якого мають випливати річні цілі",
    "planning.globalGoal": "Глобальна ціль",
    "planning.linkGlobalGoal": "Пов'язана глобальна ціль",
    "planning.linkedGlobalGoal": ({ goal }) =>
      `Підтримує глобальну ціль: ${goal}`,
    "planning.noLinkedGlobalGoal": "Немає пов'язаної глобальної цілі",
    "planning.addGlobalGoal": "Додати глобальну ціль",
    "planning.globalGoalPlaceholder": "Додай глобальну ціль",
    "planning.globalGoalExamples": "Приклади глобальних цілей",
    "planning.globalGoalExampleDiscipline":
      "Побудувати дисципліновану систему самостійної роботи",
    "planning.globalGoalExampleHealth": "Захистити здоров'я на довгу дистанцію",
    "planning.globalGoalExampleFamily": "Бути присутнім для сім'ї",
    "planning.noGlobalGoals":
      "Глобальні цілі ще не збережені. Додай їх в акаунті",
    "planning.monthFocus": ({ title }) =>
      `Почни з місячної цілі з найнижчим покриттям: ${title}`,
    "planning.yearFocus": ({ title }) =>
      `Тримай першу річну ціль видимою: ${title}`,
    "planning.addWeek": "Додати до плану тижня",
    "planning.weekQuickAddLead":
      "Обери тиждень, а потім додай тижневий пріоритет, який підтримує місячну ціль",
    "planning.weekSuggestionHint": "Лише якщо підходить на цей тиждень",
    "planning.monthSuggestionHint": "Лише якщо підходить на цей місяць",
    "planning.monthSuggestionHintShort": "Лише якщо підходить на цей місяць",
    "planning.yearSuggestionHintShort": "Лише якщо підходить на цей рік",
    "planning.useInWeekPlan": "Використати в плані тижня",
    "planning.remainingMonth": ({ percent, remaining, unit }) =>
      `${percent}% виконано · ${remaining} ${unit} залишилось цього місяця`,
    "planning.completedGoalStatus": "Завершено",
    "planning.markGoalCompleted": "Позначити ціль як завершену",
    "planning.markGoalActive": "Повернути ціль до активних",
    "planning.reorderHandle": "Перетягніть, щоб змінити порядок",
    "planning.moveGoalUp": "Перемістити вгору",
    "planning.moveGoalDown": "Перемістити вниз",
    "planning.onTrack": "За планом",
    "planning.inMotion": "У русі",
    "planning.addGoal": "Додати ціль",
    "planning.copyGoal": "Копіювати",
    "planning.copyTitleSuffix": "(копія)",
    "planning.copyTitleSuffixNumbered": ({ count }) => `(копія ${count})`,
    "ragAdmin.label": "АДМІН",
    "ragAdmin.title": "Джерела RAG",
    "ragAdmin.lead":
      "Керуй джерелами RAG, які потім використовують AI-рекомендації",
    "ragAdmin.sourceCount": ({ count }) => `${count} джерел`,
    "ragAdmin.newSource": "Нове джерело",
    "ragAdmin.editSource": "Редагувати джерело",
    "ragAdmin.formLead":
      "Використовуй структуровані поля, щоб майбутні рекомендації могли цитувати чіткі джерела",
    "ragAdmin.topic": "Тема",
    "ragAdmin.principle": "З чим може допомогти",
    "ragAdmin.principlePlaceholder": "фокус, сон, послідовність",
    "ragAdmin.principleHelp": "Кілька сфер розділяй комами",
    "ragAdmin.content": "Контент",
    "ragAdmin.actionableAdvice": "Практична порада",
    "ragAdmin.sourceType": "Тип джерела",
    "ragAdmin.confidence": "Впевненість",
    "ragAdmin.sourceUrl": "Посилання на джерело",
    "ragAdmin.sourceType.book": "Книга",
    "ragAdmin.sourceType.blog": "Блог",
    "ragAdmin.sourceType.research": "Дослідження",
    "ragAdmin.confidence.high": "Висока",
    "ragAdmin.confidence.medium": "Середня",
    "ragAdmin.confidence.low": "Низька",
    "ragAdmin.addSource": "Додати джерело",
    "ragAdmin.saveChanges": "Зберегти зміни",
    "ragAdmin.sources": "Джерела",
    "ragAdmin.sourcesLead": "Список завантажується з backend бази знань",
    "ragAdmin.errorCount": ({ count }) => `${count} помилок індексації`,
    "ragAdmin.indexError": "Помилка індексації",
    "ragAdmin.reindex": "Переіндексувати",
    "ragAdmin.reindexAll": "Переіндексувати всі",
    "ragAdmin.reindexing": "Індексація",
    "ragAdmin.reindexingOne": "Індексація джерела",
    "ragAdmin.reindexedOne": "Джерело переіндексовано",
    "ragAdmin.reindexFailed": "Індексація не вдалася. Перевір логи AI service",
    "ragAdmin.reindexingAll": ({ count }) => `Індексація ${count} джерел`,
    "ragAdmin.reindexedAll": "Усі джерела переіндексовано",
    "ragAdmin.ingestion.ready": "Готово",
    "ragAdmin.ingestion.chunked": "Поділено",
    "ragAdmin.ingestion.embedded": "Embedded",
    "ragAdmin.ingestion.error": "Помилка",
    "ragAdmin.empty": "Джерел RAG ще немає",
    "ragAdmin.required": "Заповни всі поля джерела перед збереженням",
    "ragAdmin.added": "Джерело додано",
    "ragAdmin.updated": "Джерело оновлено",
    "ragAdmin.deleted": "Джерело видалено",

    "aiUsage.label": "АДМІН",
    "aiUsage.title": "Використання AI",
    "aiUsage.lead":
      "Переглянь виклики AI, токени й орієнтовну вартість для кожного користувача.",
    "aiUsage.heroCount": ({ count }) => `${count} користувачів`,
    "aiUsage.backToRag": "Адмін RAG",
    "aiUsage.currentView": "Таблиця використання",
    "aiUsage.stats": "Підсумок використання",
    "aiUsage.totalCalls": "Виклики AI",
    "aiUsage.totalTokens": "Токени разом",
    "aiUsage.totalCost": "Орієнтовна вартість",
    "aiUsage.users": "Користувачі",
    "aiUsage.tableTitle": "Використання по користувачах",
    "aiUsage.tableLead":
      "Окремий рядок для кожного користувача із сумою викликів, токенів і вартості.",
    "aiUsage.loading": "Завантаження",
    "aiUsage.empty": "Поки що немає жодного AI-виклику",
    "aiUsage.loadError": "Не вдалося завантажити підсумок використання AI",
    "aiUsage.user": "Користувач",
    "aiUsage.calls": "Виклики",
    "aiUsage.promptTokens": "Токени prompt",
    "aiUsage.completionTokens": "Токени відповіді",
    "aiUsage.cost": "Вартість",
    "aiUsage.firstCallAt": "Перший виклик",
    "aiUsage.lastCallAt": "Останній виклик",

    "week.label": "ТИЖДЕНЬ",
    "week.heroTitle": "Тижневі цілі та прогрес",
    "week.heroLead": "Відстежуй, як щоденні дії працюють на тижневий результат",
    "week.avgCompletion": "Сер. виконання",
    "week.goalsOnTrack": "Цілей у темпі",
    "week.bestMomentum": "Найкращий імпульс",
    "week.goalsProgress": "Прогрес цілей",
    "week.progressLead":
      "Спершу підтягуй цілі з низьким покриттям, щоб захистити результат тижня",
    "week.completed": "Завершено",
    "week.remaining": ({ percent, remaining, unit }) =>
      `${percent}% виконано · залишилось ${remaining} ${unit}`,
    "longPlans.label": "ДОВГІ ПЛАНИ",
    "longPlans.heroTitle": "Довгі плани",
    "longPlans.switcher": "Діапазон цілей",
    "longPlans.week": "Тиждень",
    "longPlans.month": "Місяць",
    "longPlans.year": "Рік",
    "longPlans.global": "Глобальні",
    "longPlans.weekLead": "Відстежуй результати, які мають зрушити цього тижня",
    "longPlans.monthLead":
      "Переглядай місячні результати, що задають напрям найближчим тижням",
    "longPlans.yearLead":
      "Тримай річний напрям видимим, коли обираєш ближчі цілі",
    "longPlans.globalLead":
      "Переглядай найширші цілі, що задають загальний напрям",
    "longPlans.avgCompletion": "Сер. виконання",
    "longPlans.goalsOnTrack": "Цілей у темпі",
    "longPlans.bestMomentum": "Найкращий імпульс",
    "longPlans.activeScope": "Активний діапазон",
    "longPlans.yearItems": "Річні цілі",
    "longPlans.globalItems": "Глобальні цілі",
    "longPlans.yearAnchor": "Головна опора",
    "longPlans.globalAnchor": "Головний напрям",
    "longPlans.weekGoals": "Цілі тижня",
    "longPlans.monthGoals": "Цілі місяця",
    "longPlans.yearGoals": "Цілі року",
    "longPlans.globalGoals": "Глобальні цілі",
    "longPlans.weekProgressLead":
      "Спершу підтягни цілі з низьким покриттям, щоб захистити результат тижня",
    "longPlans.monthProgressLead":
      "Використовуй прогрес місяця, щоб вирішити, що потребує сильнішого тижневого поштовху",
    "longPlans.emptyTitle": "Тут ще немає цілей",
    "longPlans.emptyLead":
      "Визнач один чіткий результат на цей період, а потім перетвори його на ціль для дії.",
    "longPlans.emptyFilteredTitle": "Жодна ціль не відповідає цьому фільтру",
    "longPlans.emptyFilteredLead":
      "Спробуй іншу категорію або покажи всі цілі, щоб знайти наступний фокус.",
    "longPlans.clearFilter": "Показати всі цілі",
    "longPlans.yearProgressLead": "Це довгострокові опори з профілю акаунта",
    "longPlans.completed": "Завершено",
    "longPlans.remaining": ({ percent, remaining, unit }) =>
      `${percent}% виконано · залишилось ${remaining} ${unit}`,
    "longPlans.yearGoalMeta": "Річний результат",
    "longPlans.yearGoalLead":
      "Розбий це на місячні цілі, а потім на тижневі зобов'язання",
    "longPlans.noYearGoals":
      "Річні цілі ще не збережені. Додай річні цілі в акаунті, щоб бачити їх тут",
    "longPlans.globalGoalsLead":
      "Вони ширші за річні цілі й задають загальний напрям",
    "longPlans.globalGoal": "Глобальна ціль",
    "longPlans.globalGoalMeta": "Глобальний напрям",
    "longPlans.globalGoalLead":
      "Використовуй це для формування річних цілей, а потім місячних результатів",
    "longPlans.noGlobalGoals":
      "Глобальні цілі ще не збережені. Додай їх в акаунті",
    "goalImage.generate": "Створити зображення",
    "goalImage.title": "Зображення цілей",
    "goalImage.preview": "Попередній перегляд",
    "goalImage.download": "Завантажити PNG",
    "goalImage.copyImage": "Копіювати зображення",
    "goalImage.copySuccess": "Зображення скопійовано до буфера обміну.",
    "goalImage.copyError":
      "Не вдалося скопіювати зображення. Завантажте його натомість.",
    "goalImage.copyUnavailable":
      "Копіювання зображень недоступне в цьому браузері.",
    "goalImage.generating": "Створення…",
    "goalImage.close": "Закрити",
    "goalImage.generationError":
      "Не вдалося створити зображення. Спробуйте ще раз.",
    "goalImage.noGoals": "Немає цілей для експорту",
    "goalImage.totalGoals": "Усього цілей",
    "goalImage.completed": "Завершено",
    "goalImage.averageProgress": "Середній прогрес",
    "goalImage.action": "Дія",
    "goalImage.generated": "Створено",
    "goalImage.footerTagline": "Перетворюй довгострокові цілі на щоденні дії",
    "goalImage.longTermDirection": "Довгостроковий напрям",
    "goalImage.weekTitle": "Мої цілі тижня",
    "goalImage.monthTitle": "Мої цілі місяця",
    "goalImage.yearTitle": "Мої цілі року",
    "goalImage.globalTitle": "Мої глобальні цілі",
    "goalImage.weekSubtitle": "Огляд усіх цілей і дій на цей тиждень.",
    "goalImage.monthSubtitle": "Пріоритети, що формують цей місяць.",
    "goalImage.yearSubtitle": "Результати, що визначають напрям цього року.",
    "goalImage.globalSubtitle": "Напрям за твоїми довгостроковими планами.",

    "calendar.label": "КАЛЕНДАР",
    "calendar.heroSub": ({ label }) =>
      `Режим ${label.toLowerCase()} · час показано з 06:00 до 23:00`,
    "calendar.tablist": "Вигляд календаря",
    "calendar.day": "День",
    "calendar.week": "Тиждень",
    "calendar.threeDays": "3 дні",
    "calendar.month": "Місяць",
    "calendar.noFixedTime": "Без фіксованого часу",
    "calendar.done": "Готово",
    "calendar.todo": "Зробити",
    "calendar.status": "Статус",
    "calendar.editTask": "Редагувати задачу",
    "calendar.editTaskHint": "Зміни назву, тип, час і статус виконання",
    "calendar.editTaskAria": ({ title }) => `Редагувати ${title}`,
    "calendar.titleRequired": "Введи назву перед дублюванням",
    "calendar.invalidTime": "Час завершення має відрізнятися від часу початку",
    "calendar.wrapsContinuesAria": ({ title }) =>
      `${title} — продовження з попереднього дня`,
    "calendar.groupMoveFailed":
      "Не всі цілі вдалося перемістити. Дати оновлено.",
    "calendar.habitDayMoveBlocked":
      "Звичка залишається у своєму дні. Натомість змінено її час у цьому дні.",
    "calendar.now": "Зараз",
    "calendar.setStartTimeToNow": "Установити час початку на зараз",
    "calendar.setEndTimeToNow": "Установити час завершення на зараз",
    "calendar.weekdays.mon": "Пн",
    "calendar.weekdays.tue": "Вт",
    "calendar.weekdays.wed": "Ср",
    "calendar.weekdays.thu": "Чт",
    "calendar.weekdays.fri": "Пт",
    "calendar.weekdays.sat": "Сб",
    "calendar.weekdays.sun": "Нд",
    "calendar.month.todayPlan": "План на сьогодні",
    "calendar.month.planForDate": "План на цю дату",
    "calendar.month.noPlan": "На цей день ще немає збереженого плану",
    "calendar.month.noActions":
      "Ще немає дій. Додай їх із розділу Сьогодні або Планування",
    "calendar.month.noActionsForDate": "На цю дату ще немає збережених дій",
    "calendar.month.pickToday":
      "Тут показуються лише сьогоднішні дії. Вибери сьогодні, щоб побачити список",
    "calendar.anyTime": "У будь-який час",
    "dateNav.controls": "Керування датою",
    "dateNav.previous": "Назад",
    "dateNav.current": "Поточний",
    "dateNav.next": "Далі",
    "dateNav.pickDate": "Вибери дату",
    "dateNav.close": "Закрити вибір дати",

    "insights.label": "АНАЛІТИКА",
    "insights.title": "AI-зворотний зв'язок про дисципліну",
    "insights.emptyTitle": "Висновків AI ще немає",
    "insights.emptyPrompt":
      "Створіть висновок AI, щоб побачити персоналізований аналіз.",
    "insights.eligibilityProgress": ({ count, required }) =>
      `Збережені дні планування: ${count}/${required}.`,
    "insights.eligibilityRemaining": ({ count }) =>
      `Потрібно ще ${count} день планування, щоб відкрити генерування.`,
    "insights.generate": "Створити висновок",
    "insights.loadError":
      "Не вдалося завантажити дані аналітики. Спробуйте ще раз.",
    "insights.lead":
      "Коучинг щодо звичок: історія, цілі, рефлексія й сигнали виконання",
    "insights.executionStrong": "Сильне виконання",
    "insights.executionSteady": "Стабільне виконання",
    "insights.executionRisk": "Ризик виконання",
    "insights.executionNeedsWork": "Потрібна підтримка виконання",
    "insights.lockedIn": "Сильний ритм",
    "insights.building": "Ритм формується",
    "insights.fresh": "Нова серія",
    "insights.reflectionCaptured": "Рефлексію збережено",
    "insights.reflectionMissing": "Немає рефлексії",
    "insights.reflectionCount": ({ count }) => `${count}/7`,
    "insights.summary": "Підсумок AI",
    "insights.completionPercent": ({ percent }) => `${percent}% виконання`,
    "insights.metrics": "Метрики аналітики",
    "insights.completed": "Виконано цього тижня",
    "insights.actionsToday": "дій сьогодні",
    "insights.currentStreak": "Поточна серія",
    "insights.consistencySignal": "сигнал послідовності",
    "insights.reflection": "Рефлексія",
    "insights.added": "Додано",
    "insights.missing": "Немає",
    "insights.inputQuality": "якість вхідних даних",
    "insights.sourceSignals": "Джерельні сигнали",
    "insights.inputsUsed": "Вхідні сигнали для цієї рекомендації",
    "insights.date": "Дата",
    "insights.completionSignal": "Сигнал виконання",
    "insights.streakSignal": "Сигнал серії",
    "insights.dayStreakActive": ({ streak }) => `${streak} днів активної серії`,
    "insights.reflectionSignal": "Сигнал рефлексії",
    "insights.noReflection": "Сьогодні рефлексію не додано",
    "insights.aiHealth": ({ score }) => `Стан AI ${score}%`,
    "insights.nextActions": "Наступні дії",
    "insights.priorityMoves": "Пріоритетні кроки на основі тижневих прогалин",
    "insights.contribution": ({ amount, unit }) => `Внесок: ${amount} ${unit}`,
    "insights.noPriorityGap":
      "Критичної прогалини не знайдено. Тримай поточний план і захищай блоки фокусу",
    "insights.addFromSuggestion":
      "Перетвори рекомендацію на реальний елемент плану перед виходом з цього екрана",
    "insights.modelReading": "Оцінка моделі",
    "insights.weakestSignal": ({ signal }) =>
      `Покращ ${signal.toLowerCase()}, щоб підняти якість рекомендацій`,
    "insights.aiGenerated":
      "Враховує останні тижні виконання, зв'язки між цілями, рефлексію й базу знань — не лише знімок «сьогодні»",
    "insights.todayDone": "Виконання сьогодні",
    "insights.todayDoneValue": ({ done, total, percent }) =>
      `${done}/${total} дій виконано (${percent}%)`,
    "insights.whatBlocked": "Що могло завадити",
    "insights.blockerLow":
      "Критичної перешкоди не видно; захисти ритм і не додавай зайвий обсяг",
    "insights.blockerNoReflection":
      "Рефлексії немає, тому система може бачити перешкоди тільки через невиконані дії",
    "insights.blockerFromReflection": ({ reflection }) =>
      `Твоя рефлексія вказує на: ${reflection}`,
    "insights.tomorrowMove": "Що зробити завтра",
    "insights.tomorrowProtect":
      "Залиш схожу форму плану й зарезервуй один захищений блок фокусу зранку",
    "insights.tomorrowRecover":
      "Перенеси найважчу невиконану ціль у перший блок фокусу й зменш решту дня",
    "insights.problemZonesTitle": "Як йде боротьба з проблемними зонами",
    "insights.problemZonesLead":
      "Як складні зони проявляються в діях — і конкретні експерименти для кожної зони",
    "insights.problemZoneFallback": "Проблемну зону ще не задано",
    "insights.problemZoneStable":
      "Рух видно. Завтра залиш одне мале обмеження для цієї зони",
    "insights.problemZoneNeedsWork":
      "Цій зоні потрібен чіткіший бар'єр: один блок часу, один ліміт, одна наступна дія",
    "insights.alignmentTitle": "Відповідність цілей",
    "insights.alignmentLead":
      "Наскільки щільно денні шари з'єднані з вищими — сильніший зв'язок зменшує хаос",
    "insights.alignDailyWeekly": "Денні цілі → цілі тижня",
    "insights.alignWeeklyMonth": "Цілі тижня → цілі місяця",
    "insights.alignMonthYear": "Цілі місяця → цілі року",
    "insights.alignYearGlobal": "Цілі року → глобальні цілі",
    "insights.alignDailyWeeklyDetail": ({ linked, total }) =>
      `${linked}/${total} денних дій-цілей прив'язані до цілі тижня`,
    "insights.alignParentDetail": ({ linked, total }) =>
      `${linked}/${total} цілей виглядають пов'язаними з рівнем вище`,
    "insights.reloadWithAI": "Перегенерувати з AI",
    "insights.reloading": "Генерація",
    "insights.generatedBy": ({ provider, model }) =>
      `Згенеровано ${provider}/${model}`,
    "insights.reloadError": "Зараз не вдалося згенерувати AI-аналітику",
    "insights.analysisStepsTitle": "Кроки аналізу",
    "insights.analysisStepsLead":
      "Сигнали, які AI врахував, — не просто інструкції",
    "insights.patternWarningLabel": "Патерн, за яким варто стежити",
    "insights.zoneHowYouManage": "Як ти зазвичай це обходиш:",
    "insights.zoneAdvice": "Спробуй далі:",
    "insights.primaryRecommendation": "Рекомендація",
    "insights.sourcesTitle": "Джерела (RAG)",
    "insights.sourcesLead":
      "Уривки з бази знань для цього інсайту. Порожньо, якщо збігів майже не було",
    "insights.sourcesEmpty":
      "До цього запуску не додано уривків бази знань (слабкі збіги або порожній індекс)",
    "insights.sourceUntitled": "Без назви",
    "insights.sourceRank": ({ rank }) => `#${rank}`,
    "insights.sourceScore": ({ score }) => `скор ${score}`,
    "insights.sourcesCompactTitle": "Джерела",
    "insights.sourcesCaption": "Джерела бази знань для цього інсайту",
    "insights.sourceColTopic": "Тема",
    "insights.sourceColType": "Тип",
    "insights.sourceColLink": "Посилання",
    "insights.sourceColConfidence": "Впевненість",
    "insights.sourceOpenLink": "Відкрити",
    "insights.sourcesEmptyShort": "Немає рядків джерел для цього запуску",

    "aside.aiRecommendations": "AI-рекомендації",
    "aside.aiTitle": "Рекомендації",
    "aside.systemStatus": "Статус системи",
    "aside.todayOps": "Операції дня",
    "aside.unscheduledMeta": "Все ще потребує часу або свідомого рішення",
    "aside.savedLocally": "Збережено локально",
    "aside.localState": "Локальний стан оновлюється після кожної зміни",
    "aside.modelAssisted": "Підсилено моделлю",
    "aside.beforeNoon": ({ title }) =>
      `${title}. Постав це в пріоритет до полудня`,
    "aside.consistency":
      "Тримай послідовність: одна стратегічна дія і одна звичка",
    "aside.weeklyFocus": "Фокус тижня",
    "aside.weeklyFocusLead":
      "Плануючи завтра, починай із цілей нижче 60% виконання",
    "aside.planningRule": "Правило планування",
    "aside.planningRuleLead":
      "На один день тримай не більше 3 стратегічних цілей і 2 підтримувальних звичок",
    "logic.strongExecution": "Сильний ритм виконання",
    "logic.balancedExecution":
      "Стабільне виконання з місцем ще для одного сильного кроку",
    "logic.lowExecution":
      "Низьке виконання сьогодні. Зменш обсяг і захисти один блок фокусу на завтра",
    "logic.recapWithFeedback": ({ done, total, percent, cadence, feedback }) =>
      `Виконано ${done}/${total} дій (${percent}%). ${cadence} Твоя нотатка каже: "${feedback}". Рекомендація AI: залиш перші 90 хвилин завтра для найстратегічнішої цілі та заплануй одну відновлювальну звичку ввечері`,
    "logic.recapWithoutFeedback": ({ done, total, percent, cadence }) =>
      `Виконано ${done}/${total} дій (${percent}%). ${cadence} Додай коротку рефлексію, щоб завтра покращити якість рекомендацій`,
    "logic.pushGoal": ({ title, amount, unit }) =>
      `Просунь "${title}" на ${amount} ${unit}`,
    "recommendation.none":
      "Зараз немає рекомендацій. Тримай поточний план і захищай час фокусу",
    "recommendation.apply": "Додати в план",
    "recommendation.dismiss": "Відхилити",
    "goalScope.global": "Довгостроковий",
    "goalScope.year": "Річний",
    "goalScope.month": "Місячний",
    "goalScope.week": "Тижневий",
    "goalDetails.back": "Назад",
    "goalDetails.close": "Закрити",
    "goalDetails.completed": "Завершено",
    "goalDetails.loadError": "Не вдалося завантажити цю ціль",
    "goalDetails.loadingNotes": "Завантаження нотаток…",
    "goalDetails.notes": "Нотатки",
    "goalDetails.parent": ({ title }) => `Частина цілі «${title}»`,
    "goalDetails.breakdown": "Розподіл прогресу",
    "goalDetails.loadingChildren": "Завантаження елементів…",
    "goalDetails.noChildren": "Прямих елементів ще немає",
    "goalDetails.active": "Активна",
    "goalDetails.contributed": "Внесено",
    "goalDetails.dailyItem": "Щоденний елемент",
    "goalDetails.habit": "Звичка",
    "goalNotes.edit": "Редагувати",
    "goalNotes.preview": "Перегляд",
    "goalNotes.saving": "Збереження…",
    "goalNotes.saved": "Збережено",
    "goalNotes.saveError": "Не вдалося зберегти нотатки",
    "goalNotes.placeholder": "Додай контекст для цієї цілі. Введи @, щоб пов’язати іншу ціль.",
    "goalNotes.emptyPreview": "Тут поки що порожньо. Перемкнись на Редагувати, щоб додати нотатки.",
    "goalNotes.mentionSearch": "Пошук цілей",
    "goalNotes.noGoalsFound": "Цілей не знайдено",
    "goalNotes.mentionUnavailable": "Ця ціль більше не доступна",
    "goalNotes.mentionedIn": "Згадано в",
    "goalNotes.editorHint": "Пиши природно · використовуй / для блоків · @ для цілей",
    "goalNotes.commandSearch": "Команди нотатки",
    "goalNotes.noCommandsFound": "Команд не знайдено",
    "goalNotes.commandText": "Текст",
    "goalNotes.commandHeading1": "Заголовок 1",
    "goalNotes.commandHeading2": "Заголовок 2",
    "goalNotes.commandHeading3": "Заголовок 3",
    "goalNotes.commandTodo": "Список справ",
    "goalNotes.commandBulletList": "Маркований список",
    "goalNotes.commandOrderedList": "Нумерований список",
    "goalNotes.commandQuote": "Цитата",
    "goalNotes.commandCodeBlock": "Блок коду",
    "goalNotes.commandDivider": "Роздільник",
    "goalNotes.taskCheckbox": "Елемент списку справ",
    "goalNotes.untitledTask": "Завдання без назви",
  },
};

type I18nContextValue = {
  language: Language;
  locale: string;
  setLanguage: (language: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function getInitialLanguage(
  allowOptionalStorage: boolean,
  search = typeof window === "undefined" ? "" : window.location.search
): Language {
  const handoffLanguage = languageFromQuery(search);
  if (handoffLanguage) return handoffLanguage;
  if (!allowOptionalStorage) return "en";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "pl" || stored === "uk" || stored === "en") return stored;
  return "en";
}

export function localeForLanguage(language: Language): string {
  if (language === "pl") return "pl-PL";
  if (language === "uk") return "uk-UA";
  return "en-GB";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const { isOptionalStorageEnabled } = useBrowserStorageConsent();
  const [language, setLanguageState] = useState<Language>(() =>
    getInitialLanguage(isOptionalStorageEnabled)
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (!languageFromQuery(url.search)) return;
    url.searchParams.delete("language");
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  }, []);

  useEffect(() => {
    if (isOptionalStorageEnabled) localStorage.setItem(STORAGE_KEY, language);
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [isOptionalStorageEnabled, language]);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const dictionary = dictionaries[language];
      const fallback = dictionaries.en[key];
      const value = dictionary[key] ?? fallback ?? key;
      if (typeof value === "function") return value(params);
      return value;
    },
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      locale: localeForLanguage(language),
      setLanguage,
      t,
    }),
    [language, setLanguage, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
